// Data Service Layer - Abstracts between Live, Demo, and Tour modes
import type { FeatureMode, DataMode, Client, ProjectNote, WIPReport, UtilisationData, TimeEntry } from '@/types';
import type { PaginationParams, PaginatedResponse } from '@/types/pagination';
import { paginateArray } from '@/types/pagination';
import { getBudgetHealthThresholds, deriveProjectHealth } from './budget-health-thresholds-storage';
import { calculateGP, calculateBudgetGP } from './gp-calculation';
import { computeEffectiveCompletionPercent } from './completion';
import { getFinanceSettings } from './finance-settings-storage';
import * as mockData from './mock-data';
import * as tourMockData from './tour-mock-data';
import { getBudgetDataByProjectId as getMockBudgetData } from './mock-data/budget-mock-data';
import { saveMockResourceBudgetRoles, loadMockResourceBudgetRoles, saveMockCostBudgetCategories, loadMockCostBudgetCategories } from './mock-budget-storage';
import { mockGlobalCategories, mockGlobalSubcategories, mockCategoryStats, getGlobalSubcategoriesByCategory as getMockGlobalSubcategoriesByCategory } from './mock-data/category-mock-data';
import { apiFetch } from './api-error-handler';
import { formatDateForInput } from './utils';
import { wipAgingBandsFromApi } from './wip-aging';
import { authFetch } from './auth-fetch';
import type { ProjectBudgetData } from '@/types/budget.types';
import type { GlobalCategory, GlobalSubcategory, CategoryStats, CategoryType } from '@/types/category.types';
import type { RevisionReasonCategoryConfig } from '@/types/resource-budget-settings.types';
import type { ProjectTemplate, CreateTemplateData, UpdateTemplateData } from '@/types/template.types';
import type { Role, RolePermissions } from '@/types/roles.types';
import type { HierarchicalCostCentre } from '@/components/blocks/cost-centres/cost-centres-hierarchical-table';

// ============================================================================
// DEMO-CREATED PROJECTS (localStorage-backed persistence for demo mode)
// ============================================================================

// In demo mode there is no backend, so projects created via createProject are
// persisted to localStorage so they appear in the project list and detail views
// for the current session/browser (mirrors the saveNotificationSettings pattern).
const DEMO_CREATED_PROJECTS_KEY = 'access-pm-demo-created-projects';

function getDemoCreatedProjects(): any[] {
  if (typeof localStorage === 'undefined') return [];
  try {
    const raw = localStorage.getItem(DEMO_CREATED_PROJECTS_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
}

function addDemoCreatedProject(project: any): void {
  if (typeof localStorage === 'undefined') return;
  const projects = getDemoCreatedProjects();
  projects.push(project);
  try {
    localStorage.setItem(DEMO_CREATED_PROJECTS_KEY, JSON.stringify(projects));
  } catch {
    // Ignore write errors (e.g. quota exceeded or unavailable storage)
  }
}

// Patch an existing demo-created project record in localStorage so post-create
// edits (custom field values, role assignments) persist for the session and
// surface on the detail page. No-op if the id doesn't match a stored demo
// project (e.g. a real numeric id in live mode).
function updateDemoCreatedProject(projectId: number | string, patch: Record<string, unknown>): void {
  if (typeof localStorage === 'undefined') return;
  const projects = getDemoCreatedProjects();
  const index = projects.findIndex(p => String(p.id) === String(projectId));
  if (index === -1) return;
  projects[index] = { ...projects[index], ...patch };
  try {
    localStorage.setItem(DEMO_CREATED_PROJECTS_KEY, JSON.stringify(projects));
  } catch {
    // Ignore write errors (e.g. quota exceeded or unavailable storage)
  }
}

// Default API base URL - uses environment variable or defaults to /api for same-origin requests
const DEFAULT_API_BASE_URL = import.meta.env?.VITE_API_URL || '/api';

// Runtime override for API base URL (used by app-prod to inject PidgetSettings.apiUrl)
let apiBaseUrlOverride: string | null = null;

/**
 * Set the API base URL override. This is called by app-prod during initialization
 * to use the apiUrl from PidgetSettings instead of the environment variable.
 * 
 * @param url - The API base URL to use (trailing slashes will be trimmed)
 */
export function setApiBaseUrlOverride(url: string): void {
  // Trim trailing slashes to prevent double-slash URLs like "https://api.example.com//projects"
  apiBaseUrlOverride = url.replace(/\/+$/, '');
}

/**
 * Get the current API base URL. Returns the override if set (app-prod),
 * otherwise falls back to the environment variable or default '/api' (app).
 */
export function getApiBaseUrl(): string {
  return apiBaseUrlOverride ?? DEFAULT_API_BASE_URL;
}

// Runtime override for Document API URL (ADM)
let documentApiUrlOverride: string | null = null;

export function setDocumentApiUrlOverride(url: string): void {
  documentApiUrlOverride = url.replace(/\/+$/, '');
}

export function getDocumentApiUrl(): string | undefined {
  return documentApiUrlOverride ?? undefined;
}

// Storage keys
const FEATURE_MODE_KEY = 'access-pm-feature-modes';
const SENSITIVE_DATA_KEY = 'access-pm-sensitive-data-visible';
const DATA_MODE_KEY = 'evo-data-mode';


interface FeatureModes {
  [featureId: string]: FeatureMode;
}

// Get global data mode from localStorage
export function getDataMode(): DataMode {
  if (typeof window === 'undefined') return 'demo';
  
  try {
    const stored = localStorage.getItem(DATA_MODE_KEY);
    if (stored === 'demo' || stored === 'live' || stored === 'tour') {
      return stored;
    }
    // In production (non-localhost), default to 'live' so API calls are made
    const host = window.location.hostname;
    if (host !== 'localhost' && host !== '127.0.0.1') {
      return 'live';
    }
    return 'demo';
  } catch {
    return 'demo';
  }
}

// Get feature mode from localStorage (legacy - kept for backward compatibility)
export function getFeatureMode(featureId: string): FeatureMode {
  if (typeof window === 'undefined') return 'demo';
  
  try {
    const stored = localStorage.getItem(FEATURE_MODE_KEY);
    if (!stored) return 'demo';
    
    const modes: FeatureModes = JSON.parse(stored);
    return modes[featureId] || 'demo';
  } catch {
    return 'demo';
  }
}

// Set feature mode
export function setFeatureMode(featureId: string, mode: FeatureMode): void {
  if (typeof window === 'undefined') return;
  
  try {
    const stored = localStorage.getItem(FEATURE_MODE_KEY);
    const modes: FeatureModes = stored ? JSON.parse(stored) : {};
    modes[featureId] = mode;
    localStorage.setItem(FEATURE_MODE_KEY, JSON.stringify(modes));
    
    // Trigger storage event for other tabs/components
    window.dispatchEvent(new CustomEvent('featureModeChanged', { 
      detail: { featureId, mode } 
    }));
  } catch (error) {
    console.error('Failed to set feature mode:', error);
  }
}

// Get all feature modes
export function getAllFeatureModes(): FeatureModes {
  if (typeof window === 'undefined') return {};
  
  try {
    const stored = localStorage.getItem(FEATURE_MODE_KEY);
    return stored ? JSON.parse(stored) : {};
  } catch {
    return {};
  }
}

// Sensitive data visibility
export function getSensitiveDataVisible(): boolean {
  if (typeof window === 'undefined') return true;
  
  try {
    const stored = localStorage.getItem(SENSITIVE_DATA_KEY);
    return stored === null ? true : stored === 'true';
  } catch {
    return true;
  }
}

export function setSensitiveDataVisible(visible: boolean): void {
  if (typeof window === 'undefined') return;
  
  try {
    localStorage.setItem(SENSITIVE_DATA_KEY, String(visible));
    window.dispatchEvent(new CustomEvent('sensitiveDataVisibilityChanged', { 
      detail: { visible } 
    }));
  } catch (error) {
    console.error('Failed to set sensitive data visibility:', error);
  }
}

// Filter parameters for project list
export interface ProjectFilterParams {
  statusId?: number;
  health?: string;
  search?: string;
  sortBy?: string;
  sortOrder?: 'asc' | 'desc';
  pmId?: number;
  clientId?: number;
  billable?: string;
  scope?: string;
  /**
   * Serialised grid column filters (the ColumnFilters object produced by the
   * filter sheet). Live mode only — the API applies them in SQL.
   */
  columnFilters?: string;
  /** Project type slug — used to load the rows of a `project-type` leaf group */
  type?: string;
  /** Project manager ID — used to load the rows of a `project-manager` leaf group */
  managerId?: number;
  /**
   * Group-by fields whose value must be NULL, i.e. the "Unassigned" bucket of a
   * leaf group (e.g. `['project-manager']`).
   */
  unassigned?: string[];
}

// Data fetching functions - route based on global data mode
export async function fetchProjects(
  featureId: string = 'projects',
  paginationParams?: PaginationParams,
  filterParams?: ProjectFilterParams
): Promise<any> {
  const mode = getDataMode();
  
  if (mode === 'demo') {
    await new Promise(resolve => setTimeout(resolve, 300));
    
    let projects = [...getDemoCreatedProjects(), ...mockData.mockProjects];

    // Derive health from budget actuals using configured thresholds
    const budgetHealthThresholds = getBudgetHealthThresholds();
    projects = projects.map(p => ({
      ...p,
      health: deriveProjectHealth(
        {
          actualCost: p.actualCosts ?? 0,
          budgetCost: (p.budgetLabour ?? 0) + (p.budgetExpense ?? 0),
          startDate: p.startDate,
          endDate: p.endDate,
          completionPercent: p.completionPercentOverride ?? p.completionPercent ?? 0,
          actualHours: p.actualHours ?? 0,
          budgetHours: p.budgetHours ?? 0,
          wipAmount: p.wipAmount ?? 0,
          actualRevenue: p.actualRevenue ?? 0,
          budgetRevenue: p.budgetRevenue ?? 0,
          forecastEndDate: p.forecastEndDate,
        },
        budgetHealthThresholds
      ),
    }));

    // ─────────────────────────────────────────────────────────────────────────
    // WIP Amount — type-aware derivation (mirrors mockWIPReports logic)
    //
    // T&M / Retainer : project.wipAmount  (unbilled ledger entries — correct as-is)
    // Fixed Fee       : (effectiveCompletionPercent / 100 × budgetRevenue) − amountBilled
    //                   where amountBilled = actualRevenue − unbilledAmount
    //                   and effectiveCompletionPercent = override ?? completionPercent
    // ─────────────────────────────────────────────────────────────────────────
    projects = projects.map(p => {
      if (p.type !== 'fixed-price') return p;
      const effectiveCompletion: number =
        p.completionPercentOverride != null
          ? p.completionPercentOverride
          : p.completionPercent;
      const amountBilled: number = p.actualRevenue - p.unbilledAmount;
      const earnedRevenue: number = (effectiveCompletion / 100) * p.budgetRevenue;
      return { ...p, wipAmount: earnedRevenue - amountBilled };
    });

    // Apply filters in demo mode
    if (filterParams) {
      if (filterParams.statusId != null) {
        projects = projects.filter(p => p.statusId === filterParams.statusId);
      }
      if (filterParams.health && filterParams.health !== 'all') {
        if (filterParams.health === 'at-risk') {
          projects = projects.filter(p => p.health === 'amber' || p.health === 'red');
        } else {
          projects = projects.filter(p => p.health === filterParams.health);
        }
      }
      if (filterParams.search) {
        const searchLower = filterParams.search.toLowerCase();
        projects = projects.filter(p =>
          p.name.toLowerCase().includes(searchLower) ||
          (p.code && p.code.toLowerCase().includes(searchLower)) ||
          (p.clientNames && p.clientNames.toLowerCase().includes(searchLower))
        );
      }
      // Apply sorting
      if (filterParams.sortBy) {
        const healthOrder: Record<string, number> = { red: 0, amber: 1, green: 2 };
        switch (filterParams.sortBy) {
          case 'name':
            projects.sort((a, b) => a.name.localeCompare(b.name));
            break;
          case 'health':
            projects.sort((a, b) => (healthOrder[a.health] ?? 3) - (healthOrder[b.health] ?? 3));
            break;
          case 'value':
            projects.sort((a, b) => b.budgetRevenue - a.budgetRevenue);
            break;
          case 'completion':
            projects.sort((a, b) => a.completionPercent - b.completionPercent);
            break;
          default:
            // Default: health (worst first)
            projects.sort((a, b) => (healthOrder[a.health] ?? 3) - (healthOrder[b.health] ?? 3));
        }
      }
    }
    
    // If pagination params provided, return paginated response
    if (paginationParams) {
      const { page, pageSize } = paginationParams;
      return paginateArray(projects, page, pageSize);
    }
    
    // Otherwise return all projects (backward compatibility)
    return projects;
  } else if (mode === 'tour') {
    // Tour mode - use tour-specific mock data
    await new Promise(resolve => setTimeout(resolve, 300));
    
    let projects = [...tourMockData.tourMockProjects];

    // Derive health from budget actuals using configured thresholds
    const tourBudgetHealthThresholds = getBudgetHealthThresholds();
    projects = projects.map(p => ({
      ...p,
      health: deriveProjectHealth(
        {
          actualCost: p.actualCosts ?? 0,
          budgetCost: (p.budgetLabour ?? 0) + (p.budgetExpense ?? 0),
          startDate: p.startDate,
          endDate: p.endDate,
          completionPercent: p.completionPercentOverride ?? p.completionPercent ?? 0,
          actualHours: p.actualHours ?? 0,
          budgetHours: p.budgetHours ?? 0,
          wipAmount: p.wipAmount ?? 0,
          actualRevenue: p.actualRevenue ?? 0,
          budgetRevenue: p.budgetRevenue ?? 0,
          forecastEndDate: p.forecastEndDate,
        },
        tourBudgetHealthThresholds
      ),
    }));

    // ─────────────────────────────────────────────────────────────────────────
    // WIP Amount — type-aware derivation (mirrors mockWIPReports logic)
    //
    // T&M / Retainer : project.wipAmount  (unbilled ledger entries — correct as-is)
    // Fixed Fee       : (effectiveCompletionPercent / 100 × budgetRevenue) − amountBilled
    //                   where amountBilled = actualRevenue − unbilledAmount
    //                   and effectiveCompletionPercent = override ?? completionPercent
    // ─────────────────────────────────────────────────────────────────────────
    projects = projects.map(p => {
      if (p.type !== 'fixed-price') return p;
      const effectiveCompletion: number =
        p.completionPercentOverride != null
          ? p.completionPercentOverride
          : p.completionPercent;
      const amountBilled: number = p.actualRevenue - p.unbilledAmount;
      const earnedRevenue: number = (effectiveCompletion / 100) * p.budgetRevenue;
      return { ...p, wipAmount: earnedRevenue - amountBilled };
    });

    // Apply filters in tour mode
    if (filterParams) {
      if (filterParams.statusId != null) {
        projects = projects.filter(p => p.statusId === filterParams.statusId);
      }
      if (filterParams.health && filterParams.health !== 'all') {
        if (filterParams.health === 'at-risk') {
          projects = projects.filter(p => p.health === 'amber' || p.health === 'red');
        } else {
          projects = projects.filter(p => p.health === filterParams.health);
        }
      }
      if (filterParams.search) {
        const searchLower = filterParams.search.toLowerCase();
        projects = projects.filter(p =>
          p.name.toLowerCase().includes(searchLower) ||
          (p.code && p.code.toLowerCase().includes(searchLower)) ||
          (p.clientNames && p.clientNames.toLowerCase().includes(searchLower))
        );
      }
      // Apply sorting
      if (filterParams.sortBy) {
        const healthOrder: Record<string, number> = { red: 0, amber: 1, green: 2 };
        switch (filterParams.sortBy) {
          case 'name':
            projects.sort((a, b) => a.name.localeCompare(b.name));
            break;
          case 'health':
            projects.sort((a, b) => (healthOrder[a.health] ?? 3) - (healthOrder[b.health] ?? 3));
            break;
          case 'value':
            projects.sort((a, b) => b.budgetRevenue - a.budgetRevenue);
            break;
          case 'completion':
            projects.sort((a, b) => a.completionPercent - b.completionPercent);
            break;
          default:
            // Default: health (worst first)
            projects.sort((a, b) => (healthOrder[a.health] ?? 3) - (healthOrder[b.health] ?? 3));
        }
      }
    }
    
    // If pagination params provided, return paginated response
    if (paginationParams) {
      const { page, pageSize } = paginationParams;
      return paginateArray(projects, page, pageSize);
    }
    
    // Otherwise return all tour projects
    return projects;
  } else {
    // Live mode - call the API with pagination and filter params
    const params = new URLSearchParams();
    
    if (paginationParams) {
      params.append('page', paginationParams.page.toString());
      params.append('pageSize', paginationParams.pageSize.toString());
    }
    
    if (filterParams) {
      if (filterParams.statusId != null) {
        params.append('statusId', filterParams.statusId.toString());
      }
      if (filterParams.health && filterParams.health !== 'all') {
        params.append('health', filterParams.health);
      }
      if (filterParams.search) {
        params.append('search', filterParams.search);
      }
      if (filterParams.sortBy) {
        params.append('sortBy', filterParams.sortBy);
      }
      if (filterParams.sortOrder) {
        params.append('sortOrder', filterParams.sortOrder);
      }
      // Group-value filters — used by the grouped view to load one leaf's rows only
      if (filterParams.type) {
        params.append('type', filterParams.type);
      }
      if (filterParams.managerId != null) {
        params.append('managerId', filterParams.managerId.toString());
      }
      if (filterParams.unassigned && filterParams.unassigned.length > 0) {
        params.append('unassigned', filterParams.unassigned.join(','));
      }
    }
    
    // Use the dedicated report endpoint for the project-status-report page
    const basePath = featureId === 'project-status-report'
      ? `${getApiBaseUrl()}/reports/project-status`
      : `${getApiBaseUrl()}/projects`;

    // Pass pmId/clientId filters for the report endpoint
    if (filterParams?.pmId != null) {
      params.append('pmId', filterParams.pmId.toString());
    }
    if (filterParams?.clientId != null) {
      params.append('clientId', filterParams.clientId.toString());
    }
    if (filterParams?.scope) {
      params.append('scope', filterParams.scope);
    }
    if (filterParams?.columnFilters) {
      params.append('columnFilters', filterParams.columnFilters);
    }

    let url = basePath;
    if (params.toString()) {
      url += `?${params.toString()}`;
    }
    
    const response = await apiFetch<any>(url);

    // Map report-endpoint rows (ProjectStatusReportRow) → Project shape so
    // the component can use the same property names regardless of endpoint.
    const isReportEndpoint = featureId === 'project-status-report';
    const mapReportRow = (row: any) => ({
      ...row,
      id: row.projectId ?? row.id,
      name: row.projectName ?? row.name,
      code: row.projectCode ?? row.code,
      health: row.pmHealth ?? row.health,
      projectManagerId: row.pmId ?? row.projectManagerId,
      actualSales: row.salesActual ?? row.actualRevenue ?? row.actualSales ?? 0,
      actualCosts: row.costActual ?? ((row.actualLabour ?? 0) + (row.actualExpense ?? 0)),
      budgetLabour: row.budgetLabour ?? 0,
      budgetExpense: row.budgetExpense ?? 0,
      wipAmount: row.wipAmount ?? 0,
    });

    // Check if response is a paginated response (has data and pagination properties)
    if (response && typeof response === 'object' && 'data' in response && 'pagination' in response) {
      const data = isReportEndpoint
        ? (response.data as any[]).map(mapReportRow)
        : response.data;
      // When called with pagination params, return the paginated envelope
      if (paginationParams) {
        return {
          data,
          pagination: response.pagination,
          ...(response.summary ? { summary: response.summary } : {}),
        };
      }
      // Report endpoint without explicit pagination — return the mapped data array
      // but attach summary as a non-enumerable property so the caller can access it
      const result = data;
      if (isReportEndpoint && response.summary) {
        Object.defineProperty(result, '_summary', { value: response.summary, enumerable: false });
      }
      return result;
    }
    
    // Validate that the response is an array (non-paginated response)
    // The API should return a JSON array, but if it returns an object or other type,
    // we need to handle it gracefully to prevent ".filter is not a function" errors
    let projects: any[];
    if (Array.isArray(response)) {
      projects = response;
    } else if (response && typeof response === 'object') {
      // Handle common wrapper patterns: { items: [...] }, { data: [...] }, { value: [...] }, { $values: [...] }
      if (Array.isArray(response.items)) {
        projects = response.items;
      } else if (Array.isArray(response.data)) {
        projects = response.data;
      } else if (Array.isArray(response.value)) {
        projects = response.value;
      } else if (Array.isArray(response.$values)) {
        // .NET ReferenceHandler.Preserve format
        projects = response.$values;
      } else {
        console.error('API returned unexpected format for projects:', response);
        throw new Error(`API returned unexpected format for projects. Expected an array but received: ${typeof response}`);
      }
    } else {
      console.error('API returned unexpected format for projects:', response);
      throw new Error(`API returned unexpected format for projects. Expected an array but received: ${typeof response}`);
    }
    
    return isReportEndpoint ? projects.map(mapReportRow) : projects;
  }
}

// ─── Server-side grouped project tree ─────────────────────────────────

/** A node of the server-aggregated group tree returned by /projects/grouped-tree. */
export interface ProjectGroupTreeNode {
  /** Raw group value used to filter this group's rows (status ID, type slug, health
   *  slug, manager ID, client ID) or `__none__` for the Unassigned bucket. */
  key: string;
  label: string;
  groupById: string;
  depth: number;
  count: number;
  subtotals: {
    count: number;
    budgetRevenue: number;
    actualRevenue: number;
    costBudget: number;
    actualCost: number;
    grossProfit: number;
    wipAmount: number;
    budgetHours: number;
    actualHours: number;
  };
  children: ProjectGroupTreeNode[];
}

export interface ProjectGroupedTreeResponse {
  groups: ProjectGroupTreeNode[];
  errorMessage?: string | null;
}

/**
 * Fetches the server-aggregated group tree for the project list. Only counts and
 * subtotals cross the wire — each leaf group loads its project rows one page at a
 * time through `fetchProjects`.
 *
 * Returns null in demo/tour mode so the caller falls back to grouping the local
 * mock dataset client-side via `groupProjects()`.
 */
export async function fetchProjectGroupedTree(
  groupBy: string[],
  filterParams?: ProjectFilterParams
): Promise<ProjectGroupedTreeResponse | null> {
  if (getDataMode() !== 'live') return null;
  if (groupBy.length === 0) return { groups: [] };

  const params = new URLSearchParams();
  params.append('groupBy', groupBy.join(','));

  if (filterParams) {
    if (filterParams.statusId != null) {
      params.append('statusId', filterParams.statusId.toString());
    }
    if (filterParams.health && filterParams.health !== 'all') {
      params.append('health', filterParams.health);
    }
    if (filterParams.search) {
      params.append('search', filterParams.search);
    }
    if (filterParams.managerId != null) {
      params.append('managerId', filterParams.managerId.toString());
    }
    if (filterParams.scope) {
      params.append('scope', filterParams.scope);
    }
  }

  const response = await apiFetch<ProjectGroupedTreeResponse>(
    `${getApiBaseUrl()}/projects/grouped-tree?${params.toString()}`
  );

  return {
    groups: response?.groups ?? [],
    errorMessage: response?.errorMessage ?? null,
  };
}

export async function fetchProjectById(projectId: number | string): Promise<any> {
  const mode = getDataMode();
  
  if (mode === 'demo') {
    await new Promise(resolve => setTimeout(resolve, 100));
    const project = [...getDemoCreatedProjects(), ...mockData.mockProjects]
      .find(p => String(p.id) === String(projectId)) || null;
    if (!project) return null;
    const thresholds = getBudgetHealthThresholds();
    return {
      ...project,
      health: deriveProjectHealth(
        {
          actualCost: (project.actualLabour ?? 0) + (project.actualExpense ?? 0),
          budgetCost: (project.budgetLabour ?? 0) + (project.budgetExpense ?? 0),
          startDate: project.startDate,
          endDate: project.endDate,
          completionPercent: project.completionPercentOverride ?? project.completionPercent ?? 0,
          actualHours: project.actualHours ?? 0,
          budgetHours: project.budgetHours ?? 0,
          wipAmount: project.wipAmount ?? 0,
          actualRevenue: project.actualRevenue ?? 0,
          budgetRevenue: project.budgetRevenue ?? 0,
          forecastEndDate: project.forecastEndDate,
        },
        thresholds
      ),
    };
  } else if (mode === 'tour') {
    await new Promise(resolve => setTimeout(resolve, 100));
    const project = tourMockData.tourMockProjects.find(p => p.id === projectId) || null;
    if (!project) return null;
    const thresholds = getBudgetHealthThresholds();
    return {
      ...project,
      health: deriveProjectHealth(
        {
          actualCost: (project.actualLabour ?? 0) + (project.actualExpense ?? 0),
          budgetCost: (project.budgetLabour ?? 0) + (project.budgetExpense ?? 0),
          startDate: project.startDate,
          endDate: project.endDate,
          completionPercent: project.completionPercentOverride ?? project.completionPercent ?? 0,
          actualHours: project.actualHours ?? 0,
          budgetHours: project.budgetHours ?? 0,
          wipAmount: project.wipAmount ?? 0,
          actualRevenue: project.actualRevenue ?? 0,
          budgetRevenue: project.budgetRevenue ?? 0,
          forecastEndDate: project.forecastEndDate,
        },
        thresholds
      ),
    };
  } else {
    return apiFetch<any>(`${getApiBaseUrl()}/projects/${projectId}`);
  }
}

export interface UpdateProjectData {
  id: number;
  name?: string;
  code?: string;
  description?: string;
  statusId?: number;
  type?: string;
  revenueRecognitionMethod?: string;
  startDate?: string;
  endDate?: string;
  clientIds?: string;
  projectManagerId?: number;
  accountManagerId?: number;
  billable?: boolean;
  completionPercentMode?: string;
  completionPercentOverride?: number | null;
  completionPercentOverriddenBy?: string | null;
  completionPercentOverriddenAt?: string | null;
  /** @deprecated Health is now computed server-side — this field is stripped from PUT/POST requests. */
  health?: string;
}

export async function updateProject(id: number, data: UpdateProjectData): Promise<any> {
  const mode = getDataMode();
  
  if (mode === 'demo') {
    await new Promise(resolve => setTimeout(resolve, 500));
    
    return {
      ...data,
      updatedAt: new Date().toISOString(),
    };
  } else {
    if (!id) {
      throw new Error('Project ID is required for update.');
    }
    if (id !== data.id) {
      throw new Error('Project ID mismatch.');
    }
    
    // Only include fields that were explicitly provided so partial updates
    // (e.g. bulk change PM or status) don't send null for unrelated fields.
    // Sending explicit null for non-nullable backend properties (e.g. Name,
    // Type, RevenueRecognitionMethod) causes ASP.NET model-validation to
    // return 400 Bad Request.
    const projectPayload: Record<string, unknown> = { id: data.id };
    if (data.name !== undefined) projectPayload.name = data.name;
    if (data.code !== undefined) projectPayload.code = data.code || null;
    if (data.description !== undefined) projectPayload.description = data.description || null;
    if (data.statusId !== undefined) projectPayload.statusId = data.statusId;
    if (data.type !== undefined) projectPayload.type = data.type;
    if (data.revenueRecognitionMethod !== undefined) projectPayload.revenueRecognitionMethod = data.revenueRecognitionMethod || null;
    if (data.startDate !== undefined) projectPayload.startDate = data.startDate || null;
    if (data.endDate !== undefined) projectPayload.endDate = data.endDate || null;
    if (data.clientIds !== undefined) projectPayload.clientIds = data.clientIds || null;
    if (data.projectManagerId !== undefined) projectPayload.projectManagerId = data.projectManagerId || null;
    if (data.accountManagerId !== undefined) projectPayload.accountManagerId = data.accountManagerId ?? null;
    if (data.billable !== undefined) projectPayload.billable = data.billable;
    if (data.completionPercentMode !== undefined) projectPayload.completionPercentMode = data.completionPercentMode;
    if (data.completionPercentOverride !== undefined) projectPayload.completionPercentOverride = data.completionPercentOverride;
    if (data.completionPercentOverriddenBy !== undefined) projectPayload.completionPercentOverriddenBy = data.completionPercentOverriddenBy;
    if (data.completionPercentOverriddenAt !== undefined) projectPayload.completionPercentOverriddenAt = data.completionPercentOverriddenAt;
    
    return apiFetch<any>(`${getApiBaseUrl()}/projects/${id}`, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(projectPayload),
    });
  }
}

// ── Project Content API (Objectives, Key Deliverables, Notes) ──

export async function fetchProjectNotes(projectId: number): Promise<ProjectNote[]> {
  return apiFetch<ProjectNote[]>(`${getApiBaseUrl()}/projects/${projectId}/notes`);
}

export async function createProjectNote(
  projectId: number,
  content: string,
  createdByName?: string,
  createdByInitials?: string,
): Promise<ProjectNote> {
  return apiFetch<ProjectNote>(`${getApiBaseUrl()}/projects/${projectId}/notes`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ content, createdByName, createdByInitials }),
  });
}

export async function updateProjectNote(
  projectId: number,
  noteId: string,
  content: string,
): Promise<ProjectNote> {
  return apiFetch<ProjectNote>(`${getApiBaseUrl()}/projects/${projectId}/notes/${noteId}`, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ content }),
  });
}

export async function deleteProjectNote(
  projectId: number,
  noteId: string,
): Promise<void> {
  await apiFetch<void>(`${getApiBaseUrl()}/projects/${projectId}/notes/${noteId}`, {
    method: 'DELETE',
  });
}

export async function deleteProject(projectId: number, force: boolean = false): Promise<void> {
  const url = force
    ? `${getApiBaseUrl()}/projects/${projectId}?force=true`
    : `${getApiBaseUrl()}/projects/${projectId}`;
  await apiFetch<void>(url, {
    method: 'DELETE',
  });
}

export async function saveProjectObjectives(
  projectId: number,
  objectives: string | null,
): Promise<any> {
  return apiFetch<any>(`${getApiBaseUrl()}/projects/${projectId}`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ id: projectId, objectives }),
  });
}

export async function saveProjectKeyDeliverables(
  projectId: number,
  keyDeliverables: string | null,
): Promise<any> {
  return apiFetch<any>(`${getApiBaseUrl()}/projects/${projectId}`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ id: projectId, keyDeliverables }),
  });
}

export async function fetchClients(featureId: string = 'clients') {
  const mode = getDataMode();
  
  if (mode === 'demo') {
    await new Promise(resolve => setTimeout(resolve, 200));
    return mockData.mockClients;
  } else if (mode === 'tour') {
    await new Promise(resolve => setTimeout(resolve, 200));
    return tourMockData.tourMockClients;
  } else {
    return apiFetch<any[]>(`${getApiBaseUrl()}/clients`);
  }
}

/**
 * Recursively strip null values from a plain object/array so that the frontend
 * Client type sees `undefined` (absent key) instead of `null` for unset fields.
 * This prevents guards like `value != null` from passing when the backend
 * serialises an unset field as JSON null.
 */
function stripNulls(obj: unknown): unknown {
  if (obj === null || obj === undefined) return undefined;
  if (Array.isArray(obj)) return obj.map(stripNulls);
  if (typeof obj === 'object') {
    const result: Record<string, unknown> = {};
    for (const [key, value] of Object.entries(obj as Record<string, unknown>)) {
      if (value !== null) {
        result[key] = typeof value === 'object' ? stripNulls(value) : value;
      }
    }
    return result;
  }
  return obj;
}

/**
 * Fetches full Finance System detail for a client (pass-through to Financials GraphQL API).
 * In demo/tour mode, returns the mock client data which already has all fields.
 * In live mode, calls GET /api/clients/{id}/financials?companyId={companyId}.
 */
export async function fetchClientFinancials(clientId: number, companyId: number): Promise<Partial<Client>> {
  const mode = getDataMode();

  if (mode === 'demo') {
    await new Promise(resolve => setTimeout(resolve, 300));
    const client = mockData.mockClients.find(c => c.id === clientId);
    if (!client) throw new Error(`Client ${clientId} not found`);
    return client;
  } else if (mode === 'tour') {
    await new Promise(resolve => setTimeout(resolve, 300));
    const client = tourMockData.tourMockClients?.find((c: any) => c.id === clientId);
    if (!client) throw new Error(`Client ${clientId} not found`);
    return client;
  } else {
    const raw = await apiFetch<Record<string, unknown>>(`${getApiBaseUrl()}/clients/${clientId}/financials?companyId=${companyId}`);

    // The backend CustomerDetailResult serialises unset fields as JSON null.
    // The frontend Client type uses `undefined` for absent values and many
    // rendering guards use `!== undefined`, so null values slip through and
    // crash (e.g. Intl.NumberFormat receives currency:null → RangeError).
    // Strip nulls recursively so the existing guards work correctly.
    //
    // NOTE: We intentionally do NOT throw here when isSuccess === false.
    // The caller (ClientSidePanel) inspects these fields directly in its
    // .then() handler so error detection doesn't rely on throw/catch
    // propagation across async boundaries.
    return stripNulls(raw) as Partial<Client>;
  }
}

// ── Client Billing Summary ──────────────────────────────────────────────

export interface ClientBillingSummary {
  outstandingTotal: number;
  overdueCount: number;
  oldestUnpaidDate: string | null;
  currencyCode: string;
  invoices?: Array<{
    invoiceNumber: string | null;
    date: string | null;
    dueDate: string | null;
    amount: number;
    outstanding: number;
    isOverdue: boolean;
    daysOverdue: number | null;
  }>;
  isSuccess?: boolean;
  errorMessage?: string;
}

/**
 * Fetches outstanding invoice summary for a client from the Financials debtors ledger.
 * In demo/tour mode, returns deterministic mock data keyed by client ID.
 * In live mode, calls GET /api/clients/{id}/billing-summary?companyId={companyId}.
 */
export async function fetchClientBillingSummary(clientId: number, companyId: number): Promise<ClientBillingSummary> {
  const mode = getDataMode();

  if (mode === 'demo' || mode === 'tour') {
    await new Promise(resolve => setTimeout(resolve, 300));
    const seed = clientId;
    const options: ClientBillingSummary[] = [
      { outstandingTotal: 28750, overdueCount: 2, oldestUnpaidDate: '2025-02-15', currencyCode: 'GBP' },
      { outstandingTotal: 0,     overdueCount: 0, oldestUnpaidDate: null,          currencyCode: 'GBP' },
      { outstandingTotal: 14200, overdueCount: 1, oldestUnpaidDate: '2025-03-01', currencyCode: 'GBP' },
      { outstandingTotal: 52100, overdueCount: 3, oldestUnpaidDate: '2025-01-20', currencyCode: 'GBP' },
      { outstandingTotal: 8500,  overdueCount: 0, oldestUnpaidDate: '2025-04-10', currencyCode: 'GBP' },
    ];
    return options[seed % options.length];
  }

  const raw = await apiFetch<ClientBillingSummary>(
    `${getApiBaseUrl()}/clients/${clientId}/billing-summary?companyId=${companyId}`
  );
  return raw;
}

// Role type for the Role table
export interface RoleOption {
  id: number;
  name: string;
  description?: string;
  defaultCostRate: number;
  defaultBillRate: number;
  defaultDailyCostRate?: number;
  defaultDailyBillRate?: number;
}

export async function fetchRoles(): Promise<RoleOption[]> {
  const mode = getDataMode();
  
  if (mode === 'demo' || mode === 'tour') {
    await new Promise(resolve => setTimeout(resolve, 200));
    return [
      { id: 1, name: 'Project Manager', defaultCostRate: 100, defaultBillRate: 200 },
      { id: 2, name: 'Senior Consultant', defaultCostRate: 90, defaultBillRate: 180 },
      { id: 3, name: 'Consultant', defaultCostRate: 75, defaultBillRate: 150 },
      { id: 4, name: 'Developer', defaultCostRate: 75, defaultBillRate: 150 },
      { id: 5, name: 'Senior Developer', defaultCostRate: 88, defaultBillRate: 175 },
      { id: 6, name: 'QA Analyst', defaultCostRate: 63, defaultBillRate: 125 },
      { id: 7, name: 'QA Engineer', defaultCostRate: 63, defaultBillRate: 125 },
      { id: 8, name: 'Business Analyst', defaultCostRate: 70, defaultBillRate: 140 },
      { id: 9, name: 'Technical Architect', defaultCostRate: 110, defaultBillRate: 220 },
      { id: 10, name: 'UX Designer', defaultCostRate: 80, defaultBillRate: 160 },
      { id: 11, name: 'Scrum Master', defaultCostRate: 88, defaultBillRate: 175 },
      { id: 12, name: 'Senior QA', defaultCostRate: 59.38, defaultBillRate: 118.75 },
    ];
  } else {
    const roles = await apiFetch<RoleOption[]>(`${getApiBaseUrl()}/roles`);
    return roles;
  }
}
export interface RoleWithRates {
  id: number;
  name: string;
  description?: string;
  defaultCostRate: number;
  defaultBillRate: number;
  defaultDailyCostRate?: number;
  defaultDailyBillRate?: number;
}

export async function fetchRolesWithRates(): Promise<RoleWithRates[]> {
  const mode = getDataMode();
  
  if (mode === 'demo' || mode === 'tour') {
    await new Promise(resolve => setTimeout(resolve, 200));
    return [
      { id: 1, name: 'Project Manager', description: 'Experienced project manager overseeing project delivery and team coordination', defaultCostRate: 70, defaultBillRate: 140, defaultDailyCostRate: 560, defaultDailyBillRate: 1120 },
      { id: 2, name: 'Senior Consultant', description: 'Senior consultant providing strategic guidance and expertise', defaultCostRate: 90, defaultBillRate: 180, defaultDailyCostRate: 720, defaultDailyBillRate: 1440 },
      { id: 3, name: 'Consultant', description: 'Consultant delivering solutions and client engagement', defaultCostRate: 75, defaultBillRate: 150, defaultDailyCostRate: 600, defaultDailyBillRate: 1200 },
      { id: 4, name: 'Developer', description: 'Software developer building and maintaining applications', defaultCostRate: 75, defaultBillRate: 150, defaultDailyCostRate: 600, defaultDailyBillRate: 1200 },
      { id: 5, name: 'Senior Developer', description: 'Experienced software developer with 5+ years of expertise in full-stack development', defaultCostRate: 88, defaultBillRate: 175, defaultDailyCostRate: 704, defaultDailyBillRate: 1400 },
      { id: 6, name: 'QA Analyst', description: 'Quality assurance analyst ensuring software quality through testing', defaultCostRate: 63, defaultBillRate: 125, defaultDailyCostRate: 504, defaultDailyBillRate: 1000 },
      { id: 7, name: 'QA Engineer', description: 'Quality assurance specialist ensuring software quality through testing and automation', defaultCostRate: 63, defaultBillRate: 125, defaultDailyCostRate: 504, defaultDailyBillRate: 1000 },
      { id: 8, name: 'Business Analyst', description: 'Analyst bridging business needs with technical solutions and requirements', defaultCostRate: 70, defaultBillRate: 140, defaultDailyCostRate: 560, defaultDailyBillRate: 1120 },
      { id: 9, name: 'Technical Architect', description: 'Technical architect designing system architecture and infrastructure', defaultCostRate: 110, defaultBillRate: 220, defaultDailyCostRate: 880, defaultDailyBillRate: 1760 },
      { id: 10, name: 'UX Designer', description: 'Creative designer specializing in user experience and interface design', defaultCostRate: 80, defaultBillRate: 160, defaultDailyCostRate: 640, defaultDailyBillRate: 1280 },
      { id: 11, name: 'Scrum Master', description: 'Scrum master facilitating agile processes and team collaboration', defaultCostRate: 88, defaultBillRate: 175, defaultDailyCostRate: 704, defaultDailyBillRate: 1400 },
      { id: 12, name: 'Senior QA', description: 'Senior quality assurance specialist leading test strategy and quality processes', defaultCostRate: 59.38, defaultBillRate: 118.75, defaultDailyCostRate: 475.04, defaultDailyBillRate: 950 },
    ];
  } else {
    return apiFetch<RoleWithRates[]>(`${getApiBaseUrl()}/roles`);
  }
}

export interface RoleEmployee {
  id: number;
  name: string;
  email: string;
  department: string;
  photo?: string;
}

export async function fetchEmployeesByRoleId(roleId: number): Promise<RoleEmployee[]> {
  const mode = getDataMode();
  
  if (mode === 'demo' || mode === 'tour') {
    await new Promise(resolve => setTimeout(resolve, 200));
    return [];
  } else {
    return apiFetch<RoleEmployee[]>(`${getApiBaseUrl()}/roles/${roleId}/employees`);
  }
}

export interface ProjectRoleOverride {
  id: number;
  projectId: number;
  projectName: string;
  roleId: number;
  roleName: string;
  defaultCostRate: number;
  defaultBillRate: number;
  defaultDailyCostRate: number | null;
  defaultDailyBillRate: number | null;
  costRateOverride: number | null;
  billRateOverride: number | null;
  dailyCostRateOverride: number | null;
  dailyBillRateOverride: number | null;
}

export async function fetchProjectRoleOverrides(): Promise<ProjectRoleOverride[]> {
  const mode = getDataMode();

  if (mode === 'demo' || mode === 'tour') {
    await new Promise(resolve => setTimeout(resolve, 200));
    return [
      { id: 1, projectId: 1, projectName: 'Website Redesign', roleId: 1, roleName: 'Project Manager', defaultCostRate: 70, defaultBillRate: 140, defaultDailyCostRate: 560, defaultDailyBillRate: 1120, costRateOverride: 75, billRateOverride: 155, dailyCostRateOverride: 600, dailyBillRateOverride: 1240 },
      { id: 2, projectId: 1, projectName: 'Website Redesign', roleId: 4, roleName: 'Developer', defaultCostRate: 75, defaultBillRate: 150, defaultDailyCostRate: 600, defaultDailyBillRate: 1200, costRateOverride: 80, billRateOverride: 165, dailyCostRateOverride: 640, dailyBillRateOverride: 1320 },
      { id: 3, projectId: 2, projectName: 'Mobile App', roleId: 5, roleName: 'Senior Developer', defaultCostRate: 88, defaultBillRate: 175, defaultDailyCostRate: 704, defaultDailyBillRate: 1400, costRateOverride: 95, billRateOverride: 190, dailyCostRateOverride: 760, dailyBillRateOverride: 1520 },
    ];
  } else {
    return apiFetch<ProjectRoleOverride[]>(`${getApiBaseUrl()}/ratecardentries/project-role-overrides`);
  }
}

export interface EmployeeRoleOverride {
  id: number;
  employeeId: number;
  employeeName: string;
  roleId: number;
  roleName: string;
  projectId: number;
  projectName: string;
  defaultCostRate: number;
  defaultBillRate: number;
  defaultDailyCostRate: number | null;
  defaultDailyBillRate: number | null;
  employeeCostRate: number | null;
  employeeBillRate: number | null;
  employeeDailyCostRate: number | null;
  employeeDailyBillRate: number | null;
}

export async function fetchEmployeeRoleOverrides(): Promise<EmployeeRoleOverride[]> {
  const mode = getDataMode();

  if (mode === 'demo' || mode === 'tour') {
    await new Promise(resolve => setTimeout(resolve, 200));
    return [
      { id: 1, employeeId: 1, employeeName: 'Alice Morris', roleId: 4, roleName: 'Developer', projectId: 1, projectName: 'Website Redesign', defaultCostRate: 75, defaultBillRate: 150, defaultDailyCostRate: 600, defaultDailyBillRate: 1200, employeeCostRate: 82, employeeBillRate: 170, employeeDailyCostRate: 656, employeeDailyBillRate: 1360 },
      { id: 2, employeeId: 2, employeeName: 'Bob Johnson', roleId: 1, roleName: 'Project Manager', projectId: 2, projectName: 'Mobile App', defaultCostRate: 70, defaultBillRate: 140, defaultDailyCostRate: 560, defaultDailyBillRate: 1120, employeeCostRate: 78, employeeBillRate: 160, employeeDailyCostRate: 624, employeeDailyBillRate: 1280 },
    ];
  } else {
    return apiFetch<EmployeeRoleOverride[]>(`${getApiBaseUrl()}/ratecardentries/employee-role-overrides`);
  }
}

/**
 * When companyAccessOnly is set, only employees with access to the selected company are
 * returned — use it where the employee has to be a user of the company (e.g. Project Manager).
 */
export async function fetchEmployees(
  featureId: string = 'employees',
  options: { companyAccessOnly?: boolean } = {}
) {
  const mode = getDataMode();
  
  if (mode === 'demo') {
    await new Promise(resolve => setTimeout(resolve, 200));
    // Use all mock employees from mock-data.ts
    return mockData.mockEmployees;
  } else if (mode === 'tour') {
    await new Promise(resolve => setTimeout(resolve, 200));
    return tourMockData.tourMockEmployees;
  } else {
    const query = options.companyAccessOnly ? '?companyAccessOnly=true' : '';
    return apiFetch<any[]>(`${getApiBaseUrl()}/employees${query}`);
  }
}

// ── Skills ──────────────────────────────────────────────────────────────

export interface SkillSummaryResponse {
  id: number;
  skillName: string;
  category: string;
  categoryColor?: string;
  resourceCount: number;
  resources: {
    personId: number;
    personName: string;
    role: string;
    proficiency: number;
    photo?: string;
  }[];
}

export interface SkillCategoryResponse {
  id: number;
  name: string;
  color?: string;
  skillCount: number;
}

export async function fetchSkills(): Promise<SkillSummaryResponse[]> {
  const mode = getDataMode();

  if (mode === 'demo' || mode === 'tour') {
    await new Promise(resolve => setTimeout(resolve, 300));
    // Return mock data for demo/tour modes
    const { MOCK_SKILLS } = await import('./mock-skills-data');
    return MOCK_SKILLS.map(s => ({
      id: Number(s.skillId.replace(/\D/g, '')) || 0,
      skillName: s.skillName,
      category: s.category,
      resourceCount: s.resourceCount,
      resources: s.resources.map(r => ({
        personId: Number(r.personId.replace(/\D/g, '')) || 0,
        personName: r.personName,
        role: r.role,
        proficiency: r.proficiency,
        photo: r.photo,
      })),
    }));
  }

  return apiFetch<SkillSummaryResponse[]>(`${getApiBaseUrl()}/skills`);
}

export async function fetchSkillById(id: number): Promise<SkillSummaryResponse> {
  return apiFetch<SkillSummaryResponse>(`${getApiBaseUrl()}/skills/${id}`);
}

export async function fetchSkillCategories(): Promise<SkillCategoryResponse[]> {
  const mode = getDataMode();

  if (mode === 'demo' || mode === 'tour') {
    await new Promise(resolve => setTimeout(resolve, 200));
    const { SKILL_CATEGORIES } = await import('./mock-skills-data');
    return SKILL_CATEGORIES.map((name, i) => ({ id: i + 1, name, skillCount: 0 }));
  }

  return apiFetch<SkillCategoryResponse[]>(`${getApiBaseUrl()}/skills/categories`);
}

export async function fetchCompanies(featureId: string = 'companies') {
  const mode = getDataMode();
  
  if (mode === 'demo') {
    await new Promise(resolve => setTimeout(resolve, 200));
    return mockData.mockCompanies.filter(c => c.status === 'active');
  } else {
    // In live mode, fetch active companies from API
    return apiFetch<any[]>(`${getApiBaseUrl()}/companies/active`);
  }
}

export async function fetchAllCompanies() {
  const mode = getDataMode();
  
  if (mode === 'demo') {
    await new Promise(resolve => setTimeout(resolve, 200));
    return mockData.mockCompanies;
  } else {
    // In live mode, fetch all companies from API
    return apiFetch<any[]>(`${getApiBaseUrl()}/companies`);
  }
}

export interface CreateCompanyData {
  name: string;
  code: string;
  instanceId?: string;
  status?: string;
  description?: string;
  employeeCount?: number;
  activeProjects?: number;
}

export async function createCompany(data: CreateCompanyData): Promise<any> {
  const mode = getDataMode();
  
  if (mode === 'demo') {
    await new Promise(resolve => setTimeout(resolve, 500));
    
    const mockCompany = {
      id: `demo-company-${Date.now()}`,
      name: data.name,
      code: data.code,
      instanceId: data.instanceId || '',
      status: data.status || 'setup',
      description: data.description || '',
      employeeCount: data.employeeCount || 0,
      activeProjects: data.activeProjects || 0,
      createdAt: new Date(),
      updatedAt: new Date(),
    };
    
    return mockCompany;
  } else {
    const companyPayload = {
      name: data.name,
      code: data.code,
      ...(data.instanceId ? { instanceId: data.instanceId } : {}),
      status: data.status || 'setup',
      ...(data.description ? { description: data.description } : {}),
      employeeCount: data.employeeCount || 0,
      activeProjects: data.activeProjects || 0,
    };
    
    return apiFetch<any>(`${getApiBaseUrl()}/companies`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(companyPayload),
    });
  }
}

export interface UpdateCompanyData {
  id: string;
  name: string;
  code: string;
  instanceId?: string;
  status?: string;
  description?: string;
  employeeCount?: number;
  activeProjects?: number;
}

export async function updateCompany(id: string, data: UpdateCompanyData): Promise<any> {
  const mode = getDataMode();
  
  if (mode === 'demo') {
    await new Promise(resolve => setTimeout(resolve, 500));
    
    return {
      ...data,
      updatedAt: new Date(),
    };
  } else {
    if (!id) {
      throw new Error('Company ID is required for update.');
    }
    if (id !== data.id) {
      throw new Error('Company ID mismatch.');
    }
    
    const companyPayload = {
      id: data.id,
      name: data.name,
      code: data.code,
      instanceId: data.instanceId || null,
      status: data.status || 'setup',
      description: data.description || null,
      employeeCount: data.employeeCount || 0,
      activeProjects: data.activeProjects || 0,
    };
    
    return apiFetch<any>(`${getApiBaseUrl()}/companies/${id}`, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(companyPayload),
    });
  }
}

export async function deleteCompany(id: string): Promise<void> {
  const mode = getDataMode();
  
  if (mode === 'demo') {
    await new Promise(resolve => setTimeout(resolve, 500));
    return;
  } else {
    if (!id) {
      throw new Error('Company ID is required for deletion.');
    }
    
    await apiFetch<void>(`${getApiBaseUrl()}/companies/${id}`, {
      method: 'DELETE',
    });
  }
}

export async function fetchResourceAllocations(featureId: string = 'resources') {
  const mode = getDataMode();
  
  if (mode === 'demo') {
    await new Promise(resolve => setTimeout(resolve, 250));
    // Use enhanced mock allocations for resource assignment demo
    const { mockExistingAllocations } = await import('./mock-allocation-data');
    return mockExistingAllocations;
  } else {
    return apiFetch<any[]>(`${getApiBaseUrl()}/resourceallocations`);
  }
}

export interface CreateResourceAllocationData {
  projectId: number;
  employeeId: number;
  role: string;
  allocatedHours?: number;
  allocationPercent?: number;
  startDate: string;
  endDate: string;
  status?: 'confirmed' | 'tentative';
}

export async function createResourceAllocation(data: CreateResourceAllocationData): Promise<any> {
  const mode = getDataMode();
  
  if (mode === 'demo') {
    await new Promise(resolve => setTimeout(resolve, 300));
    
    const mockAllocation = {
      id: `demo-allocation-${Date.now()}`,
      ...data,
      status: data.status || 'confirmed',
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
    };
    
    return mockAllocation;
  } else {
    const allocationPayload = {
      projectId: data.projectId,
      employeeId: data.employeeId,
      role: data.role,
      allocationPercent: data.allocationPercent || 100,
      allocatedHours: data.allocatedHours || null,
      startDate: data.startDate,
      endDate: data.endDate,
      status: data.status || 'confirmed',
    };
    
    return apiFetch<any>(`${getApiBaseUrl()}/resourceallocations`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(allocationPayload),
    });
  }
}

export async function deleteResourceAllocation(id: number): Promise<void> {
  const mode = getDataMode();
  
  if (mode === 'demo') {
    await new Promise(resolve => setTimeout(resolve, 300));
    return;
  } else {
    if (!id) {
      throw new Error('Resource allocation ID is required for deletion.');
    }
    
    await apiFetch<void>(`${getApiBaseUrl()}/resourceallocations/${id}`, {
      method: 'DELETE',
    });
  }
}

// --- Project Team Exclusions ---

export interface ProjectTeamExclusion {
  id: number;
  projectId: number;
  employeeId: number;
}

export async function fetchTeamExclusionsByProjectId(projectId: number): Promise<ProjectTeamExclusion[]> {
  const mode = getDataMode();
  if (mode === 'demo') {
    return [];
  }
  return apiFetch<ProjectTeamExclusion[]>(`${getApiBaseUrl()}/projectteamexclusions/by-project/${projectId}`);
}

export async function createTeamExclusion(projectId: number, employeeId: number): Promise<ProjectTeamExclusion> {
  const mode = getDataMode();
  if (mode === 'demo') {
    return { id: 0, projectId, employeeId };
  }
  return apiFetch<ProjectTeamExclusion>(`${getApiBaseUrl()}/projectteamexclusions`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ projectId, employeeId }),
  });
}

export async function deleteTeamExclusion(projectId: number, employeeId: number): Promise<void> {
  const mode = getDataMode();
  if (mode === 'demo') {
    return;
  }
  await apiFetch<void>(`${getApiBaseUrl()}/projectteamexclusions/${projectId}/${employeeId}`, {
    method: 'DELETE',
  });
}

export interface WipReportSummary {
  totalWip: number;
  readyToBillAmount: number;
  readyToBillCount: number;
  totalUnderBilling: number;
  totalOverBilling: number;
  totalWrittenOff: number;
}

export interface WipReportFilterParams {
  projectType?: string;
  pmId?: number;
  pmName?: string;
  search?: string;
  /** 'billable' | 'non-billable' — omit for all */
  billable?: string;
}

export interface WipReportResponse {
  data: WIPReport[];
  pagination: import('@/types/pagination').PaginationMetadata;
  summary: WipReportSummary;
}

export async function fetchWIPReports(
  _featureId: string = 'wip-reporting',
  paginationParams?: PaginationParams,
  filterParams?: WipReportFilterParams
): Promise<WipReportResponse | WIPReport[]> {
  const mode = getDataMode();
  
  if (mode === 'demo') {
    await new Promise(resolve => setTimeout(resolve, 400));
    const allReports = mockData.mockWIPReports();

    // Apply filters in demo mode
    let filtered = allReports;
    if (filterParams?.projectType) {
      filtered = filtered.filter(r => r.projectType === filterParams.projectType);
    }
    if (filterParams?.pmId != null) {
      filtered = filtered.filter(r => (r as any).pmId === filterParams.pmId);
    }
    if (filterParams?.pmName) {
      filtered = filtered.filter(r => r.pmName === filterParams.pmName);
    }
    if (filterParams?.search) {
      const q = filterParams.search.toLowerCase();
      filtered = filtered.filter(r =>
        r.projectName.toLowerCase().includes(q) ||
        (r.clientName ?? '').toLowerCase().includes(q),
      );
    }
    if (filterParams?.billable === 'billable') {
      filtered = filtered.filter(r => r.billable !== false);
    } else if (filterParams?.billable === 'non-billable') {
      filtered = filtered.filter(r => r.billable === false);
    }

    if (paginationParams) {
      const { data, pagination } = paginateArray(filtered, paginationParams.page, paginationParams.pageSize);
      const summary: WipReportSummary = {
        totalWip: filtered.reduce((sum, w) => sum + w.wipAmount, 0),
        readyToBillAmount: filtered.filter(w => w.readyToBill).reduce((sum, w) => sum + w.wipAmount, 0),
        readyToBillCount: filtered.filter(w => w.readyToBill).length,
        totalUnderBilling: filtered.reduce((sum, w) => sum + w.underBilling, 0),
        totalOverBilling: filtered.reduce((sum, w) => sum + w.overBilling, 0),
        totalWrittenOff: filtered.reduce((sum, w) => sum + (w.writtenOffAmount ?? 0), 0),
      };
      return { data, pagination, summary };
    }

    return filtered;
  } else {
    // Live mode — call the dedicated WIP report endpoint with server-side pagination
    const params = new URLSearchParams();

    if (paginationParams) {
      params.append('page', paginationParams.page.toString());
      params.append('pageSize', paginationParams.pageSize.toString());
    }

    if (filterParams?.projectType) {
      params.append('projectType', filterParams.projectType);
    }
    if (filterParams?.pmId != null) {
      params.append('pmId', filterParams.pmId.toString());
    }
    if (filterParams?.pmName) {
      params.append('pmName', filterParams.pmName);
    }
    if (filterParams?.search) {
      params.append('search', filterParams.search);
    }

    let url = `${getApiBaseUrl()}/reports/wip`;
    if (params.toString()) {
      url += `?${params.toString()}`;
    }

    const response = await apiFetch<any>(url);

    // Map API response to WIPReport format
    if (response && typeof response === 'object' && 'data' in response && 'pagination' in response) {
      const rows: WIPReport[] = (response.data as any[]).map((row: any) => ({
        projectId: row.projectId,
        projectName: row.projectName,
        clientName: row.clientNames || 'Unknown',
        pmName: row.pmName,
        projectType: row.projectType === 'fixed-price' ? 'fixed-price'
          : row.projectType === 'retainer' ? 'retainer'
          : 'time-and-materials',
        costsIncurred: row.costsIncurred,
        profitEarned: row.profitEarned,
        amountBilled: row.amountBilled,
        wipAmount: row.wipAmount,
        agingDays: row.agingDays,
        agingBands: wipAgingBandsFromApi(row),
        readyToBill: row.readyToBill,
        overBilling: row.overBilling,
        underBilling: row.underBilling,
        writtenOffAmount: row.writtenOffAmount,
        nextBillingEvent: row.nextBillingEvent ?? undefined,
        nextBillingEventAmount: row.nextBillingEventAmount ?? undefined,
        nextBillingEventTriggerType: row.nextBillingEventTriggerType ?? undefined,
        nextBillingEventTriggerPercent: row.nextBillingEventTriggerPercent ?? undefined,
      }));

      if (paginationParams) {
        return {
          data: rows,
          pagination: response.pagination,
          summary: response.summary,
        };
      }

      return rows;
    }

    // Fallback: plain array response (no pagination)
    return (response as any[]).map((row: any) => ({
      projectId: row.projectId ?? row.id,
      projectName: row.projectName ?? row.name,
      clientName: row.clientNames || 'Unknown',
      pmName: row.pmName,
      projectType: row.projectType === 'fixed-price' ? 'fixed-price'
        : row.projectType === 'retainer' ? 'retainer'
        : 'time-and-materials',
      costsIncurred: row.costsIncurred ?? (row.costActual ?? ((row.actualLabour ?? 0) + (row.actualExpense ?? 0))),
      profitEarned: row.profitEarned ?? (row.actualRevenue - (row.costActual ?? ((row.actualLabour ?? 0) + (row.actualExpense ?? 0)))),
      amountBilled: row.amountBilled ?? row.actualRevenue,
      wipAmount: row.wipAmount,
      agingDays: row.agingDays ?? 0,
      agingBands: wipAgingBandsFromApi(row),
      readyToBill: row.readyToBill ?? (row.wipAmount > 0),
      overBilling: row.overBilling ?? (row.wipAmount < 0 ? Math.abs(row.wipAmount) : 0),
      underBilling: row.underBilling ?? (row.wipAmount > 0 ? row.wipAmount : 0),
      nextBillingEvent: row.nextBillingEvent ?? undefined,
      nextBillingEventAmount: row.nextBillingEventAmount ?? undefined,
      nextBillingEventTriggerType: row.nextBillingEventTriggerType ?? undefined,
      nextBillingEventTriggerPercent: row.nextBillingEventTriggerPercent ?? undefined,
    }));
  }
}

/**
 * Fetch the complete WIP dataset by walking every page of the paginated
 * WIP report endpoint. Falls back to the plain array response when the
 * underlying call is not paginated (e.g. demo mode).
 */
export async function fetchAllWIPReports(
  featureId: string = 'dashboard',
  filterParams?: WipReportFilterParams
): Promise<WIPReport[]> {
  const pageSize = 100;

  const first = await fetchWIPReports(featureId, { page: 1, pageSize }, filterParams);
  if (Array.isArray(first)) {
    return first;
  }

  const allRows: WIPReport[] = [...first.data];
  const totalPages = first.pagination?.totalPages ?? 1;

  for (let page = 2; page <= totalPages; page++) {
    const result = await fetchWIPReports(featureId, { page, pageSize }, filterParams);
    const rows = Array.isArray(result) ? result : result.data;
    if (!rows.length) break;
    allRows.push(...rows);
  }

  return allRows;
}

export async function fetchUtilisationData(featureId: string = 'utilisation'): Promise<UtilisationData[]> {
  const mode = getDataMode();
  
  if (mode === 'demo') {
    await new Promise(resolve => setTimeout(resolve, 300));
    return mockData.mockUtilisationData;
  } else {
    // Default date range: current month
    const now = new Date();
    const dateFrom = new Date(now.getFullYear(), now.getMonth(), 1).toISOString().split('T')[0];
    const dateTo = new Date(now.getFullYear(), now.getMonth() + 1, 0).toISOString().split('T')[0];

    const data = await apiFetch<UtilisationData[]>(
      `${getApiBaseUrl()}/utilisation?dateFrom=${dateFrom}&dateTo=${dateTo}`
    );

    return data;
  }
}

export async function fetchPortfolioSummary(
  featureId: string = 'portfolio',
  filters?: { clientId?: number; projectManagerId?: number; startDate?: string; endDate?: string }
) {
  const mode = getDataMode();
  
  if (mode === 'demo') {
    await new Promise(resolve => setTimeout(resolve, 350));
    return mockData.mockPortfolioSummary;
  } else {
    const params = new URLSearchParams();
    if (filters?.clientId) params.append('clientId', String(filters.clientId));
    if (filters?.projectManagerId) params.append('projectManagerId', String(filters.projectManagerId));
    if (filters?.startDate) params.append('startDate', filters.startDate);
    if (filters?.endDate) params.append('endDate', filters.endDate);
    const qs = params.toString();
    const url = `${getApiBaseUrl()}/projects/summary${qs ? `?${qs}` : ''}`;
    const summary = await apiFetch<any>(url);
    
    return {
      totalProjects: summary.totalProjects ?? 0,
      activeProjects: summary.activeProjects ?? 0,
      portfolioValue: summary.portfolioValue ?? 0,
      totalWIP: summary.totalWIP ?? 0,
      avgMarginPercent: summary.avgMarginPercent ?? 0,
      avgUtilisationPercent: summary.avgUtilisationPercent ?? 0,
      projectsByStatus: {
        'not-started': summary.statusNotStarted ?? 0,
        'in-progress': summary.statusInProgress ?? 0,
        'on-hold': summary.statusOnHold ?? 0,
        'completed': summary.statusCompleted ?? 0,
        'cancelled': summary.statusCancelled ?? 0,
      },
      projectsByHealth: {
        'green': summary.healthGreen ?? 0,
        'amber': summary.healthAmber ?? 0,
        'red': summary.healthRed ?? 0,
      },
    };
  }
}

export async function fetchIntegrationStatus(featureId: string = 'integrations') {
  const mode = getDataMode();
  
  if (mode === 'demo') {
    await new Promise(resolve => setTimeout(resolve, 150));
    return mockData.mockIntegrationStatus;
  } else {
    return apiFetch<any[]>(`${getApiBaseUrl()}/integrationstatus`);
  }
}

export async function fetchTasks(featureId: string = 'tasks') {
  const mode = getDataMode();
  
  if (mode === 'demo') {
    await new Promise(resolve => setTimeout(resolve, 200));
    return mockData.mockTasks;
  } else {
    return apiFetch<any[]>(`${getApiBaseUrl()}/tasks`);
  }
}

export async function fetchMilestones(featureId: string = 'milestones') {
  const mode = getDataMode();
  
  if (mode === 'demo') {
    await new Promise(resolve => setTimeout(resolve, 200));
    return mockData.mockMilestones;
  } else {
    return apiFetch<any[]>(`${getApiBaseUrl()}/milestones`);
  }
}

export async function fetchMilestonesByProjectId(projectId: number, featureId: string = 'milestones') {
  const mode = getDataMode();
  
  if (mode === 'demo') {
    await new Promise(resolve => setTimeout(resolve, 200));
    return mockData.mockMilestones.filter(m => m.projectId === projectId);
  } else {
    return apiFetch<any[]>(`${getApiBaseUrl()}/milestones/by-project/${projectId}`);
  }
}

export async function fetchTimeEntriesByProjectId(projectId: number, featureId: string = 'timeentries'): Promise<TimeEntry[]> {
  const mode = getDataMode();
  
  if (mode === 'demo') {
    await new Promise(resolve => setTimeout(resolve, 200));
    return mockData.mockTimeEntries
      .filter(te => te.projectId === projectId)
      .map(te => ({ ...te, costRate: 0, billRate: 0 }));
  } else {
    const response = await apiFetch<any>(`${getApiBaseUrl()}/timeentries/by-project/${projectId}`);

    let entries: any[] = [];
    if (Array.isArray(response)) {
      entries = response;
    } else if (response && typeof response === 'object') {
      if (Array.isArray(response.$values)) {
        entries = response.$values;
      } else if (Array.isArray(response.items)) {
        entries = response.items;
      } else if (Array.isArray(response.data)) {
        entries = response.data;
      } else if (Array.isArray(response.value)) {
        entries = response.value;
      }
    }

    return entries.map((e: any, index: number) => ({
      id: e.id ?? e.Id ?? index + 1,
      projectId: e.projectId ?? e.ProjectId ?? projectId,
      employeeId: e.employeeId ?? e.EmployeeId ?? 0,
      employeeName: e.employeeName ?? e.EmployeeName,
      taskId: e.taskId ?? e.TaskId,
      date: (e.date ?? e.Date) ? String(e.date ?? e.Date).split('T')[0] : new Date().toISOString().split('T')[0],
      hours: e.hours ?? e.Hours ?? 0,
      description: e.description ?? e.Description ?? '',
      billable: !!(e.billable ?? e.Billable),
      approved: !!(e.approved ?? e.Approved),
      status: e.status ?? e.Status,
      normalizedStatus: e.normalizedStatus ?? e.NormalizedStatus,
      costRate: e.costRate ?? e.CostRate ?? 0,
      billRate: e.billRate ?? e.BillRate ?? 0,
      costCentreId: e.costCentreId ?? e.CostCentreId,
      roleId: e.roleId ?? e.RoleId,
      workTypeId: e.workTypeId ?? e.WorkTypeId,
    }));
  }
}

export async function fetchExpensesByProjectId(
  projectId: number,
  projectCode?: string,
  company?: { instanceId?: string; expenseSubscriptionId?: string } | null,
) {
  const mode = getDataMode();
  
  if (mode === 'demo') {
    await new Promise(resolve => setTimeout(resolve, 200));
    return mockData.mockExpenses.filter(e => e.projectId === projectId);
  } else {
    const url = company?.instanceId
      ? `${getApiBaseUrl()}/expenses/by-project/${projectId}?companyId=${encodeURIComponent(company.instanceId)}`
      : `${getApiBaseUrl()}/expenses/by-project/${projectId}`;

    const response = await apiFetch<any>(url);

    let expenses: any[] = [];
    if (Array.isArray(response)) {
      expenses = response;
    } else if (response && typeof response === 'object') {
      if (Array.isArray(response.$values)) {
        expenses = response.$values;
      } else if (Array.isArray(response.items)) {
        expenses = response.items;
      } else if (Array.isArray(response.data)) {
        expenses = response.data;
      } else if (Array.isArray(response.value)) {
        expenses = response.value;
      }
    }

    return expenses.map((e: any, index: number) => ({
      id: e.id ?? index + 1,
      externalExpenseId: e.externalExpenseId,
      projectId: e.projectId ?? projectId,
      employeeId: e.employeeId ?? 0,
      employeeName: e.employeeName,
      date: e.date ? String(e.date).split('T')[0] : new Date().toISOString().split('T')[0],
      category: e.category ?? 'General',
      amount: e.amount ?? 0,
      description: e.description ?? '',
      billable: e.billable ?? true,
      reimbursable: true,
      approved: e.approved ?? true,
      status: e.approved ? 'Approved' : 'Pending',
      normalizedStatus: e.approved ? 'approved' as const : 'pending' as const,
      receiptUrl: e.receiptUrl,
      currency: e.currency ?? 'GBP',
      exchangeRate: e.exchangeRate,
      taxAmount: e.taxAmount,
      sourceSystem: e.sourceSystem,
    }));
  }
}

export async function fetchChangeRequestsByProjectId(projectId: number, featureId: string = 'changerequests') {
  const mode = getDataMode();
  
  if (mode === 'demo') {
    await new Promise(resolve => setTimeout(resolve, 200));
    // No mock change requests in mock-data.ts, return empty array for demo mode
    return [];
  } else {
    return apiFetch<any[]>(`${getApiBaseUrl()}/changerequests/by-project/${projectId}`);
  }
}

export async function fetchCostCentres(featureId: string = 'costcentres') {
  const mode = getDataMode();
  
  if (mode === 'demo') {
    await new Promise(resolve => setTimeout(resolve, 200));
    return [];
  } else {
    const response = await apiFetch<any>(`${getApiBaseUrl()}/costcentres`);
    
    // Handle different response formats from the API
    if (Array.isArray(response)) {
      return response;
    } else if (response && typeof response === 'object') {
      // Handle common wrapper patterns: { items: [...] }, { data: [...] }, { value: [...] }, { $values: [...] }
      if (Array.isArray(response.items)) {
        return response.items;
      } else if (Array.isArray(response.data)) {
        return response.data;
      } else if (Array.isArray(response.value)) {
        return response.value;
      } else if (Array.isArray(response.$values)) {
        // .NET ReferenceHandler.Preserve format
        return response.$values;
      }
    }
    
    console.warn('fetchCostCentres: Unexpected response format:', response);
    return [];
  }
}

export async function fetchCostCentresByProjectId(projectId: number, featureId: string = 'costcentres') {
  const mode = getDataMode();
  
  if (mode === 'demo') {
    await new Promise(resolve => setTimeout(resolve, 200));
    // Return mock cost centres for demo mode so the Cost Centre budget view has data to render
    return [
      { id: 1, code: 'CC-ENG', name: 'Engineering', type: 'Department', billable: true, costsBudget: 120000, actualCosts: 45000, committedCosts: 8000, revenueBudget: 180000, actualRevenue: 60000, hoursBudget: 800, actualHours: 290, isActive: true, status: 'In Progress' },
      { id: 2, code: 'CC-DES', name: 'Design', type: 'Department', billable: true, costsBudget: 60000, actualCosts: 22000, committedCosts: 3000, revenueBudget: 90000, actualRevenue: 30000, hoursBudget: 400, actualHours: 145, isActive: true, status: 'In Progress' },
      { id: 3, code: 'CC-QA', name: 'Quality Assurance', type: 'Department', billable: true, costsBudget: 40000, actualCosts: 18000, committedCosts: 2000, revenueBudget: 55000, actualRevenue: 22000, hoursBudget: 320, actualHours: 160, isActive: true, status: 'In Progress' },
      { id: 4, code: 'CC-PM', name: 'Project Management', type: 'Department', billable: false, costsBudget: 30000, actualCosts: 12000, committedCosts: 1500, revenueBudget: 0, actualRevenue: 0, hoursBudget: 240, actualHours: 96, isActive: true, status: 'In Progress' },
    ];
  } else {
    const response = await apiFetch<any>(`${getApiBaseUrl()}/costcentres/by-project/${projectId}`);
    
    // Handle different response formats from the API
    // The API should return a JSON array, but may return wrapped formats
    if (Array.isArray(response)) {
      return response;
    } else if (response && typeof response === 'object') {
      // Handle common wrapper patterns: { items: [...] }, { data: [...] }, { value: [...] }, { $values: [...] }
      if (Array.isArray(response.items)) {
        return response.items;
      } else if (Array.isArray(response.data)) {
        return response.data;
      } else if (Array.isArray(response.value)) {
        return response.value;
      } else if (Array.isArray(response.$values)) {
        // .NET ReferenceHandler.Preserve format
        return response.$values;
      }
    }
    
    // If we can't extract an array, log a warning and return empty array
    console.warn('fetchCostCentresByProjectId: Unexpected response format:', response);
    return [];
  }
}

export async function fetchCostCentreStructureByProjectId(projectId: number): Promise<HierarchicalCostCentre[]> {
  if (getDataMode() === 'demo') {
    return fetchCostCentresByProjectId(projectId) as Promise<HierarchicalCostCentre[]>;
  }

  const response = await apiFetch<unknown>(`${getApiBaseUrl()}/costcentres/by-project/${projectId}/structure`);
  return extractArrayResponse<HierarchicalCostCentre>(response, 'fetchCostCentreStructureByProjectId');
}

export async function saveCostCentreStructure(
  projectId: number,
  structure: HierarchicalCostCentre[],
): Promise<HierarchicalCostCentre[]> {
  if (getDataMode() === 'demo') {
    return structure;
  }

  const response = await apiFetch<unknown>(`${getApiBaseUrl()}/costcentres/by-project/${projectId}/structure`, {
    method: 'PUT',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(structure.map(item => ({
      id: item.id,
      parentId: item.parentId ?? null,
      code: item.code,
      name: item.name,
      description: item.description,
      type: item.type,
      status: item.status,
      billable: item.billable,
      isActive: item.isActive ?? true,
      isStructureNode: item.isStructureNode ?? !item.type,
      costsBudget: item.costsBudget,
      revenueBudget: item.revenueBudget,
      hoursBudget: item.hoursBudget,
      owner: item.owner,
    }))),
  });

  return extractArrayResponse<HierarchicalCostCentre>(response, 'saveCostCentreStructure');
}

function extractArrayResponse<T>(response: unknown, operation: string): T[] {
  if (Array.isArray(response)) {
    return response as T[];
  }
  if (response && typeof response === 'object') {
    const wrapper = response as Record<string, unknown>;
    for (const key of ['items', 'data', 'value', '$values']) {
      if (Array.isArray(wrapper[key])) {
        return wrapper[key] as T[];
      }
    }
  }

  console.warn(`${operation}: Unexpected response format:`, response);
  return [];
}

// Short-lived cache + in-flight dedup for global categories. These are
// reference data that rarely change within a session, yet several budget
// components fetch them on mount (e.g. the sales edit form's combobox and the
// sales budget view both load them around a save). Caching collapses those
// duplicate requests. Mutations invalidate the cache via
// invalidateGlobalCategoriesCache().
let globalCategoriesCache: { data: GlobalCategory[]; expiresAt: number } | null = null;
let globalCategoriesInflight: Promise<GlobalCategory[]> | null = null;
const GLOBAL_CATEGORIES_TTL_MS = 30_000;

export function invalidateGlobalCategoriesCache(): void {
  globalCategoriesCache = null;
  globalCategoriesInflight = null;
}

// Fetch all global categories
export async function fetchGlobalCategories(featureId: string = 'globalcategories'): Promise<GlobalCategory[]> {
  const mode = getDataMode();

  if (mode === 'demo') {
    await new Promise(resolve => setTimeout(resolve, 200));
    return mockGlobalCategories;
  }

  const now = Date.now();
  if (globalCategoriesCache && globalCategoriesCache.expiresAt > now) {
    return globalCategoriesCache.data;
  }
  if (globalCategoriesInflight) {
    return globalCategoriesInflight;
  }

  const promise = apiFetch<GlobalCategory[]>(`${getApiBaseUrl()}/globalcategories`)
    .then(data => {
      globalCategoriesCache = { data, expiresAt: Date.now() + GLOBAL_CATEGORIES_TTL_MS };
      return data;
    })
    .finally(() => {
      globalCategoriesInflight = null;
    });
  globalCategoriesInflight = promise;
  return promise;
}

// Create a new global category
export interface CreateGlobalCategoryData {
  name: string;
  code?: string;
  type: string;
  description?: string;
  isDefault: boolean;
}

export async function createGlobalCategory(data: CreateGlobalCategoryData): Promise<GlobalCategory> {
  const mode = getDataMode();
  
  if (mode === 'demo') {
    await new Promise(resolve => setTimeout(resolve, 500));
    
    const newCategory: GlobalCategory = {
      id: Date.now(),
      name: data.name,
      code: data.code,
      type: data.type as GlobalCategory['type'],
      description: data.description,
      status: 'active',
      isDefault: data.isDefault,
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
      createdBy: 'Current User',
      usageCount: 0,
    };
    
    return newCategory;
  } else {
    const payload = {
      name: data.name,
      code: data.code || null,
      type: data.type,
      description: data.description || null,
      status: 'active',
      isDefault: data.isDefault,
      createdBy: 'Current User',
      usageCount: 0,
    };
    
    const created = await apiFetch<GlobalCategory>(`${getApiBaseUrl()}/globalcategories`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(payload),
    });
    invalidateGlobalCategoriesCache();
    return created;
  }
}

// Update an existing global category
export interface UpdateGlobalCategoryData {
  id: number;
  name: string;
  code?: string;
  type: string;
  description?: string;
  status: string;
  isDefault: boolean;
  usageCount: number;
}

export async function updateGlobalCategory(id: number, data: UpdateGlobalCategoryData): Promise<void> {
  const mode = getDataMode();
  
  if (mode === 'demo') {
    await new Promise(resolve => setTimeout(resolve, 500));
    return;
  } else {
    if (id !== data.id) {
      throw new Error('Category ID mismatch.');
    }
    
    const payload = {
      id: data.id,
      name: data.name,
      code: data.code || null,
      type: data.type,
      description: data.description || null,
      status: data.status,
      isDefault: data.isDefault,
      usageCount: data.usageCount,
    };
    
    await apiFetch<void>(`${getApiBaseUrl()}/globalcategories/${id}`, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(payload),
    });
    invalidateGlobalCategoriesCache();
  }
}

// Delete a global category
export async function deleteGlobalCategory(id: number): Promise<void> {
  const mode = getDataMode();
  
  if (mode === 'demo') {
    await new Promise(resolve => setTimeout(resolve, 500));
    return;
  } else {
    await apiFetch<void>(`${getApiBaseUrl()}/globalcategories/${id}`, {
      method: 'DELETE',
    });
    invalidateGlobalCategoriesCache();
  }
}

// ── Category Project Usage ──────────────────────────────────────────────

export interface CategoryProjectUsageItem {
  id: number;
  name: string;
  code: string | null;
  type: string;
  statusId: number;
  health: string;
}

export async function fetchCategoryProjectUsage(categoryId: number): Promise<CategoryProjectUsageItem[]> {
  const mode = getDataMode();
  
  if (mode === 'demo') {
    await new Promise(resolve => setTimeout(resolve, 300));
    return [];
  } else {
    return apiFetch<CategoryProjectUsageItem[]>(`${getApiBaseUrl()}/globalcategories/${categoryId}/projects`);
  }
}

// ── Nominal Codes (Financials API) ──────────────────────────────────────

export interface NominalCodeApiItem {
  code: string;
  name: string;
  sortKey: string | null;
  isInactive: boolean;
}

/**
 * Fetch sales analysis codes (SL Analysis) from the Financials API.
 * Path: DebtorsQueries >> DebtorsListQueries >> SalesAnalysisSummaryConnection
 */
export async function fetchSalesNominalCodes(companyId: number): Promise<NominalCodeApiItem[]> {
  const mode = getDataMode();

  if (mode === 'demo') {
    await new Promise(resolve => setTimeout(resolve, 200));
    return [];
  }

  return apiFetch<NominalCodeApiItem[]>(
    `${getApiBaseUrl()}/finance/nominal-codes/sales?companyId=${companyId}`
  );
}

/**
 * Fetch purchase/cost analysis codes (PL Analysis) from the Financials API.
 * Path: CreditorsQueries >> CreditorsListQueries >> PurchaseAnalysisSummaryConnection
 */
export async function fetchCostNominalCodes(companyId: number): Promise<NominalCodeApiItem[]> {
  const mode = getDataMode();

  if (mode === 'demo') {
    await new Promise(resolve => setTimeout(resolve, 200));
    return [];
  }

  return apiFetch<NominalCodeApiItem[]>(
    `${getApiBaseUrl()}/finance/nominal-codes/cost?companyId=${companyId}`
  );
}

// ── Category Nominal Mappings (CRUD) ────────────────────────────────────

export interface CategoryNominalMappingDto {
  id: number;
  categoryId: number;
  nominalCode: string;
  nominalName: string;
  direction: string; // 'cost' | 'sales'
  createdAt: string;
  updatedAt: string;
}

export async function fetchCategoryNominalMappings(categoryId: number): Promise<CategoryNominalMappingDto[]> {
  const mode = getDataMode();

  if (mode === 'demo') {
    await new Promise(resolve => setTimeout(resolve, 100));
    return [];
  }

  return apiFetch<CategoryNominalMappingDto[]>(
    `${getApiBaseUrl()}/settings/categories/${categoryId}/nominal-mappings`
  );
}

export interface BulkReplaceMappingsPayload {
  direction: string;
  mappings: { nominalCode: string; nominalName: string }[];
}

export async function bulkReplaceCategoryNominalMappings(
  categoryId: number,
  payload: BulkReplaceMappingsPayload
): Promise<CategoryNominalMappingDto[]> {
  const mode = getDataMode();

  if (mode === 'demo') {
    await new Promise(resolve => setTimeout(resolve, 200));
    return [];
  }

  return apiFetch<CategoryNominalMappingDto[]>(
    `${getApiBaseUrl()}/settings/categories/${categoryId}/nominal-mappings`,
    {
      method: 'PUT',
      body: JSON.stringify(payload),
    }
  );
}

export async function deleteCategoryNominalMapping(categoryId: number, mappingId: number): Promise<void> {
  const mode = getDataMode();

  if (mode === 'demo') {
    await new Promise(resolve => setTimeout(resolve, 100));
    return;
  }

  await apiFetch<void>(
    `${getApiBaseUrl()}/settings/categories/${categoryId}/nominal-mappings/${mappingId}`,
    { method: 'DELETE' }
  );
}

// ── Global Cost Centres + Category Cost-Centre Mapping ──────────────────

export interface GlobalCostCentreDto {
  id: number;
  code: string;
  name: string;
  description?: string | null;
  status: string;
  /** True when this code maps to more than one distinct name across projects. */
  hasNameConflict: boolean;
  /** Number of distinct names for this code across project cost centres. */
  distinctNameCount: number;
  createdAt?: string;
  updatedAt?: string;
}

export interface CategoryCostCentreMappingDto {
  id: number;
  categoryId: number;
  costCentreCode: string;
  costCentreName: string;
  createdAt: string;
  updatedAt: string;
}

/**
 * Fetch the DISTINCT company-scoped cost-centre master list. Company scoping is
 * applied automatically by the backend from the X-Company-Id request header.
 * Each item carries hasNameConflict / distinctNameCount so the picker can show a
 * "multiple names exist for this code" hint.
 */
export async function fetchGlobalCostCentres(categoryId?: number): Promise<GlobalCostCentreDto[]> {
  const mode = getDataMode();

  if (mode === 'demo') {
    await new Promise(resolve => setTimeout(resolve, 150));
    return [
      { id: 1, code: 'CC 1', name: 'Site Labour', status: 'active', hasNameConflict: false, distinctNameCount: 1 },
      { id: 2, code: 'CC 2', name: 'Materials', status: 'active', hasNameConflict: false, distinctNameCount: 1 },
      { id: 3, code: 'CC 3', name: 'Plant Hire', status: 'active', hasNameConflict: true, distinctNameCount: 2 },
      { id: 4, code: 'CC 4', name: 'Subcontractors', status: 'active', hasNameConflict: false, distinctNameCount: 1 },
    ];
  }

  // When editing an existing category, pass its id so the backend keeps that
  // category's mapped code selectable while excluding codes used on others.
  const query = categoryId != null ? `?categoryId=${categoryId}` : '';
  return apiFetch<GlobalCostCentreDto[]>(`${getApiBaseUrl()}/globalcostcentres${query}`);
}

/** Fetch the cost-centre mappings for a category (may be empty). */
export async function fetchCategoryCostCentreMapping(categoryId: number): Promise<CategoryCostCentreMappingDto[]> {
  const mode = getDataMode();

  if (mode === 'demo') {
    await new Promise(resolve => setTimeout(resolve, 100));
    return [];
  }

  return apiFetch<CategoryCostCentreMappingDto[]>(
    `${getApiBaseUrl()}/settings/categories/${categoryId}/cost-centre-mapping`
  );
}

/**
 * Replace the full set of cost-centre mappings for a category. An empty array
 * clears all mappings. A 409 conflict (a code already assigned to another
 * category for this company) is allowed to propagate so the dialog can surface it.
 */
export async function saveCategoryCostCentreMapping(
  categoryId: number,
  costCentres: { code: string; name: string }[]
): Promise<CategoryCostCentreMappingDto[]> {
  const mode = getDataMode();

  if (mode === 'demo') {
    await new Promise(resolve => setTimeout(resolve, 150));
    return [];
  }

  return apiFetch<CategoryCostCentreMappingDto[]>(
    `${getApiBaseUrl()}/settings/categories/${categoryId}/cost-centre-mapping`,
    {
      method: 'PUT',
      body: JSON.stringify({
        costCentres: costCentres.map(cc => ({ costCentreCode: cc.code, costCentreName: cc.name })),
      }),
    }
  );
}

// Fetch all global subcategories
export async function fetchGlobalSubcategories(featureId: string = 'globalsubcategories'): Promise<GlobalSubcategory[]> {
  const mode = getDataMode();
  
  if (mode === 'demo') {
    await new Promise(resolve => setTimeout(resolve, 200));
    return mockGlobalSubcategories;
  } else {
    return apiFetch<GlobalSubcategory[]>(`${getApiBaseUrl()}/globalsubcategories`);
  }
}

// Fetch global subcategories by category ID
export async function fetchGlobalSubcategoriesByCategory(categoryId: number, featureId: string = 'globalsubcategories'): Promise<GlobalSubcategory[]> {
  const mode = getDataMode();
  
  if (mode === 'demo') {
    await new Promise(resolve => setTimeout(resolve, 200));
    return getMockGlobalSubcategoriesByCategory(categoryId);
  } else {
    return apiFetch<GlobalSubcategory[]>(`${getApiBaseUrl()}/globalsubcategories/by-category/${categoryId}`);
  }
}

// Fetch default GlobalCategories and convert them to CostCategory format for auto-populating new cost budgets
export async function fetchDefaultCategoriesAsCostCategories(): Promise<import('@/types/budget.types').CostCategory[]> {
  const globalCategories = await fetchGlobalCategories();
  return globalCategories
    .filter(gc => gc.isDefault && gc.status === 'active')
    .map(gc => ({
      id: gc.id,
      name: gc.name,
      type: gc.type,
      budget: 0,
      actual: 0,
      committed: 0,
      available: 0,
      progressPercent: 0,
      scope: 'global' as const,
      globalCategoryId: gc.id,
      isCustom: false,
    }));
}

// Fetch default GlobalCategories and convert them to SalesCategory format for auto-populating new sales budgets
export async function fetchDefaultCategoriesAsSalesCategories(): Promise<import('@/types/budget.types').SalesCategory[]> {
  const globalCategories = await fetchGlobalCategories();
  return globalCategories
    .filter(gc => gc.isDefault && gc.status === 'active')
    .map(gc => ({
      id: gc.id,
      name: gc.name,
      type: gc.type as import('@/types/budget.types').SalesCategoryType,
      budget: 0,
      actual: 0,
      remaining: 0,
      percentBilled: 0,
      scope: 'global' as const,
      globalCategoryId: gc.id,
      isCustom: false,
    }));
}

// Cost centre transaction interface
export interface CostCentreTransaction {
  id: string;
  date: string;
  description: string;
  amount: number;
  category: string;
}

// Cost centre breakdown interface
export interface CostCentreBreakdown {
  labour: number;
  expenses: number;
  materials: number;
  subcontractors: number;
}

// Fetch cost centre transactions (expenses and time entries)
export async function fetchCostCentreTransactions(
  costCentreId: number,
  projectId: number
): Promise<CostCentreTransaction[]> {
  const mode = getDataMode();
  
  if (mode === 'demo') {
    // In demo mode, return mock transactions
    await new Promise(resolve => setTimeout(resolve, 200));
    return [
      { id: '1', date: '2024-01-15', description: 'Developer salary - January', amount: 5000, category: 'Labour' },
      { id: '2', date: '2024-01-14', description: 'Software licenses', amount: 1200, category: 'Expenses' },
      { id: '3', date: '2024-01-12', description: 'Cloud hosting services', amount: 800, category: 'Infrastructure' },
      { id: '4', date: '2024-01-10', description: 'Design tools subscription', amount: 450, category: 'Expenses' },
      { id: '5', date: '2024-01-08', description: 'Contractor payment', amount: 3500, category: 'Subcontractors' },
      { id: '6', date: '2024-01-05', description: 'Office supplies', amount: 250, category: 'Materials' },
      { id: '7', date: '2024-01-03', description: 'Training materials', amount: 600, category: 'Expenses' },
      { id: '8', date: '2024-01-02', description: 'Equipment purchase', amount: 2100, category: 'Materials' },
      { id: '9', date: '2023-12-28', description: 'Consultant fees', amount: 4200, category: 'Subcontractors' },
      { id: '10', date: '2023-12-20', description: 'Team building event', amount: 850, category: 'Expenses' },
    ];
  } else {
    // In live mode, fetch cached expenses (no companyId to avoid triggering sync)
    try {
      const expenses = await apiFetch<any[]>(`${getApiBaseUrl()}/expenses/by-project/${projectId}`);
      
      // Transform expenses to transaction format and sort by date descending
      const transactions: CostCentreTransaction[] = (Array.isArray(expenses) ? expenses : [])
        .map(expense => ({
          id: expense.id,
          date: expense.date ? new Date(expense.date).toISOString().split('T')[0] : '',
          description: expense.description || expense.category || 'Expense',
          amount: expense.amount || 0,
          category: expense.category || 'Expenses',
        }))
        .sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime())
        .slice(0, 10); // Return only the 10 most recent
      
      return transactions;
    } catch (error) {
      console.warn(`[fetchCostCentreTransactions] Failed to fetch transactions for cost centre ${costCentreId}:`, error);
      return [];
    }
  }
}

// Fetch cost centre breakdown (aggregated by category)
export async function fetchCostCentreBreakdown(
  costCentreId: number,
  projectId: number,
  actualCosts: number
): Promise<CostCentreBreakdown> {
  const mode = getDataMode();
  
  if (mode === 'demo') {
    // In demo mode, return mock breakdown based on percentages
    await new Promise(resolve => setTimeout(resolve, 200));
    return {
      labour: actualCosts * 0.6,
      expenses: actualCosts * 0.15,
      materials: actualCosts * 0.15,
      subcontractors: actualCosts * 0.1,
    };
  } else {
    // In live mode, fetch expenses and time entries to calculate breakdown
    try {
      const [expenses, timeEntries] = await Promise.all([
        apiFetch<any[]>(`${getApiBaseUrl()}/expenses/by-project/${projectId}`).then(data => Array.isArray(data) ? data : []),
        apiFetch<any[]>(`${getApiBaseUrl()}/timeentries/by-project/${projectId}`).then(data => Array.isArray(data) ? data : []),
      ]);
      
      // Calculate labour costs from time entries
      const labourCosts = timeEntries.reduce((sum, entry) => {
        const cost = (entry.hours || 0) * (entry.costRate || 0);
        return sum + cost;
      }, 0);
      
      // Aggregate expenses by category
      const expensesByCategory = expenses.reduce((acc, expense) => {
        const category = (expense.category || 'Expenses').toLowerCase();
        const amount = expense.amount || 0;
        
        if (category.includes('material') || category.includes('equipment')) {
          acc.materials += amount;
        } else if (category.includes('subcontract') || category.includes('contractor') || category.includes('consultant')) {
          acc.subcontractors += amount;
        } else {
          acc.expenses += amount;
        }
        
        return acc;
      }, { materials: 0, subcontractors: 0, expenses: 0 });
      
      return {
        labour: labourCosts,
        expenses: expensesByCategory.expenses,
        materials: expensesByCategory.materials,
        subcontractors: expensesByCategory.subcontractors,
      };
    } catch (error) {
      console.warn(`[fetchCostCentreBreakdown] Failed to fetch breakdown for cost centre ${costCentreId}:`, error);
      // Return zeros if API fails
      return {
        labour: 0,
        expenses: 0,
        materials: 0,
        subcontractors: 0,
      };
    }
  }
}

// Create a new cost centre
export interface CreateCostCentreData {
  projectId: number | string;
  code: string;
  name: string;
  type?: string;
  status?: string;
  billable?: boolean;
  owner?: string;
  description?: string;
  costsBudget?: number;
  revenueBudget?: number;
  hoursBudget?: number;
}

export async function createCostCentre(data: CreateCostCentreData): Promise<any> {
  const mode = getDataMode();
  
  if (mode === 'demo') {
    // In demo mode, simulate creation but don't persist
    await new Promise(resolve => setTimeout(resolve, 500));
    
    // Return a mock created cost centre with generated ID
    const mockCostCentre = {
      id: `demo-cc-${Date.now()}`,
      projectId: data.projectId,
      code: data.code,
      name: data.name,
      type: data.type || '',
      status: data.status || 'Not Started',
      billable: data.billable ?? true,
      owner: data.owner || '',
      description: data.description || '',
      costsBudget: data.costsBudget || 0,
      actualCosts: 0,
      committedCosts: 0,
      revenueBudget: data.revenueBudget || 0,
      actualRevenue: 0,
      hoursBudget: data.hoursBudget || 0,
      actualHours: 0,
      lastUpdated: new Date().toISOString(),
    };
    
    return mockCostCentre;
  } else {
    // Live mode - validate required fields before calling API
    if (!data.projectId) {
      throw new Error('Project ID is required to create a cost centre.');
    }
    if (!data.code) {
      throw new Error('Cost centre code is required.');
    }
    if (!data.name) {
      throw new Error('Cost centre name is required.');
    }
    
    // Build the payload matching the backend CostCentre entity
    const costCentrePayload = {
      projectId: data.projectId,
      code: data.code,
      name: data.name,
      type: data.type || '',
      status: data.status || 'Not Started',
      billable: data.billable ?? true,
      owner: data.owner || '',
      description: data.description || '',
      costsBudget: data.costsBudget || 0,
      actualCosts: 0,
      committedCosts: 0,
      revenueBudget: data.revenueBudget || 0,
      actualRevenue: 0,
      hoursBudget: data.hoursBudget || 0,
      actualHours: 0,
      lastUpdated: new Date().toISOString(),
    };
    
    return apiFetch<any>(`${getApiBaseUrl()}/costcentres`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(costCentrePayload),
    });
  }
}

// Update cost centre data interface
export interface UpdateCostCentreData {
  id: number;
  projectId: number;
  code: string;
  name: string;
  type?: string;
  status?: string;
  billable?: boolean;
  owner?: string;
  description?: string;
  costsBudget?: number;
  actualCosts?: number;
  committedCosts?: number;
  revenueBudget?: number;
  actualRevenue?: number;
  hoursBudget?: number;
  actualHours?: number;
  isActive?: boolean;
}

// Update an existing cost centre
export async function updateCostCentre(id: number, data: UpdateCostCentreData): Promise<any> {
  const mode = getDataMode();
  
  if (mode === 'demo') {
    // In demo mode, simulate update but don't persist
    await new Promise(resolve => setTimeout(resolve, 500));
    
    // Return the updated cost centre
    return {
      ...data,
      lastUpdated: new Date().toISOString(),
    };
  } else {
    // Live mode - validate required fields before calling API
    if (!id) {
      throw new Error('Cost centre ID is required for update.');
    }
    if (id !== data.id) {
      throw new Error('Cost centre ID mismatch.');
    }
    
    // Build the payload matching the backend CostCentre entity
    const costCentrePayload = {
      id: data.id,
      projectId: data.projectId,
      code: data.code,
      name: data.name,
      type: data.type || '',
      status: data.status || '',
      billable: data.billable ?? true,
      owner: data.owner || '',
      description: data.description || '',
      costsBudget: data.costsBudget || 0,
      actualCosts: data.actualCosts || 0,
      committedCosts: data.committedCosts || 0,
      revenueBudget: data.revenueBudget || 0,
      actualRevenue: data.actualRevenue || 0,
      hoursBudget: data.hoursBudget || 0,
      actualHours: data.actualHours || 0,
      isActive: data.isActive ?? true,
      lastUpdated: new Date().toISOString(),
    };
    
    return apiFetch<any>(`${getApiBaseUrl()}/costcentres/${id}`, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(costCentrePayload),
    });
  }
}

// Delete a cost centre
export async function deleteCostCentre(id: number): Promise<void> {
  const mode = getDataMode();
  
  if (mode === 'demo') {
    // In demo mode, simulate deletion but don't persist
    await new Promise(resolve => setTimeout(resolve, 500));
    return;
  } else {
    // Live mode - validate required fields before calling API
    if (!id) {
      throw new Error('Cost centre ID is required for deletion.');
    }
    
    await apiFetch<void>(`${getApiBaseUrl()}/costcentres/${id}`, {
      method: 'DELETE',
    });
  }
}



// Check if a project code already exists (for uniqueness validation)
export async function checkProjectCodeExists(code: string): Promise<boolean> {
  const mode = getDataMode();
  
  if (mode === 'demo' || mode === 'tour') {
    // In demo/tour mode, simulate the check against mock data
    await new Promise(resolve => setTimeout(resolve, 200));
    const projects = mode === 'demo' ? mockData.mockProjects : tourMockData.tourMockProjects;
    return projects.some(p => p.code?.toLowerCase() === code.toLowerCase());
  } else {
    // Live mode - call the API
    const response = await apiFetch<{ exists: boolean }>(`${getApiBaseUrl()}/projects/check-code-exists?code=${encodeURIComponent(code)}`);
    return response.exists;
  }
}

// Create a new project
// Note: Fields are optional here because the form validation (which respects Project Creation Settings)
// is the source of truth for which fields are required
export interface CreateProjectData {
  name: string;
  projectCode?: string;
  description?: string;
  statusId?: number;
  priority?: string;
  type?: string;
  location?: string;
  startDate?: Date;
  endDate?: Date;
  projectOwner?: string;
  projectManager?: string;
  projectManagers?: string[];
  clients?: string;
  currencyCode?: string;
  budget?: string;
  tags?: string[];
  objectives?: string;
  keyDeliverables?: string;
  billable?: boolean;
}

export async function createProject(data: CreateProjectData): Promise<any> {
  const mode = getDataMode();
  
  // Extract optional fields with defaults - validation is handled by the form based on Project Creation Settings
  const clientId = data.clients || '';
  const projectManagerId = data.projectManager || '';
  const additionalProjectManagerIds = data.projectManagers || [];
  const projectOwnerId = data.projectOwner || '';
  const tags = data.tags || [];

  const toNumericId = (value: string): number | undefined => {
    if (!value || !/^\d+$/.test(value)) return undefined;
    return Number(value);
  };
  
  // Parse and validate budget value as a string to preserve full DECIMAL(18,2) precision
  // JavaScript's Number type can only safely handle ~15-16 significant digits, but by keeping
  // the value as a string, we let the backend/database handle the full precision
  const parseBudgetString = (budget: string | undefined): string => {
    if (!budget) return '0';
    // Remove any non-numeric characters except decimal point and minus
    const cleaned = budget.replace(/[^0-9.-]+/g, '');
    if (!cleaned || cleaned === '-' || cleaned === '.') return '0';
    // Validate it's a proper number format
    const parts = cleaned.split('.');
    if (parts.length > 2) return '0'; // Invalid: multiple decimal points
    // Ensure we have at most 16 digits before decimal and 2 after (DECIMAL(18,2))
    const integerPart = parts[0].replace('-', '');
    const decimalPart = parts[1] || '';
    if (integerPart.length > 16) return '0'; // Exceeds max
    if (decimalPart.length > 2) {
      // Truncate to 2 decimal places
      return parts[0] + '.' + decimalPart.slice(0, 2);
    }
    return cleaned;
  };
  
  const budgetRevenue = parseBudgetString(data.budget);
  
  if (mode === 'demo') {
    // In demo mode, simulate creation but don't persist
    await new Promise(resolve => setTimeout(resolve, 500));
    
    // Return a mock created project with generated ID
    const mockProject = {
      id: `demo-${Date.now()}`,
      name: data.name,
      code: data.projectCode || '',
      clientIds: clientId,
      clientNames: clientId ? `Client ${clientId}` : '',
      projectManagerId: projectManagerId || 'demo-manager',
      additionalProjectManagerIds: additionalProjectManagerIds.join(','),
      projectOwnerId: projectOwnerId,
      type: data.type || 'time-and-materials',
      statusId: data.statusId || 1,
      priority: data.priority || '',
      health: 'green',
      startDate: formatDateForInput(data.startDate),
      endDate: formatDateForInput(data.endDate),
      description: data.description || '',
      location: data.location || '',
      currencyCode: data.currencyCode || '',
      tags: tags.join(','),
      objectives: data.objectives || '',
      keyDeliverables: data.keyDeliverables || '',
      billable: data.billable ?? true,
      budgetLabour: 0,
      budgetExpense: 0,
      budgetRevenue,
      actualLabour: 0,
      actualExpense: 0,
      actualRevenue: 0,
      actualSales: 0,
      actualCosts: 0,
      salesVariance: 0,
      salesVariancePercent: 0,
      costVariance: 0,
      costVariancePercent: 0,
      budgetHours: 0,
      actualHours: 0,
      hoursVariance: 0,
      hoursVariancePercent: 0,
      totalBudget: 0,
      totalActual: 0,
      variance: 0,
      variancePercent: 0,
      marginPercent: 0,
      completionPercent: 0,
      revenueRecognitionMethod: 'percentage-of-completion',
      wipAmount: 0,
      unbilledAmount: 0,
    };
    
    addDemoCreatedProject(mockProject);
    return mockProject;
  } else {
    // Live mode - call the API
    // Use conditional spreads to omit optional fields when not provided
    // This prevents sending empty strings for DateTime fields which the API can't parse
    const projectPayload = {
      name: data.name,
      ...(data.projectCode ? { code: data.projectCode } : {}),
      ...(clientId ? { clientIds: clientId } : {}),
      // ProjectManagerId/ProjectOwnerId are long? on the API entity, which rejects
      // string ids, so employee ids are sent as numbers.
      ...(toNumericId(projectManagerId) != null ? { projectManagerId: toNumericId(projectManagerId) } : {}),
      ...(additionalProjectManagerIds.length > 0 ? { additionalProjectManagerIds: additionalProjectManagerIds.join(',') } : {}),
      ...(toNumericId(projectOwnerId) != null ? { projectOwnerId: toNumericId(projectOwnerId) } : {}),
      type: data.type || 'time-and-materials',
      ...(data.statusId ? { statusId: data.statusId } : {}),
      ...(data.priority ? { priority: data.priority } : {}),
      health: 'green',
      ...(data.startDate ? { startDate: formatDateForInput(data.startDate) } : {}),
      ...(data.endDate ? { endDate: formatDateForInput(data.endDate) } : {}),
      ...(data.description ? { description: data.description } : {}),
      ...(data.location ? { location: data.location } : {}),
      ...(data.currencyCode ? { currencyCode: data.currencyCode } : {}),
      ...(tags.length > 0 ? { tags: tags.join(',') } : {}),
      ...(data.objectives ? { objectives: data.objectives } : {}),
      ...(data.keyDeliverables ? { keyDeliverables: data.keyDeliverables } : {}),
      ...(data.billable !== undefined ? { billable: data.billable } : {}),
      budgetLabour: 0,
      budgetExpense: 0,
      budgetRevenue,
      actualLabour: 0,
      actualExpense: 0,
      actualRevenue: 0,
      budgetHours: 0,
      actualHours: 0,
      hoursVariance: 0,
      hoursVariancePercent: 0,
      totalBudget: 0,
      totalActual: 0,
      variance: 0,
      variancePercent: 0,
      marginPercent: 0,
      completionPercent: 0,
      revenueRecognitionMethod: 'percentage-of-completion',
      wipAmount: 0,
      unbilledAmount: 0,
    };
    
    return apiFetch<any>(`${getApiBaseUrl()}/projects`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(projectPayload),
    });
  }
}

// Helper to get project with related data
export async function fetchProjectDetails(projectId: number | string, featureId: string = 'projects') {
  const mode = getDataMode();
  
  if (mode === 'demo') {
    await new Promise(resolve => setTimeout(resolve, 300));
    const baseProject = [...getDemoCreatedProjects(), ...mockData.mockProjects]
      .find(p => String(p.id) === String(projectId)) || null;
    if (!baseProject) return null;
    const thresholds = getBudgetHealthThresholds();
    const project = {
      ...baseProject,
      health: deriveProjectHealth(
        {
          actualCost: (baseProject.actualLabour ?? 0) + (baseProject.actualExpense ?? 0),
          budgetCost: (baseProject.budgetLabour ?? 0) + (baseProject.budgetExpense ?? 0),
          startDate: baseProject.startDate,
          endDate: baseProject.endDate,
          completionPercent: baseProject.completionPercentOverride ?? baseProject.completionPercent ?? 0,
          actualHours: baseProject.actualHours ?? 0,
          budgetHours: baseProject.budgetHours ?? 0,
          wipAmount: baseProject.wipAmount ?? 0,
          actualRevenue: baseProject.actualRevenue ?? 0,
          budgetRevenue: baseProject.budgetRevenue ?? 0,
          forecastEndDate: baseProject.forecastEndDate,
        },
        thresholds
      ),
    };
    
    // Get all clients for this project (now supports multiple)
    const clientIds = project.clientIds?.split(',').filter(Boolean).map((id: string) => parseInt(id, 10)) || [];
    const projectClients = clientIds.map((id: number) => mockData.getClientById(id)).filter(Boolean);
    const manager = mockData.getEmployeeById(project.projectManagerId);
    const allocations = mockData.getAllocationsByProject(Number(projectId));
    const team = allocations.map(a => mockData.getEmployeeById(a.employeeId)).filter(Boolean);
    
    return {
      project,
      clients: projectClients,
      manager,
      allocations,
      team,
    };
  } else {
    // Fetch project details from API
    const project = await apiFetch<any>(`${getApiBaseUrl()}/projects/${projectId}`);
    if (!project) return null;
    
    // Fetch related data in parallel
    const [clients, employees, allocations] = await Promise.all([
      apiFetch<any[]>(`${getApiBaseUrl()}/clients`),
      apiFetch<any[]>(`${getApiBaseUrl()}/employees`),
      apiFetch<any[]>(`${getApiBaseUrl()}/resourceallocations/by-project/${projectId}`),
    ]);
    
    // Get all clients for this project (now supports multiple)
    const clientIds = project.clientIds?.split(',').filter(Boolean) || [];
    const projectClients = clientIds.map((id: string) => clients.find(c => c.id === id)).filter(Boolean);
    const manager = employees.find(e => e.id === project.projectManagerId);
    const team = allocations
      .map(a => employees.find(e => e.id === a.employeeId))
      .filter(Boolean);
    
    return {
      project,
      clients: projectClients,
      manager,
      allocations,
      team,
    };
  }
}

// Backend entity types for budget data
interface BackendCostBudget {
  id: string;
  projectId: number;
  totalBudget: number;
  totalActual: number;
  totalCommitted: number;
  totalAvailable: number;
  progressPercent: number;
}

interface BackendCostCategory {
  id: string;
  costBudgetId: string;
  name: string;
  type: string;
  budget: number;
  actual: number;
  committed: number;
  available: number;
  progressPercent: number;
  scope?: string;
  globalCategoryId?: string;
  isCustom: boolean;
  monthlyBudgets?: string; // JSON string: {"2024-12": 1000, "2025-01": 500, ...}
  code?: string;
}

interface BackendSalesCategory {
  id: string;
  salesBudgetId: string;
  name: string;
  type: string;
  budget: number;
  actual: number;
  committed: number;
  available: number;
  progressPercent: number;
  scope?: string;
  globalCategoryId?: string;
  isCustom: boolean;
  monthlyBudgets?: string;
  code?: string;
}

interface BackendSalesBudget {
  id: string;
  projectId: number;
  projectType: string;
  totalBudget?: number;
  totalActual?: number;
  totalCommitted?: number;
  totalAvailable?: number;
  progressPercent?: number;
  contractValue?: number;
  invoiced?: number;
  recognised?: number;
  remaining?: number;
  estimated?: number;
  actual?: number;
  nteAmount?: number;
  unbilledTime?: number;
  unbilledExpenses?: number;
  estimatedRevenue?: number;
  monthlyRetainer?: number;
  includedHours?: number;
  overageRate?: number;
}

interface BackendBillingMilestone {
  id: string;
  salesBudgetId: string;
  name: string;
  amount: number;
  dueDate: string;
  triggerPercent?: number; // 1-100, for percent-complete billing
  invoiced: boolean;
  invoiceDate?: string;
  paid: boolean;
  paidDate?: string;
  status: string;
}

interface BackendResourceBudget {
  id: string;
  projectId: number;
  totalBudget: number;
  totalActual: number;
  totalRemaining: number;
  progressPercent: number;
  billablePercent: number;
  targetBillablePercent: number;
  totalBudgetCost: number;
  totalActualCost: number;
}

interface BackendBudgetAlert {
  id: string;
  projectId: number;
  type: string;
  severity: string;
  title: string;
  description: string;
  message: string;
  details?: string;
  category?: string;
  actionLabel?: string;
  timestamp?: string;
  createdAt: string;
  dismissed: boolean;
}

interface BackendBudgetRevision {
  id: string;
  projectId: number;
  revisionNumber: number;
  date: string;
  createdAt?: string;
  createdBy?: string;
  changedBy: string;
  changedByName: string;
  reason: string;
  status: string;
  costBudget: number;
  salesBudget: number;
  resourceBudget: number;
  budgetData?: string;
  costChange?: number;
  salesChange?: number;
  resourceChange?: number;
  approvedBy?: string;
  approvedByName?: string;
  approvedDate?: string;
  totalHoursAtRevision?: number;
  hoursChange?: number;
  reasonCategory?: string;
  reasonCategoryLabel?: string;
  reasonDetail?: string;
  budgetLayer?: string;
}

interface BackendRoleBudget {
  id: string;
  resourceBudgetId: string;
  roleId: number | null;
  roleName: string;
  budgetHours: number;
  actualHours: number;
  remainingHours: number;
  percentUsed: number;
  billRate: number;
  costRate?: number;
  budgetCost: number;
  actualCost: number;
  monthlyHours?: string; // JSON string of number[] from backend
  costCentreCode?: string | null;
}

interface BackendPersonBudget {
  id: string;
  resourceBudgetId: string;
  personId: string;
  personName: string;
  roleId?: number;
  roleName?: string;
  budgetHours: number;
  actualHours: number;
  utilisation: number;
  costRate: number;
  budgetCost: number;
  actualCost: number;
}

interface BackendPhaseBudgetResource {
  id: string;
  resourceBudgetId: string;
  phaseId?: string;
  phaseName: string;
  budgetHours: number;
  actualHours: number;
  remainingHours: number;
  status: string;
  startDate?: string;
  endDate?: string;
}

interface BackendRetainerSummary {
  id: string;
  salesBudgetId: string;
  monthlyAmount: number;
  contractMonths: number;
  totalContract: number;
  invoiced: number;
  remaining: number;
  currentMonthHoursIncluded: number;
  currentMonthHoursUsed: number;
  currentMonthHoursRemaining: number;
  overageRate: number;
  rolloverPolicy: string;
  rolloverLimit?: number;
}

interface BackendRetainerMonth {
  id: string;
  retainerSummaryId: string;
  month: string;
  retainerAmount: number;
  hoursIncluded: number;
  hoursUsed: number;
  overageHours: number;
  overageAmount: number;
  totalAmount: number;
}

// Transform backend budget data to frontend ProjectBudgetData format
interface BackendForecastToComplete {
  projectedFinalRevenue: number;
  projectedFinalCost: number;
  projectedFinalMargin: number;
  revenueVariance: number;
  costVariance: number;
  marginVariance: number;
  warnings?: string[];
}

function transformBudgetData(
  projectId: number,
  costBudget: BackendCostBudget | null,
  salesBudget: BackendSalesBudget | null,
  resourceBudget: BackendResourceBudget | null,
  alerts: BackendBudgetAlert[],
  revisions: BackendBudgetRevision[],
  roleBudgets: BackendRoleBudget[] = [],
  personBudgets: BackendPersonBudget[] = [],
  phaseBudgetResources: BackendPhaseBudgetResource[] = [],
  costCategories: BackendCostCategory[] = [],
  billingMilestones: BackendBillingMilestone[] = [],
  salesCategories: BackendSalesCategory[] = [],
  retainerSummary: BackendRetainerSummary | null = null,
  retainerMonths: BackendRetainerMonth[] = [],
  forecastData: BackendForecastToComplete | null = null
): ProjectBudgetData | null {
  // If no core budget data exists, return null
  if (!costBudget && !salesBudget && !resourceBudget) {
    return null;
  }

  const projectType = (salesBudget?.projectType ?? 'time-and-materials') as import('@/types/budget.types').ProjectType;
  
  // Compute summary from available data
  // The sum of the Sales Budget category budget lines is authoritative for the
  // revenue budget whenever categories exist, so the Overview metric and Billing
  // tab stay consistent with the Sales Budget tab (which always sums the category
  // lines).  contractValue/estimated are only a fallback for projects that have
  // no category lines.  Deduplicate by name (keeping the first occurrence) before
  // summing to mirror the Sales Budget view exactly.
  const _seenSalesCategoryNames = new Set<string>();
  const salesTotalFromCategories = salesCategories.reduce((sum, cat) => {
    if (_seenSalesCategoryNames.has(cat.name)) return sum;
    _seenSalesCategoryNames.add(cat.name);
    return sum + cat.budget;
  }, 0);
  const revenueBudget = salesCategories.length > 0
    ? salesTotalFromCategories
    : (salesBudget?.totalBudget || salesBudget?.contractValue || salesBudget?.estimated || 0);
  // Prefer salesBudget.totalActual (populated by the costing sync from sales
  // transactions, mirroring costBudget.totalActual), then category sums, then
  // legacy fields.  Use `||` instead of `??` so that a stale 0 from
  // SalesBudget.TotalActual (before sp_SalesCategory_RecalculateActuals has
  // run) falls through to the category sum rather than displaying zero.
  const salesActualFromCategories = salesCategories.length > 0
    ? salesCategories.reduce((sum, cat) => sum + cat.actual, 0)
    : null;
  const revenueActual = salesBudget?.totalActual || salesActualFromCategories || salesBudget?.actual || 0;
  const costBudgetTotal = costBudget?.totalBudget ?? 0;
  const costActual = costBudget?.totalActual ?? 0;
  const hoursBudget = resourceBudget?.totalBudget ?? 0;
  const hoursActual = resourceBudget?.totalActual ?? 0;
  
  const revenueVariance = revenueActual - revenueBudget;
  const costVariance = costActual - costBudgetTotal;
  const _gpMethod = getFinanceSettings().gpCalculationMethod;
  const _budgetGpResult = calculateBudgetGP(
    { revenue: revenueBudget, totalDirectCosts: costBudgetTotal },
    _gpMethod
  );
  const _actualGpResult = calculateGP(
    { revenue: revenueActual, totalDirectCosts: costActual },
    _gpMethod
  );
  const budgetedMargin = _budgetGpResult.grossMarginPct;
  const actualMargin = _actualGpResult.grossMarginPct;
  
  // Determine health using the same configurable thresholds as the project list
  const _budgetHealthThresholds = getBudgetHealthThresholds();
  const health: import('@/types/budget.types').ProjectHealth = deriveProjectHealth(
    {
      actualCost: costActual,
      budgetCost: costBudgetTotal,
      startDate: undefined,
      endDate: undefined,
      completionPercent: 0,
      actualHours: hoursActual,
      budgetHours: hoursBudget,
      wipAmount: 0,
      actualRevenue: revenueActual,
      budgetRevenue: revenueBudget,
    },
    _budgetHealthThresholds
  );

  const summary: import('@/types/budget.types').BudgetSummary = {
    revenue: {
      budget: revenueBudget,
      actual: revenueActual,
      variance: revenueVariance,
      variancePercent: revenueBudget > 0 ? (revenueVariance / revenueBudget) * 100 : 0,
      progressPercent: revenueBudget > 0 ? (revenueActual / revenueBudget) * 100 : 0,
      trend: revenueVariance >= 0 ? 'up' : 'down',
      status: revenueVariance >= 0 ? 'green' : revenueVariance > -revenueBudget * 0.1 ? 'amber' : 'red',
    },
    costs: {
      budget: costBudgetTotal,
      actual: costActual,
      variance: costVariance,
      variancePercent: costBudgetTotal > 0 ? (costVariance / costBudgetTotal) * 100 : 0,
      progressPercent: costBudget?.progressPercent ?? 0,
      trend: costVariance <= 0 ? 'up' : 'down',
      status: costVariance <= 0 ? 'green' : costVariance < costBudgetTotal * 0.1 ? 'amber' : 'red',
      remaining: costBudget?.totalAvailable ?? 0,
      committed: costBudget?.totalCommitted ?? 0,
      available: costBudget?.totalAvailable ?? 0,
    },
    margin: {
      budgetedMargin,
      actualMargin,
      variance: actualMargin - budgetedMargin,
      status: actualMargin >= budgetedMargin ? 'green' : actualMargin >= budgetedMargin - 5 ? 'amber' : 'red',
    },
    hours: {
      budget: hoursBudget,
      actual: hoursActual,
      remaining: resourceBudget?.totalRemaining ?? 0,
      progressPercent: resourceBudget?.progressPercent ?? 0,
      status: (resourceBudget?.progressPercent ?? 0) <= 80 ? 'green' : (resourceBudget?.progressPercent ?? 0) <= 100 ? 'amber' : 'red',
    },
    health: {
      score: health,
      statusText: health === 'green' ? 'On Track' : health === 'amber' ? 'At Risk' : 'Critical',
      factors: [],
    },
  };

  // Transform cost budget with categories from backend
  const transformedCostBudget: import('@/types/budget.types').CostBudget = {
    projectId,
    categories: costCategories.map(cat => {
      // Parse monthly budgets from JSON string if present
      let monthlyBudgetsData: Record<string, number> | undefined;
      if (cat.monthlyBudgets) {
        try {
          monthlyBudgetsData = JSON.parse(cat.monthlyBudgets);
        } catch (e) {
          console.warn(`[transformBudgetData] Failed to parse monthlyBudgets for category ${cat.name}:`, e);
        }
      }
      
      return {
        id: parseInt(cat.id, 10),
        name: cat.name,
        type: cat.type as import('@/types/budget.types').CostCategoryType,
        budget: cat.budget,
        actual: cat.actual,
        committed: cat.committed,
        available: cat.available,
        progressPercent: cat.progressPercent,
        scope: cat.scope as 'global' | 'project' | undefined,
        globalCategoryId: cat.globalCategoryId ? parseInt(cat.globalCategoryId, 10) : undefined,
        isCustom: cat.isCustom,
        monthlyBudgets: monthlyBudgetsData,
      };
    }),
    phases: [], // Backend doesn't store phase breakdown yet
    totals: {
      budget: costBudget?.totalBudget ?? 0,
      actual: costBudget?.totalActual ?? 0,
      committed: costBudget?.totalCommitted ?? 0,
      available: costBudget?.totalAvailable ?? 0,
      progressPercent: costBudget?.progressPercent ?? 0,
    },
  };

  // Transform sales budget with billing milestones
  const transformedMilestones: import('@/types/budget.types').BillingMilestone[] = billingMilestones.map(m => ({
    id: typeof m.id === 'number' ? m.id : parseInt(String(m.id), 10),
    name: m.name,
    amount: m.amount,
    dueDate: m.dueDate ? m.dueDate.split('T')[0] : '',
    invoiced: m.invoiced,
    invoiceDate: m.invoiceDate,
    paid: m.paid,
    paidDate: m.paidDate,
    status: m.status as import('@/types/budget.types').BillingMilestoneStatus,
    // Default to 'date' until the backend returns triggerType — Phase 7 backend work
    triggerType: ((m as unknown as Record<string, unknown>).triggerType as import('@/types/budget.types').BillingMilestoneTriggerType) ?? 'date',
    triggerPercent: m.triggerPercent,
  }));

  const transformedSalesBudget: import('@/types/budget.types').SalesBudget = {
    projectId,
    projectType,
    contractValue: salesBudget?.contractValue,
    invoiced: salesBudget?.invoiced,
    recognised: salesBudget?.recognised,
    remaining: salesBudget?.remaining,
    estimated: salesBudget?.estimated,
    actual: salesBudget?.actual,
    nteAmount: salesBudget?.nteAmount,
    unbilledTime: salesBudget?.unbilledTime,
    unbilledExpenses: salesBudget?.unbilledExpenses,
    estimatedRevenue: salesBudget?.estimatedRevenue,
    monthlyRetainer: salesBudget?.monthlyRetainer,
    includedHours: salesBudget?.includedHours,
    overageRate: salesBudget?.overageRate,
    milestones: transformedMilestones.length > 0 ? transformedMilestones : undefined,
    // Populate revenueRecognition whenever a sales budget record exists
    // (contractValue may be 0 for a newly created budget).
    revenueRecognition: salesBudget ? {
      method: 'milestone' as const,
      percentComplete: salesBudget.contractValue ? ((salesBudget.recognised ?? 0) / salesBudget.contractValue) * 100 : 0,
      revenueToRecognise: (salesBudget.contractValue ?? 0) - (salesBudget.recognised ?? 0),
      revenueRecognised: salesBudget.recognised ?? 0,
      deferredRevenue: (salesBudget.contractValue ?? 0) - (salesBudget.recognised ?? 0),
      unbilledRevenue: (salesBudget.unbilledTime ?? 0) + (salesBudget.unbilledExpenses ?? 0),
    } : undefined,
    phases: transformedMilestones.length > 0 ? transformedMilestones.map((m) => {
      const isBilled = m.status === 'paid' || m.status === 'due';
      return {
        id: m.id,
        name: m.name,
        allocated: m.amount,
        recognised: isBilled ? m.amount : 0,
        invoiced: isBilled ? m.amount : 0,
        variance: m.amount - (isBilled ? m.amount : 0),
      };
    }) : undefined,
    categories: salesCategories.length > 0 ? (() => {
      const seenNames = new Set<string>();
      return salesCategories
        .map(cat => {
          let monthlyBudgetsData: Record<string, number> | undefined;
          if (cat.monthlyBudgets) {
            try {
              monthlyBudgetsData = JSON.parse(cat.monthlyBudgets);
            } catch (e) {
              console.warn(`[transformBudgetData] Failed to parse monthlyBudgets for sales category ${cat.name}:`, e);
            }
          }
          return {
            id: parseInt(cat.id, 10),
            name: cat.name,
            type: cat.type as import('@/types/budget.types').SalesCategoryType,
            budget: cat.budget,
            actual: cat.actual,
            remaining: cat.budget - cat.actual,
            percentBilled: cat.budget > 0 ? (cat.actual / cat.budget) * 100 : 0,
            scope: cat.scope as 'global' | 'project' | undefined,
            globalCategoryId: cat.globalCategoryId ? parseInt(cat.globalCategoryId, 10) : undefined,
            isCustom: cat.isCustom,
            monthlyBudgets: monthlyBudgetsData,
          };
        })
        .filter(cat => {
          if (seenNames.has(cat.name)) return false;
          seenNames.add(cat.name);
          return true;
        });
    })() : undefined,
    totals: (() => {
      // Single authoritative revenue budget shared with summary.revenue.budget so
      // the Sales Budget, Billing, Overview and Financials tabs cannot disagree.
      const totalBudget = revenueBudget;
      const totalActual = salesBudget?.totalActual
        ?? (salesCategories.length > 0 ? salesCategories.reduce((sum, cat) => sum + cat.actual, 0) : 0);
      if (totalBudget === 0 && totalActual === 0 && salesCategories.length === 0) return undefined;
      return {
        budget: totalBudget,
        actual: totalActual,
        remaining: totalBudget - totalActual,
        percentBilled: totalBudget > 0 ? (totalActual / totalBudget) * 100 : 0,
      };
    })(),
    retainer: retainerSummary ? {
      monthlyAmount: retainerSummary.monthlyAmount,
      contractMonths: retainerSummary.contractMonths,
      totalContract: retainerSummary.totalContract,
      contractValue: retainerSummary.totalContract,
      invoiced: retainerSummary.invoiced,
      remaining: retainerSummary.remaining,
      monthsCompleted: retainerSummary.contractMonths > 0 && retainerSummary.totalContract > 0
        ? Math.round((retainerSummary.invoiced / retainerSummary.monthlyAmount) || 0)
        : 0,
      monthsRemaining: retainerSummary.contractMonths - (
        retainerSummary.contractMonths > 0 && retainerSummary.totalContract > 0
          ? Math.round((retainerSummary.invoiced / retainerSummary.monthlyAmount) || 0)
          : 0
      ),
      currentMonth: {
        hoursIncluded: retainerSummary.currentMonthHoursIncluded,
        hoursUsed: retainerSummary.currentMonthHoursUsed,
        hoursRemaining: retainerSummary.currentMonthHoursRemaining,
        overageRate: retainerSummary.overageRate,
        progressPercent: retainerSummary.currentMonthHoursIncluded > 0
          ? (retainerSummary.currentMonthHoursUsed / retainerSummary.currentMonthHoursIncluded) * 100
          : 0,
      },
      rolloverPolicy: (retainerSummary.rolloverPolicy || 'none') as 'none' | 'limited' | 'unlimited',
      rolloverLimit: retainerSummary.rolloverLimit,
      monthlyBreakdown: retainerMonths.map(rm => ({
        month: rm.month,
        retainerAmount: rm.retainerAmount,
        hoursIncluded: rm.hoursIncluded,
        hoursUsed: rm.hoursUsed,
        overageHours: rm.overageHours,
        overageAmount: rm.overageAmount,
        totalAmount: rm.totalAmount,
      })),
    } : salesBudget?.monthlyRetainer ? {
      monthlyAmount: salesBudget.monthlyRetainer,
      contractMonths: 12,
      totalContract: salesBudget.monthlyRetainer * 12,
      contractValue: salesBudget.monthlyRetainer * 12,
      invoiced: salesBudget?.invoiced ?? 0,
      remaining: (salesBudget.monthlyRetainer * 12) - (salesBudget?.invoiced ?? 0),
      monthsCompleted: 0,
      monthsRemaining: 12,
      currentMonth: {
        hoursIncluded: salesBudget.includedHours ?? 0,
        hoursUsed: 0,
        hoursRemaining: salesBudget.includedHours ?? 0,
        overageRate: salesBudget.overageRate ?? 0,
        progressPercent: 0,
      },
      rolloverPolicy: 'none' as const,
      monthlyBreakdown: [],
    } : undefined,
    utilisation: retainerSummary ? {
      currentMonth: new Date().toLocaleString('default', { month: 'long', year: 'numeric' }),
      includedHours: retainerSummary.currentMonthHoursIncluded,
      hoursUsed: retainerSummary.currentMonthHoursUsed,
      hoursRemaining: retainerSummary.currentMonthHoursRemaining,
      overageRate: retainerSummary.overageRate,
      percentUsed: retainerSummary.currentMonthHoursIncluded > 0
        ? (retainerSummary.currentMonthHoursUsed / retainerSummary.currentMonthHoursIncluded) * 100
        : 0,
      rolloverPolicy: (retainerSummary.rolloverPolicy || 'none') as 'none' | 'limited' | 'unlimited',
      rolloverHours: retainerSummary.rolloverLimit,
    } : salesBudget?.monthlyRetainer ? {
      currentMonth: new Date().toLocaleString('default', { month: 'long', year: 'numeric' }),
      includedHours: salesBudget.includedHours ?? 0,
      hoursUsed: 0,
      hoursRemaining: salesBudget.includedHours ?? 0,
      overageRate: salesBudget.overageRate ?? 0,
      percentUsed: 0,
      rolloverPolicy: 'none' as const,
    } : undefined,
    monthlyBreakdown: retainerMonths.length > 0 ? retainerMonths.map(rm => ({
      month: rm.month,
      retainerAmount: rm.retainerAmount,
      hoursIncluded: rm.hoursIncluded,
      hoursUsed: rm.hoursUsed,
      overageAmount: rm.overageAmount,
      totalBilled: rm.totalAmount,
    })) : undefined,
  };

  // Transform resource budget with role, person, and phase breakdowns
  const transformedResourceBudget: import('@/types/budget.types').ResourceBudget = {
    id: resourceBudget?.id ? (typeof resourceBudget.id === 'number' ? resourceBudget.id : parseInt(String(resourceBudget.id), 10)) : undefined,
    projectId,
    summary: {
      totalBudget: resourceBudget?.totalBudget ?? 0,
      totalActual: resourceBudget?.totalActual ?? 0,
      totalRemaining: resourceBudget?.totalRemaining ?? 0,
      progressPercent: resourceBudget?.progressPercent ?? 0,
      billablePercent: resourceBudget?.billablePercent ?? 0,
      targetBillablePercent: resourceBudget?.targetBillablePercent ?? 85,
      totalBudgetCost: resourceBudget?.totalBudgetCost ?? 0,
      totalActualCost: resourceBudget?.totalActualCost ?? 0,
    },
    byRole: roleBudgets.map(rb => {
      // Parse monthlyHours JSON string from backend.
      // May be legacy number[] or new Record<string, number> keyed by YYYY-MM.
      // Components call normaliseMonthlyHours() with projectStartDate to convert
      // legacy arrays on the fly; here we pass through whichever format was stored.
      let monthlyHours: Record<string, number> | undefined;
      if (rb.monthlyHours) {
        try {
          const parsed = JSON.parse(rb.monthlyHours);
          monthlyHours = parsed as Record<string, number>;
        } catch {
          monthlyHours = undefined;
        }
      }
      
      return {
        id: typeof rb.id === 'number' ? rb.id : parseInt(String(rb.id), 10),
        roleId: rb.roleId ?? undefined,
        role: rb.roleName || '',
        budgetHours: rb.budgetHours,
        allocatedHours: rb.budgetHours, // Default to budget hours if not tracked separately
        actualHours: rb.actualHours,
        remainingHours: rb.remainingHours,
        percentUsed: rb.percentUsed,
        billRate: rb.billRate,
        costRate: rb.costRate ?? undefined,
        budgetCost: rb.budgetCost,
        actualCost: rb.actualCost,
        monthlyHours,
        costCentreCode: rb.costCentreCode ?? undefined,
      };
    }),
    byPerson: personBudgets.map(pb => ({
      id: typeof pb.id === 'number' ? pb.id : parseInt(String(pb.id), 10),
      personId: typeof pb.personId === 'number' ? pb.personId : parseInt(String(pb.personId), 10),
      personName: pb.personName,
      role: pb.roleName || '',
      budgetHours: pb.budgetHours,
      actualHours: pb.actualHours,
      utilisation: pb.utilisation,
      costRate: pb.costRate,
      budgetCost: pb.budgetCost,
      actualCost: pb.actualCost,
    })),
    byPhase: phaseBudgetResources.map(pbr => {
      const rawPhaseId = pbr.phaseId || pbr.id;
      return {
        phaseId: typeof rawPhaseId === 'number' ? rawPhaseId : parseInt(String(rawPhaseId), 10),
        phaseName: pbr.phaseName,
        budgetHours: pbr.budgetHours,
        actualHours: pbr.actualHours,
        remainingHours: pbr.remainingHours,
        status: pbr.status as 'not-started' | 'in-progress' | 'complete',
        startDate: pbr.startDate,
        endDate: pbr.endDate,
      };
    }),
  };

  // Transform alerts
  const transformedAlerts: import('@/types/budget.types').BudgetAlert[] = alerts.map(alert => ({
    id: typeof alert.id === 'number' ? alert.id : parseInt(String(alert.id), 10),
    projectId: typeof alert.projectId === 'number' ? alert.projectId : parseInt(String(alert.projectId), 10),
    type: alert.type as import('@/types/budget.types').BudgetAlertType,
    severity: alert.severity as import('@/types/budget.types').BudgetAlertSeverity,
    title: alert.title,
    description: alert.description,
    message: alert.message,
    details: alert.details,
    category: alert.category,
    actionLabel: alert.actionLabel,
    timestamp: alert.timestamp,
    createdAt: alert.createdAt,
    dismissed: alert.dismissed,
  }));

  // Transform revisions with computed changes field
  const transformedRevisions: import('@/types/budget.types').BudgetRevision[] = revisions.map(rev => ({
    id: typeof rev.id === 'number' ? rev.id : parseInt(String(rev.id), 10),
    projectId: typeof rev.projectId === 'number' ? rev.projectId : parseInt(String(rev.projectId), 10),
    revisionNumber: rev.revisionNumber,
    date: rev.date,
    createdAt: rev.createdAt,
    createdBy: rev.createdBy,
    changedBy: rev.changedBy,
    changedByName: rev.changedByName,
    reason: rev.reason,
    status: rev.status as import('@/types/budget.types').BudgetRevisionStatus,
    costBudget: rev.costBudget,
    salesBudget: rev.salesBudget,
    resourceBudget: rev.resourceBudget,
    costChange: rev.costChange,
    salesChange: rev.salesChange,
    resourceChange: rev.resourceChange,
    changes: {
      totalCostChange: rev.costChange ?? 0,
      totalRevenueChange: rev.salesChange ?? 0,
      categoriesChanged: [],
    },
    approvedBy: rev.approvedBy,
    approvedByName: rev.approvedByName,
    approvedDate: rev.approvedDate,
    totalHoursAtRevision: rev.totalHoursAtRevision,
    hoursChange: rev.hoursChange,
    reasonCategory: rev.reasonCategory as import('@/types/budget.types').RevisionReasonCategory | undefined,
    reasonCategoryLabel: rev.reasonCategoryLabel,
    reasonDetail: rev.reasonDetail,
    budgetLayer: rev.budgetLayer as import('@/types/budget.types').BudgetRevision['budgetLayer'],
  }));

  // Create allBudgets view (profitability)
  const allBudgets: import('@/types/budget.types').AllBudgetsView = {
    projectId,
    profitability: {
      revenue: { budget: revenueBudget, actual: revenueActual, variance: revenueVariance },
      directCosts: { budget: costBudgetTotal, actual: costActual, variance: costVariance },
      grossProfit: { 
        budget: _budgetGpResult.grossProfit, 
        actual: _actualGpResult.grossProfit, 
        variance: _actualGpResult.grossProfit - _budgetGpResult.grossProfit 
      },
      grossMargin: { budget: budgetedMargin, actual: actualMargin, variance: actualMargin - budgetedMargin },
      overhead: { budget: 0, actual: 0, variance: 0 },
      netProfit: { 
        budget: _budgetGpResult.grossProfit, 
        actual: _actualGpResult.grossProfit, 
        variance: _actualGpResult.grossProfit - _budgetGpResult.grossProfit 
      },
      netMargin: { budget: budgetedMargin, actual: actualMargin, variance: actualMargin - budgetedMargin },
    },
    burnRateData: [], // Would need historical data
    forecast: forecastData ? {
      projectedFinalRevenue: forecastData.projectedFinalRevenue,
      projectedFinalCost: forecastData.projectedFinalCost,
      projectedFinalMargin: forecastData.projectedFinalMargin,
      revenueVariance: forecastData.revenueVariance,
      costVariance: forecastData.costVariance,
      marginVariance: forecastData.marginVariance,
      warnings: forecastData.warnings,
    } : {
      projectedFinalRevenue: revenueBudget,
      projectedFinalCost: costBudgetTotal,
      projectedFinalMargin: budgetedMargin,
      revenueVariance: 0,
      costVariance: 0,
      marginVariance: 0,
    },
  };

  return {
    projectId,
    projectName: `Project ${projectId}`,
    projectType,
    summary,
    costBudget: transformedCostBudget,
    salesBudget: transformedSalesBudget,
    resourceBudget: transformedResourceBudget,
    allBudgets,
    alerts: transformedAlerts,
    revisions: transformedRevisions,
    lastUpdated: new Date().toISOString(),
  };
}

// Fetch costing sync status for a project (used by useBudgetData to detect background sync completion)
export async function getCostingSyncStatus(projectId: number | string): Promise<{ status: string; message?: string; storedCount?: number; completedAt?: string }> {
  const baseUrl = getApiBaseUrl();
  return apiFetch<{ status: string; message?: string; storedCount?: number; completedAt?: string }>(`${baseUrl}/projects/${projectId}/costing-sync-status`);
}

// Trigger a manual costing transaction sync for a project (fire-and-forget, poll costing-sync-status for progress)
export async function triggerCostingSync(
  projectCode: string,
  projectId: number,
  companyId: number,
  organisationId?: string,
  companyIdTenant?: number
): Promise<{ status: string; projectCode: string; projectId: number }> {
  const params = new URLSearchParams({
    projectCode,
    projectId: String(projectId),
    companyId: String(companyId),
  });
  if (organisationId) params.set('organisationId', organisationId);
  if (companyIdTenant) params.set('companyIdTenant', String(companyIdTenant));
  return apiFetch<{ status: string; projectCode: string; projectId: number }>(
    `${getApiBaseUrl()}/costingtransactions/sync?${params}`,
    { method: 'POST' }
  );
}

// In-flight deduplication cache for fetchBudgetData.
// Prevents duplicate API calls when multiple hooks or React StrictMode
// trigger concurrent fetches for the same project.
const inflightBudgetRequests = new Map<number | string, Promise<ProjectBudgetData | null>>();

// Lightweight fetch of cost budget + categories for a single project.
// Returns the CostBudget type consumed by the P&L report and other
// consumers that only need cost data (no sales/resource budgets).
export async function fetchCostBudgetSummary(
  projectId: number | string,
): Promise<import('@/types/budget.types').CostBudget | null> {
  const mode = getDataMode();

  if (mode === 'demo' || mode === 'tour') {
    const mockData = getMockBudgetData(projectId);
    return mockData?.costBudget ?? null;
  }

  try {
    const baseUrl = getApiBaseUrl();
    const summary = await apiFetch<{
      id: string;
      projectId: number;
      totalBudget: number;
      totalActual: number;
      totalCommitted: number;
      totalAvailable: number;
      progressPercent: number;
      categories: BackendCostCategory[];
      phaseBudgets: Array<{
        id: number;
        phaseId: number;
        phaseName: string;
        budget: number;
        actual: number;
        variance: number;
        status: string;
      }>;
    }>(`${baseUrl}/CostBudgets/by-project/${projectId}/summary`);

    return {
      projectId: Number(projectId),
      categories: (summary.categories ?? []).map((cat) => {
        let monthly: Record<string, number> | undefined;
        if (cat.monthlyBudgets) {
          try {
            monthly = JSON.parse(cat.monthlyBudgets);
          } catch {
            // ignore
          }
        }
        return {
          id: parseInt(cat.id, 10),
          name: cat.name,
          type: cat.type as import('@/types/budget.types').CostCategoryType,
          budget: cat.budget,
          actual: cat.actual,
          committed: cat.committed,
          available: cat.available,
          progressPercent: cat.progressPercent,
          scope: (cat.scope as 'global' | 'project') ?? 'global',
          globalCategoryId: cat.globalCategoryId
            ? parseInt(cat.globalCategoryId, 10)
            : undefined,
          monthlyBudgets: monthly,
        };
      }),
      phases: (summary.phaseBudgets ?? []).map((p) => ({
        id: p.id,
        phaseId: p.phaseId,
        phaseName: p.phaseName,
        budget: p.budget,
        actual: p.actual,
        variance: p.variance,
        status: p.status as 'not-started' | 'in-progress' | 'complete' | 'completed',
      })),
      totals: {
        budget: summary.totalBudget ?? 0,
        actual: summary.totalActual ?? 0,
        committed: summary.totalCommitted ?? 0,
        available: summary.totalAvailable ?? 0,
        progressPercent: summary.progressPercent ?? 0,
      },
    };
  } catch {
    return null;
  }
}

// The three budget layers that can be independently refetched.
export type BudgetLayer = 'cost' | 'sales' | 'resource';

// Cached raw backend budget responses per project. Used to satisfy
// layer-scoped refetches (e.g. after a sales-only save) so that layers which
// cannot have changed are not re-fetched from the API.
interface BudgetBackendSnapshot {
  costBudget: BackendCostBudget | null;
  costCategories: BackendCostCategory[];
  resourceBudget: BackendResourceBudget | null;
  roleBudgets: BackendRoleBudget[];
  personBudgets: BackendPersonBudget[];
  phaseBudgetResources: BackendPhaseBudgetResource[];
  salesBudget: BackendSalesBudget | null;
  billingMilestones: BackendBillingMilestone[];
  salesCategories: BackendSalesCategory[];
  retainerSummary: BackendRetainerSummary | null;
  retainerMonths: BackendRetainerMonth[];
}
const budgetBackendCache = new Map<number | string, BudgetBackendSnapshot>();

// Fetch budget data for a project - routes based on data mode.
// Pass `options.layers` to refetch only specific layers, reusing the previously
// fetched backend data for the others (see BudgetBackendSnapshot). Cross-layer
// data (alerts, revisions, forecast) is always refreshed.
export async function fetchBudgetData(
  projectId: number | string,
  options?: { layers?: BudgetLayer[] },
): Promise<ProjectBudgetData | null> {
  // Layer-scoped refetches skip the in-flight dedup cache (keyed by project
  // only) so they never return an unrelated full-fetch promise.
  if (options?.layers) {
    return fetchBudgetDataInternal(projectId, options.layers);
  }

  const existing = inflightBudgetRequests.get(projectId);
  if (existing) {
    return existing;
  }

  const promise = fetchBudgetDataInternal(projectId);
  inflightBudgetRequests.set(projectId, promise);
  promise.finally(() => {
    inflightBudgetRequests.delete(projectId);
  });
  return promise;
}

async function fetchBudgetDataInternal(
  projectId: number | string,
  layers?: BudgetLayer[],
): Promise<ProjectBudgetData | null> {
  const mode = getDataMode();
  
  if (mode === 'demo') {
    // Simulate network delay
    await new Promise(resolve => setTimeout(resolve, 300));
    let mockData = getMockBudgetData(projectId);
    if (!mockData) return null;

    // Overlay any saved cost budget categories (from edit screen save) on top of mock data.
    // This ensures refetchBudget() after saving returns the just-saved data rather than
    // the original hardcoded mock — fixing Issue 1 (Section 7b) for Cost Budget.
    const savedCostCategories = loadMockCostBudgetCategories(projectId);
    if (savedCostCategories && savedCostCategories.length > 0 && mockData.costBudget) {
      const totalBudget = savedCostCategories.reduce((s, c) => s + (c.budget ?? 0), 0);
      const totalActual = savedCostCategories.reduce((s, c) => s + (c.actual ?? 0), 0);
      const totalCommitted = savedCostCategories.reduce((s, c) => s + (c.committed ?? 0), 0);
      mockData = {
        ...mockData,
        costBudget: {
          ...mockData.costBudget,
          categories: savedCostCategories as typeof mockData.costBudget.categories,
          totals: {
            budget: totalBudget,
            actual: totalActual,
            committed: totalCommitted,
            available: totalBudget - totalActual - totalCommitted,
            progressPercent: totalBudget > 0 ? (totalActual / totalBudget) * 100 : 0,
          },
        },
      };
    }

    // Overlay any saved resource budget roles (from edit screen save) on top of mock data
    const savedRoles = loadMockResourceBudgetRoles(projectId);
    if (savedRoles && savedRoles.length > 0 && mockData.resourceBudget) {
      const totalBudget = savedRoles.reduce((sum, r) => sum + r.budgetHours, 0);
      const totalBudgetCost = savedRoles.reduce((sum, r) => sum + (r.budgetCost ?? 0), 0);
      return {
        ...mockData,
        resourceBudget: {
          ...mockData.resourceBudget,
          summary: {
            ...mockData.resourceBudget.summary,
            totalBudget,
            totalBudgetCost,
            totalRemaining: totalBudget - (mockData.resourceBudget.summary.totalActual ?? 0),
          },
          byRole: savedRoles,
        },
      };
    }

    return mockData;
  } else {
    // Live mode - call the individual budget API endpoints and compose the data
    const baseUrl = getApiBaseUrl();
    
    // A layer-scoped refetch reuses the previously-fetched backend data for the
    // layers that cannot have changed (e.g. after a sales-only save, the cost
    // and resource layers are untouched). Falls back to a full fetch when no
    // cached snapshot exists yet.
    const cached = budgetBackendCache.get(projectId);
    const fetchCost = !layers || layers.includes('cost') || !cached;
    const fetchSales = !layers || layers.includes('sales') || !cached;
    const fetchResource = !layers || layers.includes('resource') || !cached;

    try {
      // Fetch the requested layers in parallel using Promise.allSettled so we
      // still get partial data even if some endpoints fail. Alerts and revisions
      // are project-scoped and can shift after any layer change, so they are
      // always refreshed.
      const [costResult, salesResult, resourceResult, alertsResult, revisionsResult] = await Promise.allSettled([
        fetchCost
          ? apiFetch<BackendCostBudget>(`${baseUrl}/CostBudgets/by-project/${projectId}`)
          : Promise.resolve(cached?.costBudget ?? null),
        fetchSales
          ? apiFetch<BackendSalesBudget>(`${baseUrl}/SalesBudgets/by-project/${projectId}`)
          : Promise.resolve(cached?.salesBudget ?? null),
        fetchResource
          ? apiFetch<BackendResourceBudget>(`${baseUrl}/ResourceBudgets/by-project/${projectId}`)
          : Promise.resolve(cached?.resourceBudget ?? null),
        apiFetch<BackendBudgetAlert[]>(`${baseUrl}/BudgetAlerts/by-project/${projectId}`),
        apiFetch<BackendBudgetRevision[]>(`${baseUrl}/BudgetRevisions/by-project/${projectId}/details`),
      ]);

      // Extract values from settled promises, using null/empty for failures
      const costBudget = costResult.status === 'fulfilled' ? costResult.value : null;
      const salesBudget = salesResult.status === 'fulfilled' ? salesResult.value : null;
      const resourceBudget = resourceResult.status === 'fulfilled' ? resourceResult.value : null;
      const alerts = alertsResult.status === 'fulfilled' ? alertsResult.value : [];
      const revisions = revisionsResult.status === 'fulfilled' ? revisionsResult.value : [];

      // Log any failures for debugging
      if (costResult.status === 'rejected') {
        console.warn(`[fetchBudgetData] Failed to fetch cost budget for ${projectId}:`, costResult.reason);
      }
      if (salesResult.status === 'rejected') {
        console.warn(`[fetchBudgetData] Failed to fetch sales budget for ${projectId}:`, salesResult.reason);
      }
      if (resourceResult.status === 'rejected') {
        console.warn(`[fetchBudgetData] Failed to fetch resource budget for ${projectId}:`, resourceResult.reason);
      }

      // All of the layer sub-resources below depend only on the IDs resolved by
      // the first group above, so they are kicked off together and awaited via a
      // single Promise.all. Each group handles its own Promise.allSettled error
      // tolerance and per-request console.warn logging, and never rejects, so the
      // groups (cost categories, sales, retainer, resource, and the non-critical
      // forecast-to-complete) all run concurrently instead of serially.

      // Cost categories (needs costBudget.id, reuse cache when cost not refetched)
      const costCategoriesPromise: Promise<BackendCostCategory[]> = (async () => {
        if (!fetchCost) {
          return cached?.costCategories ?? [];
        }
        if (!costBudget?.id) {
          return [];
        }
        const costBudgetId = costBudget.id;
        const [costCategoriesResult] = await Promise.allSettled([
          apiFetch<BackendCostCategory[]>(`${baseUrl}/CostCategories/by-cost-budget/${costBudgetId}`),
        ]);
        if (costCategoriesResult.status === 'rejected') {
          console.warn(`[fetchBudgetData] Failed to fetch cost categories for cost budget ${costBudgetId}:`, costCategoriesResult.reason);
          return [];
        }
        return costCategoriesResult.value;
      })();

      // Billing milestones + sales categories (needs salesBudget.id, reuse cache
      // when sales not refetched)
      const salesSubResourcesPromise: Promise<{
        billingMilestones: BackendBillingMilestone[];
        salesCategories: BackendSalesCategory[];
      }> = (async () => {
        if (!fetchSales) {
          return {
            billingMilestones: cached?.billingMilestones ?? [],
            salesCategories: cached?.salesCategories ?? [],
          };
        }
        if (!salesBudget?.id) {
          return { billingMilestones: [], salesCategories: [] };
        }
        const salesBudgetId = salesBudget.id;
        const [billingMilestonesResult, salesCategoriesResult] = await Promise.allSettled([
          apiFetch<BackendBillingMilestone[]>(`${baseUrl}/BillingMilestones/by-sales-budget/${salesBudgetId}`),
          apiFetch<BackendSalesCategory[]>(`${baseUrl}/SalesCategories/by-sales-budget/${salesBudgetId}`),
        ]);
        if (billingMilestonesResult.status === 'rejected') {
          console.warn(`[fetchBudgetData] Failed to fetch billing milestones for sales budget ${salesBudgetId}:`, billingMilestonesResult.reason);
        }
        if (salesCategoriesResult.status === 'rejected') {
          console.warn(`[fetchBudgetData] Failed to fetch sales categories for sales budget ${salesBudgetId}:`, salesCategoriesResult.reason);
        }
        return {
          billingMilestones: billingMilestonesResult.status === 'fulfilled' ? billingMilestonesResult.value : [],
          salesCategories: salesCategoriesResult.status === 'fulfilled' ? salesCategoriesResult.value : [],
        };
      })();

      // Retainer summary then months (months genuinely depend on the summary id,
      // so they still await it, but the whole chain runs in parallel with the
      // other layer groups; reuse cache when sales not refetched)
      const retainerPromise: Promise<{
        retainerSummary: BackendRetainerSummary | null;
        retainerMonths: BackendRetainerMonth[];
      }> = (async () => {
        if (!fetchSales) {
          return {
            retainerSummary: cached?.retainerSummary ?? null,
            retainerMonths: cached?.retainerMonths ?? [],
          };
        }
        if (!(salesBudget?.id && salesBudget.projectType === 'retainer')) {
          return { retainerSummary: null, retainerMonths: [] };
        }
        const [retainerSummaryResult] = await Promise.allSettled([
          apiFetch<BackendRetainerSummary>(`${baseUrl}/RetainerSummaries/by-sales-budget/${salesBudget.id}`),
        ]);
        if (retainerSummaryResult.status === 'rejected') {
          console.warn(`[fetchBudgetData] Failed to fetch retainer summary for sales budget ${salesBudget.id}:`, retainerSummaryResult.reason);
        }
        const retainerSummary = retainerSummaryResult.status === 'fulfilled' ? retainerSummaryResult.value : null;
        if (!retainerSummary?.id) {
          return { retainerSummary, retainerMonths: [] };
        }
        const [retainerMonthsResult] = await Promise.allSettled([
          apiFetch<BackendRetainerMonth[]>(`${baseUrl}/RetainerMonths/by-retainer-summary/${retainerSummary.id}`),
        ]);
        if (retainerMonthsResult.status === 'rejected') {
          console.warn(`[fetchBudgetData] Failed to fetch retainer months for retainer summary ${retainerSummary.id}:`, retainerMonthsResult.reason);
        }
        return {
          retainerSummary,
          retainerMonths: retainerMonthsResult.status === 'fulfilled' ? retainerMonthsResult.value : [],
        };
      })();

      // Role, person, and phase budget resources (needs resourceBudget.id, reuse
      // cache when resource not refetched)
      const resourceSubResourcesPromise: Promise<{
        roleBudgets: BackendRoleBudget[];
        personBudgets: BackendPersonBudget[];
        phaseBudgetResources: BackendPhaseBudgetResource[];
      }> = (async () => {
        if (!fetchResource) {
          return {
            roleBudgets: cached?.roleBudgets ?? [],
            personBudgets: cached?.personBudgets ?? [],
            phaseBudgetResources: cached?.phaseBudgetResources ?? [],
          };
        }
        if (!resourceBudget?.id) {
          return { roleBudgets: [], personBudgets: [], phaseBudgetResources: [] };
        }
        const resourceBudgetId = resourceBudget.id;
        const [roleBudgetsResult, personBudgetsResult, phaseBudgetResourcesResult] = await Promise.allSettled([
          apiFetch<BackendRoleBudget[]>(`${baseUrl}/RoleBudgets/by-resource-budget/${resourceBudgetId}`),
          apiFetch<BackendPersonBudget[]>(`${baseUrl}/PersonBudgets/by-resource-budget/${resourceBudgetId}`),
          apiFetch<BackendPhaseBudgetResource[]>(`${baseUrl}/PhaseBudgetResources/by-resource-budget/${resourceBudgetId}`),
        ]);
        if (roleBudgetsResult.status === 'rejected') {
          console.warn(`[fetchBudgetData] Failed to fetch role budgets for resource budget ${resourceBudgetId}:`, roleBudgetsResult.reason);
        }
        if (personBudgetsResult.status === 'rejected') {
          console.warn(`[fetchBudgetData] Failed to fetch person budgets for resource budget ${resourceBudgetId}:`, personBudgetsResult.reason);
        }
        if (phaseBudgetResourcesResult.status === 'rejected') {
          console.warn(`[fetchBudgetData] Failed to fetch phase budget resources for resource budget ${resourceBudgetId}:`, phaseBudgetResourcesResult.reason);
        }
        return {
          roleBudgets: roleBudgetsResult.status === 'fulfilled' ? roleBudgetsResult.value : [],
          personBudgets: personBudgetsResult.status === 'fulfilled' ? personBudgetsResult.value : [],
          phaseBudgetResources: phaseBudgetResourcesResult.status === 'fulfilled' ? phaseBudgetResourcesResult.value : [],
        };
      })();

      // Forecast-to-complete is not needed to render the budget/actual/remaining
      // figures, so instead of awaiting it last on the critical path it overlaps
      // with the other layer groups here.
      const forecastPromise: Promise<BackendForecastToComplete | null> = (async () => {
        try {
          return await apiFetch<BackendForecastToComplete>(`${baseUrl}/projects/${projectId}/forecast-to-complete`);
        } catch {
          console.warn(`[fetchBudgetData] Failed to fetch forecast data for project ${projectId}`);
          return null;
        }
      })();

      const [
        costCategories,
        salesSubResources,
        retainerData,
        resourceSubResources,
        forecastData,
      ] = await Promise.all([
        costCategoriesPromise,
        salesSubResourcesPromise,
        retainerPromise,
        resourceSubResourcesPromise,
        forecastPromise,
      ]);

      const { billingMilestones, salesCategories } = salesSubResources;
      const { retainerSummary, retainerMonths } = retainerData;
      const { roleBudgets, personBudgets, phaseBudgetResources } = resourceSubResources;

      // Cache the composed backend snapshot so a later layer-scoped refetch can
      // reuse the layers that did not change.
      budgetBackendCache.set(projectId, {
        costBudget,
        costCategories,
        resourceBudget,
        roleBudgets,
        personBudgets,
        phaseBudgetResources,
        salesBudget,
        billingMilestones,
        salesCategories,
        retainerSummary,
        retainerMonths,
      });

      // Transform and return the composed budget data
      return transformBudgetData(Number(projectId), costBudget, salesBudget, resourceBudget, alerts, revisions, roleBudgets, personBudgets, phaseBudgetResources, costCategories, billingMilestones, salesCategories, retainerSummary, retainerMonths, forecastData);
    } catch (error) {
      console.warn(`[fetchBudgetData] Budget API not available for project ${projectId}:`, error);
      return null;
    }
  }
}

// Fetch the resource budget ID for a project (used as fallback when ID is not in local state)
export async function fetchResourceBudgetIdForProject(projectId: number): Promise<number | null> {
  try {
    const baseUrl = getApiBaseUrl();
    const resourceBudget = await apiFetch<BackendResourceBudget>(`${baseUrl}/ResourceBudgets/by-project/${projectId}`);
    if (resourceBudget?.id) {
      return typeof resourceBudget.id === 'number'
        ? resourceBudget.id
        : parseInt(String(resourceBudget.id), 10);
    }
    return null;
  } catch {
    return null;
  }
}

// Create a PersonBudget record (team member allocation linked to a resource budget)
export interface CreatePersonBudgetData {
  resourceBudgetId: number;
  personId: number;
  personName: string;
  roleId?: number;
  budgetHours: number;
  costRate: number;
}

export async function createPersonBudget(data: CreatePersonBudgetData): Promise<BackendPersonBudget> {
  const baseUrl = getApiBaseUrl();
  const budgetCost = data.budgetHours * data.costRate;

  const payload = {
    resourceBudgetId: data.resourceBudgetId,
    personId: data.personId,
    personName: data.personName,
    roleId: data.roleId ?? null,
    budgetHours: data.budgetHours,
    actualHours: 0,
    utilisation: 0,
    costRate: data.costRate,
    budgetCost: budgetCost,
    actualCost: 0,
  };

  const created = await apiFetch<BackendPersonBudget>(`${baseUrl}/PersonBudgets`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  });

  return created;
}

// Fetch category stats - computed from categories data
export async function fetchCategoryStats(): Promise<CategoryStats> {
  const mode = getDataMode();
  
  if (mode === 'demo') {
    await new Promise(resolve => setTimeout(resolve, 150));
    return mockCategoryStats;
  } else {
    // In live mode, fetch categories and compute stats
    const categories = await fetchGlobalCategories();
    
    const totalActive = categories.filter(c => c.status === 'active').length;
    const totalArchived = categories.filter(c => c.status === 'archived').length;
    
    // Sort by usage count and get top 5
    const mostUsedCategories = [...categories]
      .sort((a, b) => b.usageCount - a.usageCount)
      .slice(0, 5)
      .map(c => ({ id: c.id, name: c.name, usageCount: c.usageCount }));
    
    return {
      totalGlobal: categories.length,
      totalProjectSpecific: 0, // Would need project-specific categories endpoint
      totalActive,
      totalArchived,
      mostUsedCategories,
    };
  }
}

// Column preferences - stored in UserPreferences table
export interface ColumnPreference {
  id: string;
  name: string;
  label?: string;
  description: string;
  category: string;
  visible: boolean;
  locked?: boolean;
  order: number;
  align?: 'left' | 'right';
}

/**
 * Fetch column preferences for the current authenticated user.
 * Uses the /userpreferences/me endpoint which identifies the user from the JWT token.
 * No userId parameter needed - the backend extracts it from the auth token.
 * 
 * @param pageKey - Unique key identifying which page's column preferences to retrieve
 *                  (e.g., 'projects-column-preferences', 'billing-invoice-column-preferences').
 *                  If not provided, returns all column preferences as a flat array (legacy behavior).
 */
export async function fetchColumnPreferences(pageKey?: string): Promise<ColumnPreference[] | null> {
  const mode = getDataMode();
  
  if (mode === 'demo') {
    await new Promise(resolve => setTimeout(resolve, 100));
    return null;
  } else {
    try {
      const response = await apiFetch<{ columnPreferences?: string }>(`${getApiBaseUrl()}/userpreferences/me`);
      if (response?.columnPreferences) {
        const parsed = JSON.parse(response.columnPreferences);
        
        // If a pageKey is provided, extract that page's preferences from the keyed map
        if (pageKey) {
          // New format: { "pageKey": [...columns...], "pageKey2": [...columns...] }
          if (parsed && typeof parsed === 'object' && !Array.isArray(parsed)) {
            const pageColumns = parsed[pageKey];
            return Array.isArray(pageColumns) ? pageColumns as ColumnPreference[] : null;
          }
          // Legacy format: flat array - return as-is for backward compatibility
          if (Array.isArray(parsed)) {
            return parsed as ColumnPreference[];
          }
          return null;
        }
        
        // No pageKey: legacy behavior - return flat array
        if (Array.isArray(parsed)) {
          return parsed as ColumnPreference[];
        }
        return null;
      }
      return null;
    } catch {
      return null;
    }
  }
}

/**
 * Save column preferences for the current authenticated user.
 * Uses the PATCH /userpreferences/me/column-preferences endpoint which only updates
 * column preferences without overwriting other user preference fields.
 * No userId parameter needed - the backend extracts it from the auth token.
 * 
 * @param columns - The column preference array to save
 * @param pageKey - Unique key identifying which page's column preferences to save
 *                  (e.g., 'projects-column-preferences', 'billing-invoice-column-preferences').
 *                  When provided, preferences are stored in a keyed map so different pages
 *                  don't overwrite each other.
 */
export async function saveColumnPreferences(columns: ColumnPreference[], pageKey?: string): Promise<void> {
  const mode = getDataMode();
  
  if (mode === 'demo') {
    await new Promise(resolve => setTimeout(resolve, 100));
    return;
  } else {
    try {
      let columnPreferencesJson: string;
      
      if (pageKey) {
        // Keyed format: read existing preferences, merge in the new page's columns
        let allPreferences: Record<string, ColumnPreference[]> = {};
        try {
          const response = await apiFetch<{ columnPreferences?: string }>(`${getApiBaseUrl()}/userpreferences/me`);
          if (response?.columnPreferences) {
            const parsed = JSON.parse(response.columnPreferences);
            if (parsed && typeof parsed === 'object' && !Array.isArray(parsed)) {
              allPreferences = parsed;
            }
          }
        } catch {
          // If we can't read existing preferences, start fresh
        }
        allPreferences[pageKey] = columns;
        columnPreferencesJson = JSON.stringify(allPreferences);
      } else {
        // Legacy format: store flat array
        columnPreferencesJson = JSON.stringify(columns);
      }
      
      await apiFetch(`${getApiBaseUrl()}/userpreferences/me/column-preferences`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          columnPreferences: columnPreferencesJson,
        }),
      });
    } catch (error) {
      console.error('Failed to save column preferences to API:', error);
      throw error;
    }
  }
}

/**
 * Fetch project tab order for a specific project from the backend.
 * The backend stores a JSON map keyed by projectId: { "123": ["budget","billing",...] }
 */
export async function fetchProjectTabOrder(projectId: string): Promise<string[] | null> {
  const mode = getDataMode();
  if (mode === 'demo') return null;

  try {
    const response = await apiFetch<{ projectTabOrder?: string }>(`${getApiBaseUrl()}/userpreferences/me`);
    if (response?.projectTabOrder) {
      const parsed = JSON.parse(response.projectTabOrder);
      if (parsed && typeof parsed === 'object' && !Array.isArray(parsed)) {
        const tabIds = parsed[projectId];
        return Array.isArray(tabIds) ? tabIds : null;
      }
    }
    return null;
  } catch {
    return null;
  }
}

/**
 * Save project tab order for a specific project to the backend.
 * Merges into the existing keyed map so other projects' orders are preserved.
 */
export async function saveProjectTabOrder(projectId: string, tabIds: string[]): Promise<void> {
  const mode = getDataMode();
  if (mode === 'demo') return;

  try {
    let allOrders: Record<string, string[]> = {};
    try {
      const response = await apiFetch<{ projectTabOrder?: string }>(`${getApiBaseUrl()}/userpreferences/me`);
      if (response?.projectTabOrder) {
        const parsed = JSON.parse(response.projectTabOrder);
        if (parsed && typeof parsed === 'object' && !Array.isArray(parsed)) {
          allOrders = parsed;
        }
      }
    } catch { /* start fresh */ }

    allOrders[projectId] = tabIds;

    await apiFetch(`${getApiBaseUrl()}/userpreferences/me/project-tab-order`, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ projectTabOrder: JSON.stringify(allOrders) }),
    });
  } catch (error) {
    console.error('Failed to save project tab order to API:', error);
    throw error;
  }
}

// Project Creation Settings API functions
export interface ProjectCreationSettingsData {
  fieldConfigs: string | null;
  timelineSettings: string | null;
  fieldLengthConstraints: string | null;
  defaultStatusId: number | null;
}

export async function fetchProjectCreationSettings(): Promise<ProjectCreationSettingsData | null> {
  const mode = getDataMode();
  
  if (mode === 'demo') {
    // In demo mode, return null to use localStorage defaults
    return null;
  } else {
    try {
      const response = await apiFetch<{
        organisationId: string;
        fieldConfigs: string | null;
        timelineSettings: string | null;
        fieldLengthConstraints: string | null;
        defaultStatusId: number | null;
      }>(`${getApiBaseUrl()}/projectcreationsettings`);
      
      return {
        fieldConfigs: response.fieldConfigs,
        timelineSettings: response.timelineSettings,
        fieldLengthConstraints: response.fieldLengthConstraints,
        defaultStatusId: response.defaultStatusId ?? null,
      };
    } catch (error: unknown) {
      // Check if this is a 404 error - this is expected when the feature isn't deployed yet
      // In this case, silently fall back to localStorage defaults
      const appError = error as { metadata?: { apiError?: { status?: number } } };
      const statusCode = appError?.metadata?.apiError?.status;
      if (statusCode === 404) {
        // 404 is expected when the endpoint doesn't exist yet - use localStorage defaults silently
        return null;
      }
      // For other errors, log them as they may indicate real issues
      console.error('Failed to fetch project creation settings:', error);
      return null;
    }
  }
}

export async function saveProjectCreationSettings(data: ProjectCreationSettingsData): Promise<void> {
  const mode = getDataMode();
  
  if (mode === 'demo') {
    // In demo mode, don't save to API (localStorage only)
    return;
  } else {
    await apiFetch<void>(`${getApiBaseUrl()}/projectcreationsettings`, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(data),
    });
  }
}

// ============================================================================
// PROJECT CUSTOM FIELD DEFINITIONS (Settings → Custom Fields)
// ============================================================================

export interface CustomFieldDefinitionResponse {
  id: string;
  label: string;
  fieldType: string;
  required: boolean;
  enabled: boolean;
  displayOrder: number;
  options: string | null;   // JSON array string from backend
  placeholder: string | null;
  hasExistingValues: boolean;
  createdAt: string;
  updatedAt: string;
}

export async function fetchCustomFieldDefinitions(): Promise<CustomFieldDefinitionResponse[]> {
  const mode = getDataMode();

  if (mode === 'demo' || mode === 'tour') {
    // In demo/tour mode, return empty array — localStorage fallback handled by hook
    return [];
  }

  try {
    const response = await apiFetch<CustomFieldDefinitionResponse[]>(
      `${getApiBaseUrl()}/settings/project/custom-field-definitions`
    );
    return Array.isArray(response) ? response : [];
  } catch (error: unknown) {
    const appError = error as { metadata?: { apiError?: { status?: number } } };
    const statusCode = appError?.metadata?.apiError?.status;
    if (statusCode === 404) {
      return [];
    }
    console.error('Failed to fetch custom field definitions:', error);
    return [];
  }
}

export interface BulkDefinitionItem {
  id?: string;
  label: string;
  fieldType: string;
  required: boolean;
  enabled: boolean;
  options?: string[] | null;
  placeholder?: string | null;
}

export async function saveCustomFieldDefinitionsBulk(
  definitions: BulkDefinitionItem[]
): Promise<CustomFieldDefinitionResponse[]> {
  const mode = getDataMode();

  if (mode === 'demo' || mode === 'tour') {
    return [];
  }

  const response = await apiFetch<CustomFieldDefinitionResponse[]>(
    `${getApiBaseUrl()}/settings/project/custom-field-definitions/bulk`,
    {
      method: 'PUT',
      body: JSON.stringify({ definitions }),
    }
  );
  return Array.isArray(response) ? response : [];
}

export async function deleteCustomFieldDefinition(id: string): Promise<void> {
  const mode = getDataMode();

  if (mode === 'demo' || mode === 'tour') {
    return;
  }

  await apiFetch<void>(
    `${getApiBaseUrl()}/settings/project/custom-field-definitions/${id}`,
    { method: 'DELETE' }
  );
}

// ============================================================================
// PROJECT CUSTOM FIELD VALUES (per-project)
// ============================================================================

export async function fetchProjectCustomFieldValues(
  projectId: number
): Promise<Record<string, string>> {
  const mode = getDataMode();

  if (mode === 'demo' || mode === 'tour') {
    return {};
  }

  try {
    const response = await apiFetch<Record<string, string>>(
      `${getApiBaseUrl()}/projects/${projectId}/custom-fields`
    );
    return response && typeof response === 'object' ? response : {};
  } catch (error: unknown) {
    const appError = error as { metadata?: { apiError?: { status?: number } } };
    const statusCode = appError?.metadata?.apiError?.status;
    if (statusCode === 404) {
      return {};
    }
    console.error('Failed to fetch project custom field values:', error);
    return {};
  }
}

export async function saveProjectCustomFieldValues(
  projectId: number | string,
  fields: Record<string, string>
): Promise<Record<string, string>> {
  const mode = getDataMode();

  if (mode === 'demo' || mode === 'tour') {
    // Persist onto the demo-created project record so the values survive
    // navigation/reload and show on the detail page (no-op for mock projects).
    updateDemoCreatedProject(projectId, { projectCustomFields: fields });
    return fields;
  }

  const response = await apiFetch<Record<string, string>>(
    `${getApiBaseUrl()}/projects/${projectId}/custom-fields`,
    {
      method: 'PATCH',
      body: JSON.stringify(fields),
    }
  );
  return response && typeof response === 'object' ? response : {};
}

// ============================================================================
// KEY PEOPLE — ROLE CONFIGS (company-level settings)
// ============================================================================

export interface ProjectRoleConfigResponse {
  id: string;
  organisationId: string;
  companyId: string | null;
  role: string;
  enabled: boolean;
  required: boolean;
  displayLabel: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface RoleConfigItem {
  role: string;
  enabled: boolean;
  required: boolean;
  displayLabel?: string | null;
}

export async function fetchProjectRoleConfigs(): Promise<ProjectRoleConfigResponse[]> {
  const mode = getDataMode();

  if (mode === 'demo' || mode === 'tour') {
    return [];
  }

  try {
    const response = await apiFetch<ProjectRoleConfigResponse[]>(
      `${getApiBaseUrl()}/settings/project/role-configs`
    );
    return Array.isArray(response) ? response : [];
  } catch (error: unknown) {
    const appError = error as { metadata?: { apiError?: { status?: number } } };
    const statusCode = appError?.metadata?.apiError?.status;
    if (statusCode === 404) {
      return [];
    }
    console.error('Failed to fetch project role configs:', error);
    return [];
  }
}

export async function saveProjectRoleConfigs(
  configs: RoleConfigItem[]
): Promise<ProjectRoleConfigResponse[]> {
  const mode = getDataMode();

  if (mode === 'demo' || mode === 'tour') {
    return [];
  }

  const response = await apiFetch<ProjectRoleConfigResponse[]>(
    `${getApiBaseUrl()}/settings/project/role-configs`,
    {
      method: 'PUT',
      body: JSON.stringify({ configs }),
    }
  );
  return Array.isArray(response) ? response : [];
}

// ============================================================================
// KEY PEOPLE — ROLE ASSIGNMENTS (project-level)
// ============================================================================

export interface ProjectRoleAssignmentResponse {
  id: string;
  organisationId: string;
  companyId: string | null;
  projectId: number;
  role: string;
  employeeId: number;
  employeeName: string;
  employeeJobTitle: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface RoleAssignmentItem {
  role: string;
  employeeId: number;
  employeeName: string;
  employeeJobTitle?: string | null;
}

export async function fetchProjectRoleAssignments(
  projectId: number
): Promise<ProjectRoleAssignmentResponse[]> {
  const mode = getDataMode();

  if (mode === 'demo' || mode === 'tour') {
    return [];
  }

  try {
    const response = await apiFetch<ProjectRoleAssignmentResponse[]>(
      `${getApiBaseUrl()}/projects/${projectId}/role-assignments`
    );
    return Array.isArray(response) ? response : [];
  } catch (error: unknown) {
    const appError = error as { metadata?: { apiError?: { status?: number } } };
    const statusCode = appError?.metadata?.apiError?.status;
    if (statusCode === 404) {
      return [];
    }
    console.error('Failed to fetch project role assignments:', error);
    return [];
  }
}

export async function saveProjectRoleAssignments(
  projectId: number | string,
  assignments: RoleAssignmentItem[]
): Promise<ProjectRoleAssignmentResponse[]> {
  const mode = getDataMode();

  if (mode === 'demo' || mode === 'tour') {
    // Persist onto the demo-created project record so Key People survive
    // navigation/reload and show on the detail page (no-op for mock projects).
    updateDemoCreatedProject(projectId, {
      roleAssignments: assignments.map(a => ({
        id: 0,
        projectId,
        role: a.role,
        employeeId: a.employeeId,
        employeeName: a.employeeName,
        employeeJobTitle: a.employeeJobTitle ?? undefined,
      })),
    });
    return [];
  }

  const response = await apiFetch<ProjectRoleAssignmentResponse[]>(
    `${getApiBaseUrl()}/projects/${projectId}/role-assignments`,
    {
      method: 'PUT',
      body: JSON.stringify({ assignments }),
    }
  );
  return Array.isArray(response) ? response : [];
}

// ============================================================================
// NOTIFICATION SETTINGS FUNCTIONS
// ============================================================================

export interface NotificationSettingsData {
  projectAssignmentEnabled: boolean;
  projectOverBudgetEnabled: boolean;
  billingTriggerDateEnabled: boolean;
  billingTriggerDateDays: string;
  billingTriggerDateTimeUtc: string;
  fixedPriceBillingPercentEnabled: boolean;
  // New fields (FE-2 → FE-11)
  hoursOverBudgetEnabled: boolean;
  salesUnderBudgetEnabled: boolean;
  projectHealthChangedEnabled: boolean;
  projectEndDateApproachingEnabled: boolean;
  invoiceOverdueEnabled: boolean;
  scheduleOverrunEnabled: boolean;
  agedWipAlertEnabled: boolean;
  projectStatusChangedEnabled: boolean;
  projectRoleUnfilledEnabled: boolean;
  projectRoleUnfilledDelayHours: number;
}

export async function fetchNotificationSettings(): Promise<NotificationSettingsData> {
  const mode = getDataMode();
  
  if (mode === 'demo') {
    // In demo mode, return defaults from localStorage or true
    try {
      const stored = localStorage.getItem('access-pm-notification-settings');
      if (stored) {
        return JSON.parse(stored);
      }
    } catch {
      // ignore
    }
    return {
      projectAssignmentEnabled: true,
      projectOverBudgetEnabled: true,
      billingTriggerDateEnabled: true,
      billingTriggerDateDays: 'mon,tue,wed,thu,fri',
      billingTriggerDateTimeUtc: '06:00',
      fixedPriceBillingPercentEnabled: true,
      hoursOverBudgetEnabled: true,
      salesUnderBudgetEnabled: true,
      projectHealthChangedEnabled: true,
      projectEndDateApproachingEnabled: true,
      invoiceOverdueEnabled: true,
      scheduleOverrunEnabled: true,
      agedWipAlertEnabled: true,
      projectStatusChangedEnabled: true,
      projectRoleUnfilledEnabled: false,
      projectRoleUnfilledDelayHours: 0,
    };
  } else {
    try {
      const response = await apiFetch<{
        organisationId: string;
        projectAssignmentEnabled: boolean;
        projectOverBudgetEnabled: boolean;
        billingTriggerDateEnabled: boolean;
        billingTriggerDateDays: string;
        billingTriggerDateTimeUtc: string;
        fixedPriceBillingPercentEnabled: boolean;
        hoursOverBudgetEnabled: boolean;
        salesUnderBudgetEnabled: boolean;
        projectHealthChangedEnabled: boolean;
        projectEndDateApproachingEnabled: boolean;
        invoiceOverdueEnabled: boolean;
        scheduleOverrunEnabled: boolean;
        agedWipAlertEnabled: boolean;
        projectStatusChangedEnabled: boolean;
        projectRoleUnfilledEnabled: boolean;
        projectRoleUnfilledDelayHours: number;
      }>(`${getApiBaseUrl()}/notificationsettings`);
      
      return {
        projectAssignmentEnabled: response.projectAssignmentEnabled,
        projectOverBudgetEnabled: response.projectOverBudgetEnabled,
        billingTriggerDateEnabled: response.billingTriggerDateEnabled,
        billingTriggerDateDays: response.billingTriggerDateDays ?? 'mon,tue,wed,thu,fri',
        billingTriggerDateTimeUtc: response.billingTriggerDateTimeUtc ?? '06:00',
        fixedPriceBillingPercentEnabled: response.fixedPriceBillingPercentEnabled ?? true,
        hoursOverBudgetEnabled: response.hoursOverBudgetEnabled ?? true,
        salesUnderBudgetEnabled: response.salesUnderBudgetEnabled ?? true,
        projectHealthChangedEnabled: response.projectHealthChangedEnabled ?? true,
        projectEndDateApproachingEnabled: response.projectEndDateApproachingEnabled ?? true,
        invoiceOverdueEnabled: response.invoiceOverdueEnabled ?? true,
        scheduleOverrunEnabled: response.scheduleOverrunEnabled ?? true,
        agedWipAlertEnabled: response.agedWipAlertEnabled ?? true,
        projectStatusChangedEnabled: response.projectStatusChangedEnabled ?? true,
        projectRoleUnfilledEnabled: response.projectRoleUnfilledEnabled ?? false,
        projectRoleUnfilledDelayHours: response.projectRoleUnfilledDelayHours ?? 0,
      };
    } catch (error: unknown) {
      const appError = error as { metadata?: { apiError?: { status?: number } } };
      const statusCode = appError?.metadata?.apiError?.status;
      const fallback: NotificationSettingsData = {
        projectAssignmentEnabled: true,
        projectOverBudgetEnabled: true,
        billingTriggerDateEnabled: true,
        billingTriggerDateDays: 'mon,tue,wed,thu,fri',
        billingTriggerDateTimeUtc: '06:00',
        fixedPriceBillingPercentEnabled: true,
        hoursOverBudgetEnabled: true,
        salesUnderBudgetEnabled: true,
        projectHealthChangedEnabled: true,
        projectEndDateApproachingEnabled: true,
        invoiceOverdueEnabled: true,
        scheduleOverrunEnabled: true,
        agedWipAlertEnabled: true,
        projectStatusChangedEnabled: true,
        projectRoleUnfilledEnabled: false,
        projectRoleUnfilledDelayHours: 0,
      };
      if (statusCode === 404) {
        return fallback;
      }
      console.error('Failed to fetch notification settings:', error);
      return fallback;
    }
  }
}

export async function saveNotificationSettings(data: NotificationSettingsData): Promise<void> {
  const mode = getDataMode();
  
  if (mode === 'demo') {
    // In demo mode, save to localStorage only
    localStorage.setItem('access-pm-notification-settings', JSON.stringify(data));
    return;
  } else {
    await apiFetch<void>(`${getApiBaseUrl()}/notificationsettings`, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(data),
    });
  }
}

// ============================================================================
// PROJECT STATUS FUNCTIONS
// ============================================================================

export interface ProjectStatusOption {
  id: number;
  name: string;
}

// Default status labels used in demo/tour mode and as fallback
// Only includes the statuses available in the system's status picker
const DEFAULT_PROJECT_STATUSES: ProjectStatusOption[] = [
  { id: 1, name: 'Planning' },
  { id: 2, name: 'In Progress' },
  { id: 3, name: 'On Hold' },
  { id: 5, name: 'Cancelled' },
  { id: 6, name: 'Completed' },
];

export async function fetchProjectStatuses(): Promise<ProjectStatusOption[]> {
  const mode = getDataMode();

  if (mode === 'demo' || mode === 'tour') {
    await new Promise(resolve => setTimeout(resolve, 100));
    return DEFAULT_PROJECT_STATUSES;
  } else {
    try {
      const statuses = await apiFetch<ProjectStatusOption[]>(`${getApiBaseUrl()}/projectstatuses`);
      return Array.isArray(statuses) && statuses.length > 0 ? statuses : DEFAULT_PROJECT_STATUSES;
    } catch (error: unknown) {
      const appError = error as { metadata?: { apiError?: { status?: number } } };
      const statusCode = appError?.metadata?.apiError?.status;
      if (statusCode === 404) {
        return DEFAULT_PROJECT_STATUSES;
      }
      console.error('Failed to fetch project statuses:', error);
      return DEFAULT_PROJECT_STATUSES;
    }
  }
}

export async function updateProjectStatus(id: number, name: string): Promise<void> {
  const mode = getDataMode();

  if (mode === 'demo' || mode === 'tour') {
    await new Promise(resolve => setTimeout(resolve, 200));
    return;
  } else {
    await apiFetch<void>(`${getApiBaseUrl()}/projectstatuses/${id}`, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ id, name }),
    });
  }
}

// ============================================================================
// BUDGET SAVE FUNCTIONS
// ============================================================================

// Interface for saving billing milestone data
export interface SaveBillingMilestoneData {
  id?: number;
  name: string;
  amount: number;
  dueDate: string;
  triggerPercent?: number; // 1-100, for percent-complete billing
  invoiced?: boolean;
  invoiceDate?: string;
  paid?: boolean;
  paidDate?: string;
  status?: string;
}

// Interface for saving sales budget data
export interface SaveSalesCategoryData {
  id?: number;
  name: string;
  code?: string;
  type: string;
  budget: number;
  actual?: number;
  committed?: number;
  available?: number;
  progressPercent?: number;
  scope?: string;
  globalCategoryId?: number;
  isCustom?: boolean;
  monthlyBudgets?: Record<string, number>;
}

export interface SaveSalesBudgetData {
  projectId: number;
  projectType: string;
  totalBudget?: number;
  totalActual?: number;
  contractValue?: number;
  invoiced?: number;
  recognised?: number;
  remaining?: number;
  estimated?: number;
  actual?: number;
  nteAmount?: number;
  unbilledTime?: number;
  unbilledExpenses?: number;
  estimatedRevenue?: number;
  monthlyRetainer?: number;
  includedHours?: number;
  overageRate?: number;
  milestones?: SaveBillingMilestoneData[];
  categories?: SaveSalesCategoryData[];
}

// Helper function to save billing milestones for a sales budget
async function saveBillingMilestones(salesBudgetId: string, milestones: SaveBillingMilestoneData[]): Promise<void> {
  const baseUrl = getApiBaseUrl();
  
  // First, fetch existing milestones for this sales budget
  let existingMilestones: BackendBillingMilestone[] = [];
  try {
    existingMilestones = await apiFetch<BackendBillingMilestone[]>(
      `${baseUrl}/BillingMilestones/by-sales-budget/${salesBudgetId}`
    );
  } catch (error) {
    // If 404, no existing milestones - that's fine
    const appError = error as { metadata?: { apiError?: { status?: number } } };
    if (appError?.metadata?.apiError?.status !== 404) {
      console.warn('[saveBillingMilestones] Failed to fetch existing milestones:', error);
    }
  }
  
  // Track which existing milestones we've processed
  const processedIds = new Set<string>();
  
  // Save each milestone - update if exists, create if new
  for (const milestone of milestones) {
    const existing = milestone.id ? existingMilestones.find(m => String(m.id) === String(milestone.id)) : null;
    
    if (existing) {
      // Update existing milestone
      processedIds.add(String(existing.id));
      // Backend expects salesBudgetId as a number and dueDate as a valid date
      const numericSalesBudgetId = typeof salesBudgetId === 'string' ? parseInt(salesBudgetId, 10) : salesBudgetId;
      const defaultDueDate = milestone.dueDate || existing.dueDate || new Date().toISOString().split('T')[0];
      
      const updatePayload = {
        id: parseInt(existing.id, 10),
        salesBudgetId: numericSalesBudgetId,
        name: milestone.name,
        amount: milestone.amount,
        dueDate: defaultDueDate,
        triggerPercent: milestone.triggerPercent ?? existing.triggerPercent ?? null,
        invoiced: milestone.invoiced ?? existing.invoiced,
        invoiceDate: milestone.invoiceDate ?? existing.invoiceDate,
        paid: milestone.paid ?? existing.paid,
        paidDate: milestone.paidDate ?? existing.paidDate,
        status: milestone.status ?? existing.status,
      };
      
      await apiFetch<void>(`${baseUrl}/BillingMilestones/${existing.id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(updatePayload),
      });
    } else {
      // Create new milestone
      // Backend expects salesBudgetId as a number and dueDate as a valid date
      const numericSalesBudgetId = typeof salesBudgetId === 'string' ? parseInt(salesBudgetId, 10) : salesBudgetId;
      const defaultDueDate = milestone.dueDate || new Date().toISOString().split('T')[0];
      
      const createPayload = {
        salesBudgetId: numericSalesBudgetId,
        name: milestone.name,
        amount: milestone.amount,
        dueDate: defaultDueDate,
        triggerPercent: milestone.triggerPercent ?? null,
        invoiced: milestone.invoiced ?? false,
        invoiceDate: milestone.invoiceDate ?? null,
        paid: milestone.paid ?? false,
        paidDate: milestone.paidDate ?? null,
        status: milestone.status ?? 'pending',
      };
      
      console.log('[saveBillingMilestones] Creating milestone with payload:', createPayload);
      
      await apiFetch<BackendBillingMilestone>(`${baseUrl}/BillingMilestones`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(createPayload),
      });
    }
  }
  
  // Delete milestones that were removed (not in the new list)
  for (const existing of existingMilestones) {
    if (!processedIds.has(String(existing.id)) && !milestones.some(m => String(m.id) === String(existing.id))) {
      await apiFetch<void>(`${baseUrl}/BillingMilestones/${existing.id}`, {
        method: 'DELETE',
      });
    }
  }
}

async function saveSalesCategories(salesBudgetId: string, categories: SaveSalesCategoryData[]): Promise<void> {
  const baseUrl = getApiBaseUrl();

  let existingCategories: BackendSalesCategory[] = [];
  try {
    existingCategories = await apiFetch<BackendSalesCategory[]>(
      `${baseUrl}/SalesCategories/by-sales-budget/${salesBudgetId}`
    );
  } catch (error) {
    const appError = error as { metadata?: { apiError?: { status?: number } } };
    if (appError?.metadata?.apiError?.status !== 404) {
      console.warn('[saveSalesCategories] Failed to fetch existing categories:', error);
    }
  }

  const existingByName = new Map(existingCategories.map(c => [c.name, c]));
  const processedIds = new Set<string>();

  for (const category of categories) {
    const existing = category.id
      ? (existingCategories.find(c => c.id === String(category.id)) || existingByName.get(category.name))
      : existingByName.get(category.name);

    if (existing) {
      processedIds.add(String(existing.id));
      const numericSalesBudgetId = typeof salesBudgetId === 'string' ? parseInt(salesBudgetId, 10) : salesBudgetId;
      const updatePayload = {
        id: parseInt(existing.id, 10),
        salesBudgetId: numericSalesBudgetId,
        name: category.name,
        type: category.type,
        budget: category.budget,
        actual: category.actual ?? existing.actual,
        committed: category.committed ?? existing.committed,
        available: category.available ?? (category.budget - (category.actual ?? existing.actual) - (category.committed ?? existing.committed)),
        progressPercent: category.progressPercent ?? existing.progressPercent,
        scope: category.scope ?? existing.scope,
        globalCategoryId: category.globalCategoryId ?? (existing.globalCategoryId ? parseInt(existing.globalCategoryId, 10) : undefined),
        isCustom: category.isCustom ?? existing.isCustom,
        monthlyBudgets: category.monthlyBudgets ? JSON.stringify(category.monthlyBudgets) : existing.monthlyBudgets,
        code: category.code ?? existing.code ?? null,
      };

      await apiFetch<void>(`${baseUrl}/SalesCategories/${existing.id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(updatePayload),
      });
    } else {
      const createPayload = {
        salesBudgetId: parseInt(salesBudgetId, 10),
        name: category.name,
        type: category.type,
        budget: category.budget,
        actual: category.actual ?? 0,
        committed: category.committed ?? 0,
        available: category.available ?? category.budget,
        progressPercent: category.progressPercent ?? 0,
        scope: category.scope,
        globalCategoryId: category.globalCategoryId,
        isCustom: category.isCustom ?? false,
        monthlyBudgets: category.monthlyBudgets ? JSON.stringify(category.monthlyBudgets) : null,
        code: category.code ?? null,
      };

      const created = await apiFetch<BackendSalesCategory>(`${baseUrl}/SalesCategories`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(createPayload),
      });
      processedIds.add(created.id);
    }
  }

  for (const existing of existingCategories) {
    if (!processedIds.has(existing.id) && !categories.some(c => c.id === parseInt(existing.id, 10))) {
      await apiFetch<void>(`${baseUrl}/SalesCategories/${existing.id}`, {
        method: 'DELETE',
      });
    }
  }
}

// Save sales budget - creates or updates based on whether one exists for the project
export async function saveSalesBudget(data: SaveSalesBudgetData): Promise<BackendSalesBudget> {
  const mode = getDataMode();
  
  if (mode === 'demo') {
    await new Promise(resolve => setTimeout(resolve, 300));
    console.log('[saveSalesBudget] Demo mode - simulating save:', data);

    // Persist categories to localStorage so the Sales Budget tab can read them back
    if (data.categories && data.categories.length > 0) {
      try {
        localStorage.setItem(
          `mock_sales_budget_${data.projectId}`,
          JSON.stringify({ projectId: data.projectId, categories: data.categories, lastUpdated: new Date().toISOString() })
        );
      } catch {
        // non-fatal
      }
    }

    return {
      id: `demo-sales-budget-${Date.now()}`,
      ...data,
    };
  }
  
  // Compute totalBudget/totalActual from categories when available (authoritative source).
  // Milestone-billed (fixed price) budgets have no categories: their agreed value is
  // the milestone schedule, so fall back to that before writing a zero total.
  const milestoneScheduleTotal = data.milestones && data.milestones.length > 0
    ? data.milestones.reduce((sum, m) => sum + (m.amount || 0), 0)
    : null;
  const computedTotalBudget = data.categories && data.categories.length > 0
    ? data.categories.reduce((sum, cat) => sum + (cat.budget || 0), 0)
    : data.totalBudget || milestoneScheduleTotal || 0;
  const computedTotalActual = data.categories && data.categories.length > 0
    ? data.categories.reduce((sum, cat) => sum + (cat.actual || 0), 0)
    : data.totalActual ?? 0;

  // Live mode - check if a sales budget already exists for this project
  try {
    const existingBudget = await apiFetch<BackendSalesBudget>(
      `${getApiBaseUrl()}/salesbudgets/by-project/${data.projectId}`
    );
    
    // Budget exists - update it
    const updatePayload = {
      id: existingBudget.id,
      projectId: data.projectId,
      projectType: data.projectType,
      totalBudget: computedTotalBudget,
      totalActual: computedTotalActual,
      contractValue: data.contractValue ?? existingBudget.contractValue,
      invoiced: data.invoiced ?? existingBudget.invoiced,
      recognised: data.recognised ?? existingBudget.recognised,
      remaining: data.remaining ?? existingBudget.remaining,
      estimated: data.estimated ?? existingBudget.estimated,
      actual: data.actual ?? existingBudget.actual,
      nteAmount: data.nteAmount ?? existingBudget.nteAmount,
      unbilledTime: data.unbilledTime ?? existingBudget.unbilledTime,
      unbilledExpenses: data.unbilledExpenses ?? existingBudget.unbilledExpenses,
      estimatedRevenue: data.estimatedRevenue ?? existingBudget.estimatedRevenue,
      monthlyRetainer: data.monthlyRetainer ?? existingBudget.monthlyRetainer,
      includedHours: data.includedHours ?? existingBudget.includedHours,
      overageRate: data.overageRate ?? existingBudget.overageRate,
    };
    
    await apiFetch<void>(`${getApiBaseUrl()}/salesbudgets/${existingBudget.id}`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ ...updatePayload, id: parseInt(existingBudget.id, 10) }),
    });
    
    // Save milestones if provided
    if (data.milestones && data.milestones.length > 0) {
      await saveBillingMilestones(existingBudget.id, data.milestones);
    }

    // Save sales categories if provided
    if (data.categories && data.categories.length > 0) {
      await saveSalesCategories(existingBudget.id, data.categories);
    }
    
    return updatePayload;
  } catch (error) {
    // Check if it's a 404 (budget doesn't exist) - create new one
    const appError = error as { metadata?: { apiError?: { status?: number } } };
    const statusCode = appError?.metadata?.apiError?.status;
    
    if (statusCode === 404) {
      // Create new sales budget
      const createPayload = {
        projectId: data.projectId,
        projectType: data.projectType,
        totalBudget: computedTotalBudget,
        totalActual: computedTotalActual,
        contractValue: data.contractValue ?? 0,
        invoiced: data.invoiced ?? 0,
        recognised: data.recognised ?? 0,
        remaining: data.remaining ?? 0,
        estimated: data.estimated ?? 0,
        actual: data.actual ?? 0,
        nteAmount: data.nteAmount ?? 0,
        unbilledTime: data.unbilledTime ?? 0,
        unbilledExpenses: data.unbilledExpenses ?? 0,
        estimatedRevenue: data.estimatedRevenue ?? 0,
        monthlyRetainer: data.monthlyRetainer ?? 0,
        includedHours: data.includedHours ?? 0,
        overageRate: data.overageRate ?? 0,
      };
      
      const created = await apiFetch<BackendSalesBudget>(`${getApiBaseUrl()}/salesbudgets`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(createPayload),
      });
      
      // Save milestones if provided
      if (data.milestones && data.milestones.length > 0) {
        await saveBillingMilestones(created.id, data.milestones);
      }

      // Save sales categories if provided
      if (data.categories && data.categories.length > 0) {
        await saveSalesCategories(created.id, data.categories);
      }
      
      return created;
    }
    
    // Re-throw other errors
    throw error;
  }
}

// ============================================================================
// RETAINER BUDGET FUNCTIONS
// ============================================================================

export interface SaveRetainerSummaryData {
  salesBudgetId: number;
  monthlyAmount: number;
  contractMonths: number;
  totalContract: number;
  invoiced?: number;
  remaining?: number;
  currentMonthHoursIncluded?: number;
  currentMonthHoursUsed?: number;
  currentMonthHoursRemaining?: number;
  overageRate?: number;
  rolloverPolicy?: string;
  rolloverLimit?: number;
}

export interface SaveRetainerMonthData {
  id?: number;
  retainerSummaryId: number;
  month: string;
  retainerAmount: number;
  hoursIncluded: number;
  hoursUsed?: number;
  overageHours?: number;
  overageAmount?: number;
  totalAmount?: number;
}

export async function fetchRetainerSummary(salesBudgetId: number): Promise<BackendRetainerSummary | null> {
  const mode = getDataMode();
  if (mode === 'demo') return null;

  try {
    return await apiFetch<BackendRetainerSummary>(
      `${getApiBaseUrl()}/RetainerSummaries/by-sales-budget/${salesBudgetId}`
    );
  } catch {
    return null;
  }
}

export async function fetchRetainerMonths(retainerSummaryId: number): Promise<BackendRetainerMonth[]> {
  const mode = getDataMode();
  if (mode === 'demo') return [];

  try {
    return await apiFetch<BackendRetainerMonth[]>(
      `${getApiBaseUrl()}/RetainerMonths/by-retainer-summary/${retainerSummaryId}`
    );
  } catch {
    return [];
  }
}

export async function saveRetainerSummary(data: SaveRetainerSummaryData): Promise<BackendRetainerSummary> {
  const baseUrl = getApiBaseUrl();

  const payload = {
    salesBudgetId: data.salesBudgetId,
    monthlyAmount: data.monthlyAmount,
    contractMonths: data.contractMonths,
    totalContract: data.totalContract,
    invoiced: data.invoiced ?? 0,
    remaining: data.remaining ?? (data.totalContract - (data.invoiced ?? 0)),
    currentMonthHoursIncluded: data.currentMonthHoursIncluded ?? 0,
    currentMonthHoursUsed: data.currentMonthHoursUsed ?? 0,
    currentMonthHoursRemaining: data.currentMonthHoursRemaining ?? (data.currentMonthHoursIncluded ?? 0),
    overageRate: data.overageRate ?? 0,
    rolloverPolicy: data.rolloverPolicy ?? 'none',
    rolloverLimit: data.rolloverLimit,
  };

  // Check if a retainer summary already exists for this sales budget
  try {
    const existing = await apiFetch<BackendRetainerSummary>(
      `${baseUrl}/RetainerSummaries/by-sales-budget/${data.salesBudgetId}`
    );

    const updatePayload = { ...payload, id: parseInt(existing.id, 10) };
    await apiFetch<void>(`${baseUrl}/RetainerSummaries/${existing.id}`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(updatePayload),
    });

    return {
      ...existing,
      monthlyAmount: payload.monthlyAmount,
      contractMonths: payload.contractMonths,
      totalContract: payload.totalContract,
      invoiced: payload.invoiced,
      remaining: payload.remaining,
      currentMonthHoursIncluded: payload.currentMonthHoursIncluded,
      currentMonthHoursUsed: payload.currentMonthHoursUsed,
      currentMonthHoursRemaining: payload.currentMonthHoursRemaining,
      overageRate: payload.overageRate,
      rolloverPolicy: payload.rolloverPolicy,
      rolloverLimit: payload.rolloverLimit,
    };
  } catch (error) {
    const appError = error as { metadata?: { apiError?: { status?: number } } };
    if (appError?.metadata?.apiError?.status === 404) {
      const created = await apiFetch<BackendRetainerSummary>(`${baseUrl}/RetainerSummaries`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      return created;
    }
    throw error;
  }
}

export async function saveRetainerMonth(data: SaveRetainerMonthData): Promise<BackendRetainerMonth> {
  const baseUrl = getApiBaseUrl();

  const payload = {
    retainerSummaryId: data.retainerSummaryId,
    month: data.month,
    retainerAmount: data.retainerAmount,
    hoursIncluded: data.hoursIncluded,
    hoursUsed: data.hoursUsed ?? 0,
    overageHours: data.overageHours ?? 0,
    overageAmount: data.overageAmount ?? 0,
    totalAmount: data.totalAmount ?? data.retainerAmount,
  };

  if (data.id) {
    const updatePayload = { ...payload, id: data.id };
    await apiFetch<void>(`${baseUrl}/RetainerMonths/${data.id}`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(updatePayload),
    });
    return { ...updatePayload, id: String(data.id), retainerSummaryId: String(data.retainerSummaryId) };
  }

  return await apiFetch<BackendRetainerMonth>(`${baseUrl}/RetainerMonths`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  });
}

export async function deleteRetainerMonth(id: number): Promise<void> {
  await apiFetch<void>(`${getApiBaseUrl()}/RetainerMonths/${id}`, {
    method: 'DELETE',
  });
}

// Interface for saving cost category data
export interface SaveCostCategoryData {
  id?: number;
  name: string;
  code?: string;
  type: string;
  budget: number;
  actual?: number;
  committed?: number;
  available?: number;
  progressPercent?: number;
  scope?: string;
  globalCategoryId?: number;
  isCustom?: boolean;
  monthlyBudgets?: Record<string, number>; // {"2024-12": 1000, "2025-01": 500, ...}
}

// Interface for saving cost budget data
export interface SaveCostBudgetData {
  projectId: number;
  totalBudget: number;
  totalActual?: number;
  totalCommitted?: number;
  totalAvailable?: number;
  progressPercent?: number;
  categories?: SaveCostCategoryData[];
}

// Helper function to save cost categories for a cost budget
async function saveCostCategories(costBudgetId: string, categories: SaveCostCategoryData[]): Promise<void> {
  const baseUrl = getApiBaseUrl();
  
  // First, fetch existing categories for this cost budget
  let existingCategories: BackendCostCategory[] = [];
  try {
    existingCategories = await apiFetch<BackendCostCategory[]>(
      `${baseUrl}/CostCategories/by-cost-budget/${costBudgetId}`
    );
  } catch (error) {
    // If 404, no existing categories - that's fine
    const appError = error as { metadata?: { apiError?: { status?: number } } };
    if (appError?.metadata?.apiError?.status !== 404) {
      console.warn('[saveCostCategories] Failed to fetch existing categories:', error);
    }
  }
  
  // Create a map of existing categories by name for easy lookup
  const existingByName = new Map(existingCategories.map(c => [c.name, c]));
  const processedIds = new Set<string>();
  
  // Save each category - update if exists, create if new
  for (const category of categories) {
    const existing = category.id
      ? (existingCategories.find(c => c.id === String(category.id)) || existingByName.get(category.name))
      : existingByName.get(category.name);
    
    const numericCostBudgetId = typeof costBudgetId === 'string' ? parseInt(costBudgetId, 10) : costBudgetId;
    
    if (existing) {
      // Update existing category
      const actual = Number(category.actual ?? existing.actual) || 0;
      const committed = Number(category.committed ?? existing.committed) || 0;
      const budget = Number(category.budget) || 0;
      const globalCatId = category.globalCategoryId ?? existing.globalCategoryId;
      const updatePayload = {
        id: parseInt(existing.id, 10),
        costBudgetId: numericCostBudgetId,
        name: category.name,
        type: category.type,
        budget: budget,
        actual: actual,
        committed: committed,
        available: category.available != null ? Number(category.available) : (budget - actual - committed),
        progressPercent: Number(category.progressPercent ?? existing.progressPercent) || 0,
        scope: category.scope ?? existing.scope ?? null,
        globalCategoryId: globalCatId != null ? Number(globalCatId) : null,
        isCustom: category.isCustom ?? existing.isCustom ?? false,
        monthlyBudgets: category.monthlyBudgets ? JSON.stringify(category.monthlyBudgets) : (existing.monthlyBudgets ?? null),
        code: category.code ?? existing.code ?? null,
      };
      
      processedIds.add(String(existing.id));

      await apiFetch<void>(`${baseUrl}/CostCategories/${existing.id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(updatePayload),
      });
    } else {
      // Create new category
      const createPayload = {
        costBudgetId: numericCostBudgetId,
        name: category.name,
        type: category.type,
        budget: Number(category.budget) || 0,
        actual: Number(category.actual) || 0,
        committed: Number(category.committed) || 0,
        available: category.available != null ? Number(category.available) : (Number(category.budget) || 0),
        progressPercent: Number(category.progressPercent) || 0,
        scope: category.scope ?? null,
        globalCategoryId: category.globalCategoryId != null ? Number(category.globalCategoryId) : null,
        isCustom: category.isCustom ?? false,
        monthlyBudgets: category.monthlyBudgets ? JSON.stringify(category.monthlyBudgets) : null,
        code: category.code ?? null,
      };
      
      const created = await apiFetch<BackendCostCategory>(`${baseUrl}/CostCategories`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(createPayload),
      });
      processedIds.add(created.id);
    }
  }

  // Delete categories that exist in the backend but were removed from the budget
  for (const existing of existingCategories) {
    if (!processedIds.has(existing.id) && !categories.some(c => c.id === parseInt(existing.id, 10))) {
      await apiFetch<void>(`${baseUrl}/CostCategories/${existing.id}`, {
        method: 'DELETE',
      });
    }
  }
}

// Save cost budget - creates or updates based on whether one exists for the project
export async function saveCostBudget(data: SaveCostBudgetData): Promise<BackendCostBudget> {
  const mode = getDataMode();
  
  if (mode === 'demo') {
    await new Promise(resolve => setTimeout(resolve, 300));
    console.log('[saveCostBudget] Demo mode - simulating save:', data);

    // Persist categories to localStorage so the Cost Budget tab can read them back
    // after saving (same fix as saveSalesBudget — Issue 1, Section 7b).
    if (data.categories && data.categories.length > 0) {
      try {
        saveMockCostBudgetCategories(data.projectId, data.categories);
      } catch {
        // non-fatal
      }
    }

    return {
      id: `demo-cost-budget-${Date.now()}`,
      projectId: data.projectId,
      totalBudget: data.totalBudget,
      totalActual: data.totalActual ?? 0,
      totalCommitted: data.totalCommitted ?? 0,
      totalAvailable: data.totalAvailable ?? data.totalBudget,
      progressPercent: data.progressPercent ?? 0,
    };
  }
  
  // Live mode - check if a cost budget already exists for this project
  try {
    const existingBudget = await apiFetch<BackendCostBudget>(
      `${getApiBaseUrl()}/costbudgets/by-project/${data.projectId}`
    );
    
    // Budget exists - update it
    const updatePayload = {
      id: existingBudget.id,
      projectId: data.projectId,
      totalBudget: data.totalBudget,
      totalActual: data.totalActual ?? existingBudget.totalActual,
      totalCommitted: data.totalCommitted ?? existingBudget.totalCommitted,
      totalAvailable: data.totalAvailable ?? (data.totalBudget - (data.totalActual ?? existingBudget.totalActual) - (data.totalCommitted ?? existingBudget.totalCommitted)),
      progressPercent: data.progressPercent ?? existingBudget.progressPercent,
    };
    
    await apiFetch<void>(`${getApiBaseUrl()}/costbudgets/${existingBudget.id}`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ ...updatePayload, id: parseInt(existingBudget.id, 10) }),
    });
    
    // Save categories if provided
    if (data.categories && data.categories.length > 0) {
      await saveCostCategories(existingBudget.id, data.categories);
    }
    
    return updatePayload;
  } catch (error) {
    // Check if it's a 404 (budget doesn't exist) - create new one
    const appError = error as { metadata?: { apiError?: { status?: number } } };
    const statusCode = appError?.metadata?.apiError?.status;
    
    if (statusCode === 404) {
      // Create new cost budget
      const createPayload = {
        projectId: data.projectId,
        totalBudget: data.totalBudget,
        totalActual: data.totalActual ?? 0,
        totalCommitted: data.totalCommitted ?? 0,
        totalAvailable: data.totalAvailable ?? data.totalBudget,
        progressPercent: data.progressPercent ?? 0,
      };
      
      const created = await apiFetch<BackendCostBudget>(`${getApiBaseUrl()}/costbudgets`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(createPayload),
      });
      
      // Save categories if provided
      if (data.categories && data.categories.length > 0) {
        await saveCostCategories(created.id, data.categories);
      }
      
      return created;
    }
    
    // Re-throw other errors
    throw error;
  }
}

// Interface for saving role budget data
export interface SaveRoleBudgetData {
  id?: number;
  roleId?: number;
  role?: string;
  roleName?: string; // alias for role (used when constructing from revision snapshots)
  budgetHours: number;
  actualHours?: number;
  remainingHours?: number;
  percentUsed?: number;
  billRate: number;
  costRate?: number;
  budgetCost?: number;
  actualCost?: number;
  monthlyHours?: Record<string, number>;
  /** Cost Centre mode only — the cost centre code this role entry belongs to */
  costCentreCode?: string | null;
}

// Interface for saving resource budget data
export interface SaveResourceBudgetData {
  projectId: number;
  totalBudget?: number;
  totalActual?: number;
  totalRemaining?: number;
  progressPercent?: number;
  billablePercent?: number;
  targetBillablePercent?: number;
  totalBudgetCost?: number;
  totalActualCost?: number;
  byRole?: SaveRoleBudgetData[];
}

// Helper function to save role budgets for a resource budget
async function saveRoleBudgets(resourceBudgetId: string, roles: SaveRoleBudgetData[]): Promise<void> {
  const baseUrl = getApiBaseUrl();
  
  // First, fetch existing role budgets for this resource budget
  let existingRoles: BackendRoleBudget[] = [];
  try {
    existingRoles = await apiFetch<BackendRoleBudget[]>(
      `${baseUrl}/RoleBudgets/by-resource-budget/${resourceBudgetId}`
    );
  } catch (error) {
    // If 404, no existing roles - that's fine
    const appError = error as { metadata?: { apiError?: { status?: number } } };
    if (appError?.metadata?.apiError?.status !== 404) {
      console.warn('[saveRoleBudgets] Failed to fetch existing roles:', error);
    }
  }
  
  // Create a map of existing roles by role name for easy lookup
  const existingByRole = new Map(existingRoles.map(r => [r.roleId, r]));
  
  // Track which existing roles we've processed (to delete ones not in the new list)
  const processedRoleIds = new Set<string>();
  
  // Save each role - update if exists, create if new
  for (const role of roles) {
    const existing = role.id ? existingRoles.find(r => r.id === String(role.id)) : (role.roleId ? existingByRole.get(role.roleId) : undefined);
    
    // Calculate derived values
    const budgetHours = role.budgetHours;
    const actualHours = role.actualHours ?? 0;
    const remainingHours = role.remainingHours ?? (budgetHours - actualHours);
    const percentUsed = budgetHours > 0 ? (actualHours / budgetHours) * 100 : 0;
    const effectiveCostRate = role.costRate ?? role.billRate;
    const budgetCost = role.budgetCost ?? (budgetHours * effectiveCostRate);
    const actualCost = role.actualCost ?? (actualHours * effectiveCostRate);
    
    // monthlyHours is a Record<string, number> keyed by 'YYYY-MM'
    // e.g., {"2026-01": 100, "2026-02": 100}
    const monthlyHoursJson = role.monthlyHours ? JSON.stringify(role.monthlyHours) : null;

    // Persist a genuine role name only. An empty/whitespace name is stored as
    // NULL so the DB can fall back to the linked Role.Name (COALESCE) rather
    // than propagating a blank name through to role coverage / the request modal.
    const roleNameToSave = role.role?.trim() ? role.role.trim() : null;
    
    if (existing) {
      // Update existing role
      processedRoleIds.add(existing.id);
      
      const updatePayload = {
        id: existing.id,
        resourceBudgetId,
        roleId: role.roleId ?? null,
        roleName: roleNameToSave,
        budgetHours,
        actualHours,
        remainingHours,
        percentUsed,
        billRate: role.billRate,
        costRate: effectiveCostRate,
        budgetCost,
        actualCost,
        monthlyHours: monthlyHoursJson,
        costCentreCode: role.costCentreCode ?? null,
      };
      
      await apiFetch<void>(`${baseUrl}/RoleBudgets/${existing.id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(updatePayload),
      });
    } else {
      // Create new role
      const createPayload = {
        resourceBudgetId,
        roleId: role.roleId ?? null,
        roleName: roleNameToSave,
        budgetHours,
        actualHours,
        remainingHours,
        percentUsed,
        billRate: role.billRate,
        costRate: effectiveCostRate,
        budgetCost,
        actualCost,
        monthlyHours: monthlyHoursJson,
        costCentreCode: role.costCentreCode ?? null,
      };
      
      const created = await apiFetch<BackendRoleBudget>(`${baseUrl}/RoleBudgets`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(createPayload),
      });
      
      processedRoleIds.add(created.id);
    }
  }
  
  // Delete any existing roles that are no longer in the list
  for (const existingRole of existingRoles) {
    if (!processedRoleIds.has(existingRole.id)) {
      try {
        await apiFetch<void>(`${baseUrl}/RoleBudgets/${existingRole.id}`, {
          method: 'DELETE',
        });
      } catch (error) {
        console.warn(`[saveRoleBudgets] Failed to delete role ${existingRole.id}:`, error);
      }
    }
  }
}

// Save resource budget - creates or updates based on whether one exists for the project
export async function saveResourceBudget(data: SaveResourceBudgetData): Promise<BackendResourceBudget> {
  const mode = getDataMode();
  
  if (mode === 'demo') {
    await new Promise(resolve => setTimeout(resolve, 300));
    console.log('[saveResourceBudget] Demo mode - persisting to localStorage:', data);

    // Persist byRole data so refetchBudget() and the tab view return the saved state.
    // Handle an empty array too, so deleting all roles is persisted rather than ignored.
    if (data.byRole) {
      const rolesWithDerivedFields = data.byRole.map(role => ({
        id: role.id ?? -Date.now(),
        roleId: role.roleId,
        role: role.role ?? role.roleName ?? '',
        budgetHours: role.budgetHours,
        allocatedHours: role.budgetHours,
        actualHours: role.actualHours ?? 0,
        remainingHours: role.remainingHours ?? role.budgetHours,
        percentUsed: role.budgetHours > 0 ? ((role.actualHours ?? 0) / role.budgetHours) * 100 : 0,
        billRate: role.billRate,
        budgetCost: role.budgetCost ?? role.budgetHours * role.billRate,
        actualCost: role.actualCost ?? (role.actualHours ?? 0) * role.billRate,
        monthlyHours: role.monthlyHours ?? {},
        costCentreCode: role.costCentreCode ?? null,
      }));
      saveMockResourceBudgetRoles(data.projectId, rolesWithDerivedFields);
    }

    const totalBudget = data.byRole?.reduce((sum, r) => sum + r.budgetHours, 0) ?? data.totalBudget ?? 0;
    const totalBudgetCost = data.byRole?.reduce((sum, r) => sum + (r.budgetCost ?? r.budgetHours * r.billRate), 0) ?? data.totalBudgetCost ?? 0;

    return {
      id: `demo-resource-budget-${data.projectId}`,
      projectId: data.projectId,
      totalBudget,
      totalActual: data.totalActual ?? 0,
      totalRemaining: data.totalRemaining ?? totalBudget,
      progressPercent: data.progressPercent ?? 0,
      billablePercent: data.billablePercent ?? 0,
      targetBillablePercent: data.targetBillablePercent ?? 85,
      totalBudgetCost,
      totalActualCost: data.totalActualCost ?? 0,
    };
  }
  
  // Calculate totals from role budgets if provided
  let totalBudget = data.totalBudget ?? 0;
  let totalBudgetCost = data.totalBudgetCost ?? 0;
  
  if (data.byRole && data.byRole.length > 0) {
    totalBudget = data.byRole.reduce((sum, role) => sum + role.budgetHours, 0);
    totalBudgetCost = data.byRole.reduce((sum, role) => sum + (role.budgetCost ?? role.budgetHours * role.billRate), 0);
  }
  
  // Live mode - check if a resource budget already exists for this project
  try {
    const existingBudget = await apiFetch<BackendResourceBudget>(
      `${getApiBaseUrl()}/resourcebudgets/by-project/${data.projectId}`
    );
    
    // Budget exists - update it
    const updatePayload = {
      id: existingBudget.id,
      projectId: data.projectId,
      totalBudget,
      totalActual: data.totalActual ?? existingBudget.totalActual,
      totalRemaining: data.totalRemaining ?? (totalBudget - (data.totalActual ?? existingBudget.totalActual)),
      progressPercent: data.progressPercent ?? existingBudget.progressPercent,
      billablePercent: data.billablePercent ?? existingBudget.billablePercent,
      targetBillablePercent: data.targetBillablePercent ?? existingBudget.targetBillablePercent,
      totalBudgetCost,
      totalActualCost: data.totalActualCost ?? existingBudget.totalActualCost,
    };
    
    await apiFetch<void>(`${getApiBaseUrl()}/resourcebudgets/${existingBudget.id}`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(updatePayload),
    });
    
    // Save role budgets whenever byRole is provided (including an empty array so
    // the diff-and-delete logic removes any remaining rows when all roles are deleted)
    if (data.byRole) {
      await saveRoleBudgets(existingBudget.id, data.byRole);
    }
    
    return updatePayload;
  } catch (error) {
    // Check if it's a 404 (budget doesn't exist) - create new one
    const appError = error as { metadata?: { apiError?: { status?: number } } };
    const statusCode = appError?.metadata?.apiError?.status;
    
    if (statusCode === 404) {
      // Create new resource budget
      const createPayload = {
        projectId: data.projectId,
        totalBudget,
        totalActual: data.totalActual ?? 0,
        totalRemaining: data.totalRemaining ?? totalBudget,
        progressPercent: data.progressPercent ?? 0,
        billablePercent: data.billablePercent ?? 0,
        targetBillablePercent: data.targetBillablePercent ?? 85,
        totalBudgetCost,
        totalActualCost: data.totalActualCost ?? 0,
      };
      
      const created = await apiFetch<BackendResourceBudget>(`${getApiBaseUrl()}/resourcebudgets`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(createPayload),
      });
      
      // Save role budgets if provided
      if (data.byRole && data.byRole.length > 0) {
        await saveRoleBudgets(created.id, data.byRole);
      }
      
      return created;
    }
    
    // Re-throw other errors
    throw error;
  }
}

// ============================================================
// Budget Revision API Functions
// ============================================================

export interface SaveRevisionPayload {
  projectId: number;
  reasonCategory?: string;
  reasonCategoryLabel?: string;
  reasonDetail?: string;
  status: 'active' | 'draft';
  budgetLayer?: string;
  budgetTarget?: string;
  roleBudgets: {
    roleId?: number;
    roleName: string;
    budgetHours: number;
    billRate: number;
    costRate?: number;
    budgetCost: number;
    monthlyHours?: string;
    costCentreCode?: string;
  }[];
  cascadeDecision?: {
    updateCostLabour: boolean;
    updateSalesLabour: boolean;
    overriddenByUser: boolean;
    cascadeTargetCategoryId?: number;
    cascadeTargetCategoryName?: string;
  };
}

export async function saveRevision(payload: SaveRevisionPayload): Promise<unknown> {
  const mode = getDataMode();

  if (mode === 'demo') {
    await new Promise(resolve => setTimeout(resolve, 300));
    return { id: Date.now(), revisionNumber: 1, status: payload.status };
  }

  const result = await apiFetch<unknown>(`${getApiBaseUrl()}/BudgetRevisions/save`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  });

  return result;
}

export interface CreateBudgetRevisionPayload {
  projectId: number;
  status: 'draft';
  budgetLayer?: string;
  reasonCategory?: string;
  reasonCategoryLabel?: string;
  reasonDetail?: string;
  roleBudgets: {
    roleId?: number;
    roleName: string;
    budgetHours: number;
    billRate: number;
    costRate?: number;
    budgetCost: number;
    monthlyHours?: string;
  }[];
}

export async function createBudgetRevision(payload: CreateBudgetRevisionPayload): Promise<unknown> {
  const mode = getDataMode();

  if (mode === 'demo') {
    await new Promise(resolve => setTimeout(resolve, 300));
    console.log('[createBudgetRevision] Demo mode - simulating create:', payload);
    return { id: Date.now(), revisionNumber: 1, status: 'draft' };
  }

  const result = await apiFetch<unknown>(`${getApiBaseUrl()}/BudgetRevisions/save`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ ...payload, status: 'draft' }),
  });

  console.log('[createBudgetRevision] Draft revision created successfully:', result);
  return result;
}

export interface ActivateBudgetRevisionPayload {
  cascadeDecision?: {
    updateCostLabour: boolean;
    updateSalesLabour: boolean;
    overriddenByUser: boolean;
    cascadeTargetCategoryId?: number;
    cascadeTargetCategoryName?: string;
  };
}

export async function activateBudgetRevision(revisionId: number, payload?: ActivateBudgetRevisionPayload): Promise<unknown> {
  const mode = getDataMode();

  if (mode === 'demo') {
    await new Promise(resolve => setTimeout(resolve, 300));
    console.log('[activateBudgetRevision] Demo mode - simulating activate:', revisionId);
    return { id: revisionId, status: 'active' };
  }

  const result = await apiFetch<unknown>(`${getApiBaseUrl()}/BudgetRevisions/${revisionId}/activate`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload ?? {}),
  });

  console.log('[activateBudgetRevision] Revision activated successfully:', result);
  return result;
}

// ============================================================
// Billing API Functions
// ============================================================

export interface BillingWipItem {
  id: number;
  projectId: number;
  sourceType: number; // 1=Timesheet, 2=Expense, 3=Disbursement
  sourceId: number;
  taskId?: number;
  employeeId?: number;
  date: string;
  description: string;
  originalQuantity: number;
  originalRate: number;
  originalAmount: number;
  quantity: number;
  unit: string;
  rate: number;
  netAmount: number;
  taxAmount: number;
  grossAmount: number;
  costRate?: number;
  status: number; // 0=Unbilled, 1=Draft, 2=Invoiced, 3=WriteOff, 4=Void
  isWriteOff: boolean;
  invoiceId?: number;
  billedDate?: string;
  createdBy?: string;
  updatedBy?: string;
  postedAt?: string;
  postedBy?: string;
  employeeName?: string;
  taskName?: string;
  externalCostCentreCode?: string;
  roleName?: string;
}

export interface BillingInvoice {
  id: number;
  projectId: number;
  milestoneId?: number; // FK to BillingMilestone; undefined for T&M invoices
  invoiceNumber?: string;
  invoiceDate?: string;
  dueDate?: string;
  description?: string;
  notes?: string;
  status: number; // 0=Draft, 1=Sent, 2=PartiallyPaid, 3=Paid, 4=Overdue, 5=Void, 6=WriteOff, 7=Credited, 8=PartiallyCredited
  subtotal: number;
  taxRate: number;
  taxAmount: number;
  grossTotal: number;
  accessFinancialsId?: string;
  sentDate?: string;
  paidDate?: string;
  paidAmount?: number;
  voidDate?: string;
  voidReason?: string;
  createdBy?: string;
  updatedBy?: string;
  postedAt?: string;
  postedBy?: string;
  draftEmailTo?: string;
  draftEmailCc?: string;
  draftEmailBcc?: string;
  draftEmailSubject?: string;
  draftEmailBody?: string;
  draftEmailTemplateId?: string;
}

export interface BillingInvoiceLineItem {
  id: number;
  invoiceId: number;
  wipLedgerId?: number;
  sortOrder: number;
  description: string;
  quantity: number;
  rate: number;
  netAmount: number;
  taxAmount: number;
  grossAmount: number;
  financialsTransactionId?: number;
  createdBy?: string;
  updatedBy?: string;
  wipDate?: string;
  wipResourceName?: string;
  wipRoleName?: string;
  /** Human-readable WIP source transaction type (e.g. 'Time', 'Purchase', 'Invoice', 'Journal', 'VAT Journal'). */
  type?: string;
  wipCostCentreCode?: string;
  wipCostCentreName?: string;
  wipPhase?: string;
  wipTask?: string;
}

const INVOICE_STATUS_MAP: Record<number, string> = {
  0: 'Draft',
  1: 'Sent',
  2: 'PartiallyPaid',
  3: 'Paid',
  4: 'Overdue',
  5: 'Void',
  6: 'WriteOff',
  7: 'Credited',
  8: 'PartiallyCredited',
};

const WIP_SOURCE_TYPE_MAP: Record<number, string> = {
  1: 'Time',
  2: 'Expense',
  3: 'Purchase',
  4: 'Invoice',
  5: 'Credit Note',
  6: 'Adjustment',
  7: 'Journal',
  8: 'Sales',
  9: 'Miscellaneous',
  10: 'VAT Journal',
  99: 'Other',
};

export function mapInvoiceStatus(status: number): string {
  return INVOICE_STATUS_MAP[status] || 'Unknown';
}

/** Derive the display status for an invoice, promoting Sent/PartiallyPaid to
 *  Overdue when the due date has passed. */
export function deriveInvoiceDisplayStatus(inv: BillingInvoice): string {
  const base = mapInvoiceStatus(inv.status);
  if ((base === 'Sent' || base === 'PartiallyPaid') && inv.dueDate) {
    const due = new Date(inv.dueDate);
    due.setHours(0, 0, 0, 0);
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    if (due < today) return 'Overdue';
  }
  return base;
}

export function mapWipSourceType(sourceType: number): string {
  return WIP_SOURCE_TYPE_MAP[sourceType] || 'Unknown';
}

export interface WipSummary {
  totalNetAmount: number;
  timeNetAmount: number;
  expenseNetAmount: number;
  timeHours: number;
}

export interface PaginatedBillingWipResponse {
  items: BillingWipItem[];
  totalCount: number;
  page: number;
  pageSize: number;
  summary?: WipSummary;
}

export async function fetchBillingWipItems(projectId: number, status?: number, page?: number, pageSize?: number): Promise<BillingWipItem[]> {
  const mode = getDataMode();
  
  if (mode === 'demo') {
    // Demo mode returns empty array - mock data is handled in the component
    return [];
  } else {
    const statusParam = status !== undefined ? `?status=${status}` : '';
    return apiFetch<BillingWipItem[]>(`${getApiBaseUrl()}/billing/${projectId}/wip${statusParam}`);
  }
}

export async function fetchBillingWipItemsPaged(projectId: number, status: number, page: number, pageSize: number): Promise<PaginatedBillingWipResponse> {
  const mode = getDataMode();
  
  if (mode === 'demo') {
    return { items: [], totalCount: 0, page, pageSize, summary: { totalNetAmount: 0, timeNetAmount: 0, expenseNetAmount: 0, timeHours: 0 } };
  } else {
    return apiFetch<PaginatedBillingWipResponse>(
      `${getApiBaseUrl()}/billing/${projectId}/wip?status=${status}&page=${page}&pageSize=${pageSize}`
    );
  }
}

export async function fetchBillingInvoices(projectId: number, status?: number): Promise<BillingInvoice[]> {
  const mode = getDataMode();
  
  if (mode === 'demo') {
    return [];
  } else {
    const statusParam = status !== undefined ? `?status=${status}` : '';
    return apiFetch<BillingInvoice[]>(`${getApiBaseUrl()}/billing/${projectId}/invoices${statusParam}`);
  }
}

export interface PaginatedBillingInvoiceResponse {
  items: BillingInvoice[];
  totalCount: number;
  page: number;
  pageSize: number;
  totalInvoiced: number;
  /** "Invoiced" excluding VAT, for like-for-like comparison against the net budget. */
  totalInvoicedNet: number;
  totalOutstanding: number;
  totalPaid: number;
}

export async function fetchBillingInvoicesPaged(
  projectId: number,
  page: number,
  pageSize: number,
  status?: number,
  search?: string
): Promise<PaginatedBillingInvoiceResponse> {
  const mode = getDataMode();

  if (mode === 'demo') {
    return { items: [], totalCount: 0, page, pageSize, totalInvoiced: 0, totalInvoicedNet: 0, totalOutstanding: 0, totalPaid: 0 };
  } else {
    const params = new URLSearchParams({ page: String(page), pageSize: String(pageSize) });
    if (status !== undefined) params.set('status', String(status));
    if (search) params.set('search', search);
    return apiFetch<PaginatedBillingInvoiceResponse>(
      `${getApiBaseUrl()}/billing/${projectId}/invoices?${params.toString()}`
    );
  }
}

export async function refreshInvoicePaymentStatus(): Promise<{ updatedCount: number; message: string }> {
  return apiFetch<{ updatedCount: number; message: string }>(
    `${getApiBaseUrl()}/billing/invoices/refresh-payment-status`,
    { method: 'POST' }
  );
}

export async function fetchBillingInvoiceById(invoiceId: number): Promise<{ invoice: BillingInvoice; lineItems: BillingInvoiceLineItem[] }> {
  return apiFetch(`${getApiBaseUrl()}/billing/invoices/${invoiceId}`);
}

export interface CustomerTermsResult {
  dueDays: number | null;
  dueType?: string;
  code?: string;
  description?: string;
  error?: string;
}

export async function getCustomerTerms(customerCode: string, companyId: number): Promise<CustomerTermsResult> {
  return apiFetch<CustomerTermsResult>(`${getApiBaseUrl()}/billing/customer-terms?customerCode=${encodeURIComponent(customerCode)}&companyId=${companyId}`);
}

export interface CustomerTaxRateResult {
  vatCode: string | null;
  vatRate: number | null;
}

export async function getCustomerTaxRate(customerCode: string, companyId: number): Promise<CustomerTaxRateResult> {
  return apiFetch<CustomerTaxRateResult>(`${getApiBaseUrl()}/billing/customer-tax-rate?customerCode=${encodeURIComponent(customerCode)}&companyId=${companyId}`);
}

export async function createBillingDraft(projectId: number, wipItemIds: number[], description?: string, notes?: string, invoiceDate?: string, dueDate?: string, options?: { isFixedFee?: boolean; fixedFeeAmount?: number; costCentreCode?: string; milestoneId?: number; manualInvoiceNumber?: string; hasAgreedValue?: boolean; agreedValueWriteOffReason?: string }): Promise<BillingInvoice> {
  return apiFetch<BillingInvoice>(`${getApiBaseUrl()}/billing/${projectId}/drafts`, {
    method: 'POST',
    body: JSON.stringify({ wipItemIds, description, notes, invoiceDate, dueDate, ...options }),
  });
}

export interface UpdateDraftRequest {
  additions?: number[];
  removals?: number[];
  edits?: Array<{
    wipLedgerId: number;
    quantity?: number;
    rate?: number;
    netAmount?: number;
    description?: string;
  }>;
}

export async function updateBillingDraft(invoiceId: number, updates: UpdateDraftRequest): Promise<BillingInvoice> {
  return apiFetch<BillingInvoice>(`${getApiBaseUrl()}/billing/drafts/${invoiceId}`, {
    method: 'PUT',
    body: JSON.stringify(updates),
  });
}

export async function updateDraftDescription(invoiceId: number, description: string | null): Promise<void> {
  await apiFetch(`${getApiBaseUrl()}/billing/drafts/${invoiceId}/description`, {
    method: 'PATCH',
    body: JSON.stringify({ description }),
  });
}

export async function deleteBillingDraft(invoiceId: number): Promise<void> {
  await apiFetch(`${getApiBaseUrl()}/billing/drafts/${invoiceId}`, {
    method: 'DELETE',
  });
}

export async function updateDraftEmailData(invoiceId: number, emailData: { to?: string; cc?: string; bcc?: string; subject?: string; body?: string; templateId?: string }): Promise<void> {
  await apiFetch(`${getApiBaseUrl()}/billing/drafts/${invoiceId}/email-draft`, {
    method: 'PATCH',
    body: JSON.stringify(emailData),
  });
}

export interface PostToFinancialsResponse {
  success: boolean;
  message?: string;
  financialsIds?: number[];
  error?: string;
  validationErrors?: Array<{
    message?: string;
    details?: Array<{ primaryKey?: string; field?: string; value?: string }>;
  }>;
}

export async function postBillingDraftToFinancials(invoiceId: number): Promise<PostToFinancialsResponse> {
  console.log(`[Billing] Posting draft invoice ${invoiceId} to Financials...`);

  const response = await authFetch(`${getApiBaseUrl()}/billing/drafts/${invoiceId}/post`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
  });

  const data = await response.json();
  console.log(`[Billing] Response status: ${response.status}`, data);

  if (response.ok) {
    // 200 OK — success
    console.log(`[Billing] Success — Financials IDs:`, data.financialsIds);
    return {
      success: true,
      message: data.message,
      financialsIds: data.financialsIds,
    };
  }

  if (response.status === 422) {
    // Validation errors from Financials API
    console.warn(`[Billing] Validation errors for invoice ${invoiceId}:`, data.validationErrors);
    return {
      success: false,
      error: data.error,
      validationErrors: data.validationErrors,
      financialsIds: data.financialsIds,
    };
  }

  if (response.status === 400) {
    // Bad request (e.g. invoice not a draft)
    console.warn(`[Billing] Bad request for invoice ${invoiceId}:`, data.error);
    return {
      success: false,
      error: data.error || 'Invalid request',
    };
  }

  // 500 / other errors (backend returns 500 when Financials API fails)
  console.error(`[Billing] Error posting invoice ${invoiceId} — status ${response.status}:`, data);
  return {
    success: false,
    error: data.error || data.message || `Request failed with status ${response.status}`,
    financialsIds: data.financialsIds,
  };
}

export async function voidBillingInvoice(invoiceId: number, reason: string): Promise<BillingInvoice> {
  return apiFetch<BillingInvoice>(`${getApiBaseUrl()}/billing/invoices/${invoiceId}/void`, {
    method: 'POST',
    body: JSON.stringify({ reason }),
  });
}

// ── Company Billing Settings ───────────────────────────────────────────

export interface CompanyBillingSettingsResponse {
  autoNumberingEnabled: boolean;
  lastInvoiceNumber: string | null;
  agedWipDaysThreshold: number | null;
  agedWipAmountWarning: number | null;
  agedWipAmountAlert: number | null;
}

export async function getCompanyBillingSettings(): Promise<CompanyBillingSettingsResponse> {
  const raw = await apiFetch<Partial<CompanyBillingSettingsResponse>>(`${getApiBaseUrl()}/company/billing-settings`);
  return {
    autoNumberingEnabled: raw.autoNumberingEnabled ?? true,
    lastInvoiceNumber: raw.lastInvoiceNumber ?? null,
    agedWipDaysThreshold: raw.agedWipDaysThreshold ?? null,
    agedWipAmountWarning: raw.agedWipAmountWarning ?? null,
    agedWipAmountAlert: raw.agedWipAmountAlert ?? null,
  };
}

export async function patchCompanyBillingSettings(data: {
  autoNumberingEnabled?: boolean;
  agedWipDaysThreshold?: number;
  agedWipAmountWarning?: number;
  agedWipAmountAlert?: number;
}): Promise<CompanyBillingSettingsResponse> {
  const raw = await apiFetch<Partial<CompanyBillingSettingsResponse>>(`${getApiBaseUrl()}/company/billing-settings`, {
    method: 'PATCH',
    body: JSON.stringify(data),
  });
  return {
    autoNumberingEnabled: raw.autoNumberingEnabled ?? true,
    lastInvoiceNumber: raw.lastInvoiceNumber ?? null,
    agedWipDaysThreshold: raw.agedWipDaysThreshold ?? null,
    agedWipAmountWarning: raw.agedWipAmountWarning ?? null,
    agedWipAmountAlert: raw.agedWipAmountAlert ?? null,
  };
}

export interface AccessFinancialsSettingsResponse {
  autoInvoiceNumbering: boolean;
}

export async function getAccessFinancialsSettings(): Promise<AccessFinancialsSettingsResponse> {
  return apiFetch<AccessFinancialsSettingsResponse>(`${getApiBaseUrl()}/integrations/access-financials/settings`);
}

// ── Invoice Number Validation ──────────────────────────────────────────

export interface ValidateInvoiceNumberResponse {
  isValid: boolean;
  isDuplicate: boolean;
  message?: string;
}

export async function validateInvoiceNumber(invoiceNumber: string): Promise<ValidateInvoiceNumberResponse> {
  return apiFetch<ValidateInvoiceNumberResponse>(`${getApiBaseUrl()}/billing/invoices/validate-number`, {
    method: 'POST',
    body: JSON.stringify({ invoiceNumber }),
  });
}

// ── Project Transactions ───────────────────────────────────────────────

export interface ProjectTransactionQueryParams {
  first?: number;
  after?: string | null;
  projectCode?: string;
  entryType?: string;
  isBatched?: boolean | null;
  isInvoiced?: boolean | null;
  dateFrom?: string;
  dateTo?: string;
  sortField?: string;
  sortOrder?: 'Ascending' | 'Descending';
}

export interface ProjectTransactionPageInfo {
  endCursor: string | null;
  hasNextPage: boolean;
  hasPreviousPage: boolean;
  startCursor: string | null;
}

export interface ProjectTransactionDto {
  transactionId: number | null;
  projectCode: string | null;
  projectName: string | null;
  projectStatus: string | null;
  date: string | null;
  periodNumber: number | null;
  yearLabel: string | null;
  weekNo: number | null;
  dateEntered: string | null;
  transactionType: string | null;
  transactionStatus: string | null;
  source: string | null;
  entryType: string | null;
  isBatched: boolean | null;
  isInvoiced: boolean | null;
  reference: string | null;
  internalRef: number | null;
  detail: string | null;
  batchRef: string | null;
  sortKey: string | null;
  headerKey: string | null;
  resourceCode: string | null;
  resourceName: string | null;
  resourcePriceLine: number | null;
  employeeCode: string | null;
  enteredBy: string | null;
  accountCode: string | null;
  accountName: string | null;
  invoiceAccountCode: string | null;
  invoiceAccountName: string | null;
  orderAccountCode: string | null;
  orderAccountName: string | null;
  supplierAccountCode: string | null;
  costCentreCode: string | null;
  costCentreName: string | null;
  quantity: number | null;
  price: number | null;
  value: number | null;
  uplift: number | null;
  upliftValue: number | null;
  priceWithUplift: number | null;
  currencyCode: string | null;
  currentPrice: number | null;
  currentValue: number | null;
  currentUpliftValue: number | null;
  actualSales: number | null;
  actualCost: number | null;
  salesOrderNumber: number | null;
  purchaseOrderNumber: string | null;
  stockCode: string | null;
  stockName: string | null;
  analysisCode: string | null;
  analysisName: string | null;
  nominalCode: string | null;
  payElement: string | null;
  payRate: number | null;
  subLedger: string | null;
  customField1: string | null;
  customField2: string | null;
  customField3: string | null;
  customField4: string | null;
  customField5: string | null;
  customField6: string | null;
  userKey1: string | null;
  userKey2: string | null;
  userKey3: string | null;
  userChar1: string | null;
  userChar2: string | null;
  userChar3: string | null;
  userChar4: string | null;
  userDate1: string | null;
  userDate2: string | null;
  userFlag1: boolean | null;
  userFlag2: boolean | null;
  userNumber1: number | null;
  userNumber2: number | null;
}

export interface ProjectTransactionResponse {
  items: ProjectTransactionDto[];
  pageInfo: ProjectTransactionPageInfo | null;
  errorMessage: string | null;
}

/**
 * Fetches project transactions from the backend.
 * In demo mode returns mock data; in live mode queries the real API
 * which delegates to the external transaction data provider (e.g. Financials GraphQL).
 */
export async function fetchProjectTransactions(
  params: ProjectTransactionQueryParams = {}
): Promise<ProjectTransactionResponse> {
  const mode = getDataMode();

  if (mode === 'demo') {
    await new Promise(resolve => setTimeout(resolve, 400));
    // Return empty result for demo mode — the page already has its own mock data
    return { items: [], pageInfo: null, errorMessage: null };
  }

  // Build query string — companyId is sent via the X-Company-Id header by authFetch
  const queryParams = new URLSearchParams();
  if (params.first) queryParams.set('first', params.first.toString());
  if (params.after) queryParams.set('after', params.after);
  if (params.projectCode) queryParams.set('projectCode', params.projectCode);
  if (params.entryType) queryParams.set('entryType', params.entryType);
  if (params.isBatched != null) queryParams.set('isBatched', params.isBatched.toString());
  if (params.isInvoiced != null) queryParams.set('isInvoiced', params.isInvoiced.toString());
  if (params.dateFrom) queryParams.set('dateFrom', params.dateFrom);
  if (params.dateTo) queryParams.set('dateTo', params.dateTo);
  if (params.sortField) queryParams.set('sortField', params.sortField);
  if (params.sortOrder) queryParams.set('sortOrder', params.sortOrder);

  return apiFetch<ProjectTransactionResponse>(
    `${getApiBaseUrl()}/projecttransactions?${queryParams.toString()}`
  );
}

/**
 * Request body for the POST /api/projecttransactions/query endpoint.
 * Supports multi-field sorting and structured filters for server-side
 * pagination, filtering, and sorting.
 */
export interface ProjectTransactionQueryBody {
  companyId?: string;
  first: number;
  after?: string | null;
  sort?: Array<{ field: string; order: string }>;
  projectCode?: string;
  projectCodes?: string[];
  transactionTypes?: string[];
  sources?: string[];
  entryType?: string;
  isBatched?: boolean;
  isInvoiced?: boolean;
  projectStatuses?: string[];
  dateFrom?: string;
  dateTo?: string;
  searchTerm?: string;
  analysisCodes?: string[];
}

/**
 * Queries project transactions using the POST /query endpoint.
 * Supports multi-field sorting and advanced filters via a JSON body,
 * enabling true server-side pagination, filtering, and sorting.
 */
export async function queryProjectTransactions(
  body: ProjectTransactionQueryBody
): Promise<ProjectTransactionResponse> {
  const mode = getDataMode();

  if (mode === 'demo') {
    await new Promise(resolve => setTimeout(resolve, 400));
    return { items: [], pageInfo: null, errorMessage: null };
  }

  return apiFetch<ProjectTransactionResponse>(
    `${getApiBaseUrl()}/projecttransactions/query`,
    {
      method: 'POST',
      body: JSON.stringify(body),
    }
  );
}

// ── Transaction Totals (server-side KPIs) ──────────────────────────────

export interface ProjectTransactionTotalsResponse {
  totalCount: number;
  totalSales: number;
  totalCost: number;
  netTotal: number;
  errorMessage: string | null;
}

export async function queryProjectTransactionTotals(
  body: ProjectTransactionQueryBody
): Promise<ProjectTransactionTotalsResponse> {
  const mode = getDataMode();
  if (mode === 'demo') {
    return { totalCount: 0, totalSales: 0, totalCost: 0, netTotal: 0, errorMessage: null };
  }
  return apiFetch<ProjectTransactionTotalsResponse>(
    `${getApiBaseUrl()}/projecttransactions/totals`,
    { method: 'POST', body: JSON.stringify(body) }
  );
}

// ── Transaction Grouped Totals (server-side Group By) ──────────────────

export interface ProjectTransactionGroupRow {
  key: string;
  count: number;
  totalSales: number;
  totalCost: number;
  net: number;
}

export interface ProjectTransactionGroupedTotalsResponse {
  groups: ProjectTransactionGroupRow[];
  errorMessage: string | null;
}

export async function queryProjectTransactionGroupedTotals(
  body: ProjectTransactionQueryBody & { groupBy: string }
): Promise<ProjectTransactionGroupedTotalsResponse> {
  const mode = getDataMode();
  if (mode === 'demo') {
    return { groups: [], errorMessage: null };
  }
  return apiFetch<ProjectTransactionGroupedTotalsResponse>(
    `${getApiBaseUrl()}/projecttransactions/grouped-totals`,
    { method: 'POST', body: JSON.stringify(body) }
  );
}

// ── Sales Transactions ─────────────────────────────────────────────────

export interface BulkCreateSalesTransactionRequest {
  transactions: Array<Record<string, unknown>>;
  validateOnly?: boolean;
}

export interface BulkCreateSalesTransactionResponse {
  ids?: number[];
  errors?: Array<{ message?: string; details?: Array<{ primaryKey?: string; field?: string; value?: string }> }>;
  warnings?: Array<{ message?: string; details?: Array<{ primaryKey?: string; field?: string; value?: string }> }>;
  errorMessage?: string;
}

export async function bulkCreateSalesTransactions(
  request: BulkCreateSalesTransactionRequest,
  companyId: number
): Promise<BulkCreateSalesTransactionResponse> {
  return apiFetch<BulkCreateSalesTransactionResponse>(
    `${getApiBaseUrl()}/billing/sales-transactions/bulk-create?companyId=${companyId}`,
    {
      method: 'POST',
      body: JSON.stringify(request),
    }
  );
}

// ── Revision Reason Categories ───────────────────────────────────────────

export async function fetchRevisionReasonCategories(): Promise<RevisionReasonCategoryConfig[]> {
  const baseUrl = getApiBaseUrl();
  return apiFetch<RevisionReasonCategoryConfig[]>(`${baseUrl}/RevisionReasonCategories`);
}

export async function createRevisionReasonCategory(data: { label: string; displayOrder: number }): Promise<RevisionReasonCategoryConfig> {
  const baseUrl = getApiBaseUrl();
  return apiFetch<RevisionReasonCategoryConfig>(`${baseUrl}/RevisionReasonCategories`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(data),
  });
}

export async function updateRevisionReasonCategory(id: number, data: { label?: string; isEnabled?: boolean; displayOrder?: number }): Promise<void> {
  const baseUrl = getApiBaseUrl();
  return apiFetch<void>(`${baseUrl}/RevisionReasonCategories/${id}`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(data),
  });
}

export async function deleteRevisionReasonCategory(id: number): Promise<void> {
  const baseUrl = getApiBaseUrl();
  return apiFetch<void>(`${baseUrl}/RevisionReasonCategories/${id}`, {
    method: 'DELETE',
  });
}

export async function reorderRevisionReasonCategories(updates: { id: number; displayOrder: number }[]): Promise<void> {
  const baseUrl = getApiBaseUrl();
  return apiFetch<void>(`${baseUrl}/RevisionReasonCategories/reorder`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ updates }),
  });
}

// ── Category Types ──────────────────────────────────────────────────────────

const DEMO_CATEGORY_TYPES: CategoryType[] = [
  { id: 1, code: 'labour',         label: 'Labour',         abbreviation: 'LAB', description: 'Labour and professional services',    displayOrder: 1, isSystem: true, isActive: true },
  { id: 2, code: 'expenses',       label: 'Expenses',       abbreviation: 'EXP', description: 'Business expenses',                   displayOrder: 2, isSystem: true, isActive: true },
  { id: 3, code: 'subcontractors', label: 'Subcontractors', abbreviation: 'SUB', description: 'External subcontractor costs',        displayOrder: 3, isSystem: true, isActive: true },
  { id: 4, code: 'materials',      label: 'Materials',      abbreviation: 'MAT', description: 'Materials and supplies',               displayOrder: 4, isSystem: true, isActive: true },
  { id: 5, code: 'equipment',      label: 'Equipment',      abbreviation: 'EQP', description: 'Equipment costs',                     displayOrder: 5, isSystem: true, isActive: true },
  { id: 6, code: 'overhead',       label: 'Overhead',       abbreviation: 'OVH', description: 'Overhead and indirect costs',         displayOrder: 6, isSystem: true, isActive: true },
  { id: 7, code: 'contingency',    label: 'Contingency',    abbreviation: 'CTG', description: 'Contingency and risk reserves',       displayOrder: 7, isSystem: true, isActive: true },
];

export async function fetchCategoryTypes(): Promise<CategoryType[]> {
  const mode = getDataMode();

  if (mode === 'demo') {
    await new Promise(resolve => setTimeout(resolve, 200));
    return DEMO_CATEGORY_TYPES;
  } else {
    return apiFetch<CategoryType[]>(`${getApiBaseUrl()}/category-types`);
  }
}

// ── Write-Off API functions ─────────────────────────────────────────────────

export interface WriteOffReason {
  id: number;
  code: string;
  label: string;
  isDefault: boolean;
  isActive: boolean;
  sortOrder: number;
}

export async function writeOffWipItems(wipItemIds: number[], reasonCode?: string): Promise<{ message: string; count: number }> {
  return apiFetch<{ message: string; count: number }>(`${getApiBaseUrl()}/billing/wip/write-off`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ wipItemIds, reasonCode }),
  });
}

// ── Credit Note API functions ───────────────────────────────────────────────

export interface CreateCreditNoteParams {
  creditType: 'full' | 'partial';
  creditAmount?: number;
  tamDisposition?: 'return-to-wip' | 'write-off';
  writeOffReasonCode?: string;
}

export interface CreditNoteResponse {
  id: number;
  invoiceId: number;
  projectId: number;
  creditNoteNumber: string | null;
  creditType: string;
  creditAmount: number;
  tamDisposition: string | null;
  writeOffReasonCode: string | null;
  status: number;
  accessFinancialsId: string | null;
  subtotal: number;
  taxAmount: number;
  grossTotal: number;
  postedAt: string | null;
  postedBy: string | null;
  createdBy: string | null;
}

export async function createCreditNote(invoiceId: number, params: CreateCreditNoteParams): Promise<CreditNoteResponse> {
  return apiFetch<CreditNoteResponse>(`${getApiBaseUrl()}/billing/invoices/${invoiceId}/credit-note`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      creditType: params.creditType,
      creditAmount: params.creditAmount,
      tamDisposition: params.tamDisposition,
      writeOffReasonCode: params.writeOffReasonCode,
    }),
  });
}

export async function undoWriteOff(wipItemId: number): Promise<{ message: string }> {
  return apiFetch<{ message: string }>(`${getApiBaseUrl()}/billing/wip/${wipItemId}/undo-write-off`, {
    method: 'POST',
  });
}

export async function fetchWriteOffReasons(): Promise<WriteOffReason[]> {
  return apiFetch<WriteOffReason[]>(`${getApiBaseUrl()}/settings/write-off-reasons`);
}

export async function fetchBillingSettings(): Promise<{ requireWriteOffReason: boolean }> {
  return apiFetch<{ requireWriteOffReason: boolean }>(`${getApiBaseUrl()}/settings/billing`);
}

export async function updateBillingSettings(settings: { requireWriteOffReason: boolean }): Promise<void> {
  await apiFetch<void>(`${getApiBaseUrl()}/settings/billing`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(settings),
  });
}

export async function createWriteOffReason(reason: { code: string; label: string; isDefault?: boolean; sortOrder?: number }): Promise<WriteOffReason> {
  return apiFetch<WriteOffReason>(`${getApiBaseUrl()}/settings/write-off-reasons`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(reason),
  });
}

export async function updateWriteOffReason(id: number, reason: { code: string; label: string; isDefault?: boolean; sortOrder?: number }): Promise<void> {
  await apiFetch<void>(`${getApiBaseUrl()}/settings/write-off-reasons/${id}`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(reason),
  });
}

export async function deleteWriteOffReason(id: number): Promise<void> {
  await apiFetch<void>(`${getApiBaseUrl()}/settings/write-off-reasons/${id}`, {
    method: 'DELETE',
  });
}

// ── Project Template API functions ──────────────────────────────────────────

function parseJsonFields(template: Record<string, unknown>): ProjectTemplate {
  const parsed = { ...template } as Record<string, unknown>;
  if (typeof parsed.costCentres === 'string') {
    try { parsed.costCentres = JSON.parse(parsed.costCentres as string); } catch { parsed.costCentres = []; }
  }
  parsed.costCentres = parsed.costCentres ?? [];
  if (typeof parsed.resourceBudget === 'string') {
    try { parsed.resourceBudget = JSON.parse(parsed.resourceBudget as string); } catch { parsed.resourceBudget = []; }
  }
  parsed.resourceBudget = parsed.resourceBudget ?? [];
  // Map backend numeric id to string for frontend compatibility
  if (parsed.id !== undefined && parsed.id !== null) {
    parsed.id = String(parsed.id);
  }
  return parsed as unknown as ProjectTemplate;
}

function stringifyJsonFields(data: Record<string, unknown>): Record<string, unknown> {
  const payload = { ...data };
  if (payload.costCentres !== undefined && typeof payload.costCentres !== 'string') {
    payload.costCentres = JSON.stringify(payload.costCentres ?? []);
  }
  if (payload.resourceBudget !== undefined && typeof payload.resourceBudget !== 'string') {
    payload.resourceBudget = JSON.stringify(payload.resourceBudget ?? []);
  }
  return payload;
}

export async function fetchProjectTemplates(): Promise<ProjectTemplate[]> {
  const mode = getDataMode();
  if (mode === 'demo' || mode === 'tour') {
    return [];
  }
  const raw = await apiFetch<Record<string, unknown>[]>(`${getApiBaseUrl()}/project-templates`);
  return raw.map(parseJsonFields);
}

export async function fetchProjectTemplateById(id: string | number): Promise<ProjectTemplate> {
  const raw = await apiFetch<Record<string, unknown>>(`${getApiBaseUrl()}/project-templates/${id}`);
  return parseJsonFields(raw);
}

export async function createProjectTemplate(data: CreateTemplateData): Promise<ProjectTemplate> {
  const payload = stringifyJsonFields(data as unknown as Record<string, unknown>);
  const raw = await apiFetch<Record<string, unknown>>(`${getApiBaseUrl()}/project-templates`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  });
  return parseJsonFields(raw);
}

export async function updateProjectTemplate(id: string | number, data: Partial<CreateTemplateData> & { id?: string | number }): Promise<void> {
  const payload = stringifyJsonFields({ ...data, id: Number(id) } as unknown as Record<string, unknown>);
  await apiFetch<void>(`${getApiBaseUrl()}/project-templates/${id}`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  });
}

export async function deleteProjectTemplate(id: string | number): Promise<void> {
  await apiFetch<void>(`${getApiBaseUrl()}/project-templates/${id}`, {
    method: 'DELETE',
  });
}

export async function incrementProjectTemplateUsage(id: string | number): Promise<void> {
  await apiFetch<void>(`${getApiBaseUrl()}/project-templates/${id}/use`, {
    method: 'POST',
  });
}

// ── Profitability Analysis Report ────────────────────────────────────────────

/**
 * Fetches per-project profitability data for the Profitability Analysis Report.
 *
 * Demo mode: builds from `mockProjects` + `getMockBudgetData()`. Derives health
 * using the configured `BudgetHealthThresholds` so RAG colouring is consistent
 * with the rest of the application.
 *
 * Live mode: calls `GET /api/reports/profitability` and returns the `projects`
 * array from the response. The endpoint returns revenue/cost actuals sourced
 * from costing transactions and overhead from overhead-type cost categories.
 *
 * @returns Array of `ProjectProfitabilityData`, one entry per project. Projects
 *          without budget data (no `allBudgets.profitability`) are excluded so
 *          the table never renders rows with undefined financial figures.
 */
export async function fetchProfitabilityData(): Promise<import('@/types/reports.types').ProjectProfitabilityData[]> {
  const mode = getDataMode();

  if (mode === 'demo' || mode === 'tour') {
    await new Promise(resolve => setTimeout(resolve, 350));

    const projects = mode === 'demo' ? mockData.mockProjects : (tourMockData as any).tourMockProjects ?? mockData.mockProjects;
    const thresholds = getBudgetHealthThresholds();

    const results = await Promise.all(
      projects.map(async (project: any) => {
        const budgetData = getMockBudgetData(project.id);
        const profitability = budgetData?.allBudgets?.profitability;

        // Skip projects with no profitability data (should not happen in demo — all
        // projects have either explicit or dynamically-generated profitability)
        if (!profitability) return null;

        // Derive health consistently with the project list
        const health = deriveProjectHealth(
          {
            actualCost: (project.actualLabour ?? 0) + (project.actualExpense ?? 0),
            budgetCost: (project.budgetLabour ?? 0) + (project.budgetExpense ?? 0),
            startDate: project.startDate,
            endDate: project.endDate,
            completionPercent: project.completionPercentOverride ?? project.completionPercent ?? 0,
            actualHours: project.actualHours ?? 0,
            budgetHours: project.budgetHours ?? 0,
            wipAmount: project.wipAmount ?? 0,
            actualRevenue: project.actualRevenue ?? 0,
            budgetRevenue: project.budgetRevenue ?? 0,
            forecastEndDate: project.forecastEndDate,
          },
          thresholds
        );

        // Resolve PM name from roleAssignments (preferred) or fall back to
        // projectManagerId lookup via mockEmployees
        const pmFromRoles = project.roleAssignments?.find(
          (r: any) => r.role === 'project-manager'
        )?.employeeName;
        const pmFromId = pmFromRoles ?? (
          mockData.mockEmployees?.find((e: any) => e.id === project.projectManagerId)?.name ?? '—'
        );

        const entry: import('@/types/reports.types').ProjectProfitabilityData = {
          projectId: project.id,
          projectName: project.name,
          projectCode: project.code ?? '',
          clientName: project.clientNames ?? '—',
          projectManager: pmFromId,
          projectType: project.type ?? '',
          status: project.status ?? String(project.statusId ?? ''),
          health,
          billable: project.billable ?? true,
          profitability,
        };

        return entry;
      })
    );

    // Filter out any null entries (projects without profitability data)
    return results.filter((r): r is import('@/types/reports.types').ProjectProfitabilityData => r !== null);
  }

  // Live mode — calls GET /api/reports/profitability (§8.5)
  const baseUrl = getApiBaseUrl();
  const response = await apiFetch<{ projects: import('@/types/reports.types').ProjectProfitabilityData[] }>(
    `${baseUrl}/reports/profitability`,
    { credentials: 'include' }
  );
  return response.projects;
}

/** Parameters for the paginated profitability report endpoint. */
export interface ProfitabilityReportParams {
  page?: number;
  pageSize?: number;
  status?: string;
  pm?: string;
  search?: string;
  sortBy?: string;
  sortOrder?: 'asc' | 'desc';
  /** 'billable' | 'non-billable' — omit for all */
  billable?: string;
}

/**
 * Fetches a single page of profitability data from the server.
 *
 * Demo mode: falls back to the full client-side dataset wrapped in a paginated
 * response shape so the page component can use the same code path.
 *
 * Live mode: calls `GET /api/reports/profitability?page=&pageSize=&…` which
 * returns only the requested page plus portfolio-level summary and filter options.
 */
export async function fetchProfitabilityDataPaged(
  params: ProfitabilityReportParams = {},
): Promise<import('@/types/reports.types').ProfitabilityPaginatedResponse> {
  const mode = getDataMode();

  if (mode === 'demo' || mode === 'tour') {
    // Re-use the existing demo builder then paginate client-side
    const allProjects = await fetchProfitabilityData();
    const page = params.page ?? 1;
    const pageSize = params.pageSize ?? 25;

    // Apply filters
    let filtered = allProjects;
    if (params.status && params.status !== 'all')
      filtered = filtered.filter(d => d.status === params.status);
    if (params.pm && params.pm !== 'all')
      filtered = filtered.filter(d => d.projectManager === params.pm);
    if (params.search) {
      const searchLower = params.search.toLowerCase();
      filtered = filtered.filter(d =>
        d.projectName.toLowerCase().includes(searchLower) ||
        (d.projectCode && d.projectCode.toLowerCase().includes(searchLower))
      );
    }
    if (params.billable === 'billable') {
      filtered = filtered.filter(d => d.billable !== false);
    } else if (params.billable === 'non-billable') {
      filtered = filtered.filter(d => d.billable === false);
    }

    // Sort
    const sortKey = params.sortBy ?? 'grossMarginActual';
    const asc = params.sortOrder === 'asc';
    filtered = [...filtered].sort((a, b) => {
      let diff = 0;
      switch (sortKey) {
        case 'projectName':
          diff = a.projectName.localeCompare(b.projectName);
          break;
        case 'revenueActual':
          diff = a.profitability.revenue.actual - b.profitability.revenue.actual;
          break;
        case 'grossProfitActual':
          diff = a.profitability.grossProfit.actual - b.profitability.grossProfit.actual;
          break;
        default:
          diff = a.profitability.grossMargin.actual - b.profitability.grossMargin.actual;
      }
      return asc ? diff : -diff;
    });

    // Summary
    const totalGP = filtered.reduce((s, d) => s + d.profitability.grossProfit.actual, 0);
    const totalRev = filtered.reduce((s, d) => s + d.profitability.revenue.actual, 0);
    const thresholds = getBudgetHealthThresholds();

    const highest = filtered.length > 0
      ? filtered.reduce((best, d) => d.profitability.grossMargin.actual > best.profitability.grossMargin.actual ? d : best)
      : null;
    const lowest = filtered.length > 0
      ? filtered.reduce((worst, d) => d.profitability.grossMargin.actual < worst.profitability.grossMargin.actual ? d : worst)
      : null;

    // Paginate
    const { paginateArray } = await import('@/types/pagination');
    const { data, pagination } = paginateArray(filtered, page, pageSize);

    return {
      projects: data,
      portfolioSummary: {
        totalProjects: filtered.length,
        totalBudgetRevenue: filtered.reduce((s, d) => s + d.profitability.revenue.budget, 0),
        totalActualRevenue: totalRev,
        totalBudgetGP: filtered.reduce((s, d) => s + d.profitability.grossProfit.budget, 0),
        totalActualGP: totalGP,
        portfolioGPPercent: totalRev > 0 ? (totalGP / totalRev) * 100 : 0,
        projectsBelowAmberThreshold: filtered.filter(d => d.profitability.grossMargin.actual < (thresholds.marginAmberThreshold ?? 25)).length,
        totalOverheadBudget: filtered.reduce((s, d) => s + d.profitability.overhead.budget, 0),
        totalOverheadActual: filtered.reduce((s, d) => s + d.profitability.overhead.actual, 0),
        totalNetProfit: filtered.reduce((s, d) => s + d.profitability.netProfit.actual, 0),
        portfolioNetMarginPercent: 0,
        highestMarginProjectName: highest?.projectName ?? null,
        highestMarginValue: highest?.profitability.grossMargin.actual ?? 0,
        lowestMarginProjectName: lowest?.projectName ?? null,
        lowestMarginValue: lowest?.profitability.grossMargin.actual ?? 0,
      },
      pagination,
      filterOptions: {
        projectManagers: [...new Set(allProjects.map(d => d.projectManager).filter(Boolean))].sort(),
        statuses: [...new Set(allProjects.map(d => d.status).filter(Boolean))].sort(),
      },
    };
  }

  // Live mode — server-side pagination
  const baseUrl = getApiBaseUrl();
  const searchParams = new URLSearchParams();
  if (params.page) searchParams.set('page', String(params.page));
  if (params.pageSize) searchParams.set('pageSize', String(params.pageSize));
  if (params.status && params.status !== 'all') searchParams.set('status', params.status);
  if (params.pm && params.pm !== 'all') searchParams.set('pm', params.pm);
  if (params.search) searchParams.set('search', params.search);
  if (params.sortBy) searchParams.set('sortBy', params.sortBy);
  if (params.sortOrder) searchParams.set('sortOrder', params.sortOrder);

  const qs = searchParams.toString();
  const url = `${baseUrl}/reports/profitability${qs ? `?${qs}` : ''}`;

  return apiFetch<import('@/types/reports.types').ProfitabilityPaginatedResponse>(url, {
    credentials: 'include',
  });
}

// ── Project P&L Report ──────────────────────────────────────────────────────

/**
 * Fetches Project P&L data for a single project.
 *
 * Demo mode: builds from `mockProjects` using lightweight `computeProjectCostBudget`
 * arithmetic (no expensive `getBudgetDataByProjectId` call). GP is calculated
 * client-side using `calculateGP()` and `calculateBudgetGP()`.
 *
 * Live mode: calls `GET /api/reports/project-pl/{projectId}` and returns the
 * response directly. The endpoint provides raw revenue and cost figures; GP is
 * still calculated client-side by the page component using the user's chosen method.
 */
export async function fetchProjectPLData(projectId: number): Promise<import('@/types/reports.types').ProjectPLReport | null> {
  const mode = getDataMode();

  if (mode === 'demo' || mode === 'tour') {
    await new Promise(resolve => setTimeout(resolve, 250));

    const projects = mode === 'demo' ? mockData.mockProjects : (tourMockData as any).tourMockProjects ?? mockData.mockProjects;
    const project = projects.find((p: any) => p.id === projectId);
    if (!project) return null;

    // Lightweight cost budget computation (same logic as computeProjectCostBudget in project-pl-report.tsx)
    const labourBudget  = project.budgetLabour ?? 0;
    const labourActual  = project.actualLabour ?? 0;
    const expenseBudget = project.budgetExpense ?? 0;
    const expenseActual = project.actualExpense ?? 0;

    const totalBudgetCost = labourBudget + expenseBudget;
    const totalActualCost = labourActual + expenseActual;

    // Category breakdown using fixed percentage splits
    const categories: import('@/types/reports.types').PLCostCategory[] = [
      { id: 1, name: 'Labour', type: 'labour', budget: labourBudget, actual: labourActual, variance: labourActual - labourBudget, variancePercent: labourBudget !== 0 ? ((labourActual - labourBudget) / labourBudget) * 100 : 0 },
      { id: 2, name: 'Materials', type: 'expense', budget: expenseBudget * 0.25, actual: expenseActual * 0.24, variance: (expenseActual * 0.24) - (expenseBudget * 0.25), variancePercent: (expenseBudget * 0.25) !== 0 ? (((expenseActual * 0.24) - (expenseBudget * 0.25)) / (expenseBudget * 0.25)) * 100 : 0 },
      { id: 3, name: 'Equipment', type: 'expense', budget: expenseBudget * 0.20, actual: expenseActual * 0.21, variance: (expenseActual * 0.21) - (expenseBudget * 0.20), variancePercent: (expenseBudget * 0.20) !== 0 ? (((expenseActual * 0.21) - (expenseBudget * 0.20)) / (expenseBudget * 0.20)) * 100 : 0 },
      { id: 4, name: 'Subcontractors', type: 'subcontractor', budget: expenseBudget * 0.15, actual: expenseActual * 0.16, variance: (expenseActual * 0.16) - (expenseBudget * 0.15), variancePercent: (expenseBudget * 0.15) !== 0 ? (((expenseActual * 0.16) - (expenseBudget * 0.15)) / (expenseBudget * 0.15)) * 100 : 0 },
      { id: 5, name: 'Travel & Expenses', type: 'expense', budget: expenseBudget * 0.15, actual: expenseActual * 0.14, variance: (expenseActual * 0.14) - (expenseBudget * 0.15), variancePercent: (expenseBudget * 0.15) !== 0 ? (((expenseActual * 0.14) - (expenseBudget * 0.15)) / (expenseBudget * 0.15)) * 100 : 0 },
      { id: 6, name: 'Software & Licenses', type: 'expense', budget: expenseBudget * 0.15, actual: expenseActual * 0.15, variance: (expenseActual * 0.15) - (expenseBudget * 0.15), variancePercent: (expenseBudget * 0.15) !== 0 ? (((expenseActual * 0.15) - (expenseBudget * 0.15)) / (expenseBudget * 0.15)) * 100 : 0 },
      { id: 7, name: 'Overhead', type: 'overhead', budget: expenseBudget * 0.07, actual: expenseActual * 0.07, variance: (expenseActual * 0.07) - (expenseBudget * 0.07), variancePercent: (expenseBudget * 0.07) !== 0 ? (((expenseActual * 0.07) - (expenseBudget * 0.07)) / (expenseBudget * 0.07)) * 100 : 0 },
      { id: 8, name: 'Contingency', type: 'expense', budget: expenseBudget * 0.03, actual: expenseActual * 0.03, variance: (expenseActual * 0.03) - (expenseBudget * 0.03), variancePercent: (expenseBudget * 0.03) !== 0 ? (((expenseActual * 0.03) - (expenseBudget * 0.03)) / (expenseBudget * 0.03)) * 100 : 0 },
    ];

    // Revenue
    const contractValue = project.budgetRevenue ?? 0;
    const invoicedRevenue = project.actualSales ?? project.actualRevenue ?? 0;
    const revenueVariance = invoicedRevenue - contractValue;
    const revenueVariancePercent = contractValue !== 0 ? (revenueVariance / contractValue) * 100 : 0;

    // Cost variance
    const costVariance = totalActualCost - totalBudgetCost;
    const costVariancePercent = totalBudgetCost !== 0 ? (costVariance / totalBudgetCost) * 100 : 0;

    // Hours
    const budgetHours = project.budgetHours ?? 0;
    const actualHours = project.actualHours ?? 0;
    const hoursVariance = actualHours - budgetHours;
    const hoursVariancePercent = budgetHours !== 0 ? (hoursVariance / budgetHours) * 100 : 0;

    // GP (simple method for the raw data — page component recalculates with chosen method)
    const budgetGPResult = calculateBudgetGP({ revenue: contractValue, totalDirectCosts: totalBudgetCost }, 'simple');
    const actualGPResult = calculateGP({
      revenue: invoicedRevenue,
      totalDirectCosts: totalActualCost,
      labourCosts: project.actualLabourCosts,
      subcontractorCosts: project.actualSubcontractorCosts,
      overheadAbsorption: project.actualOverheadAbsorption,
    }, 'simple');

    // Resolve PM name
    const pmFromRoles = project.roleAssignments?.find(
      (r: any) => r.role === 'project-manager'
    )?.employeeName;
    const pmName = pmFromRoles ?? (
      mockData.mockEmployees?.find((e: any) => e.id === project.projectManagerId)?.name ?? '—'
    );

    const result: import('@/types/reports.types').ProjectPLReport = {
      projectId: project.id,
      projectName: project.name,
      projectCode: project.code ?? '',
      clientName: project.clientNames ?? '—',
      projectType: project.type ?? 'time-and-materials',
      projectManager: pmName,
      status: project.status ?? String(project.statusId ?? ''),
      startDate: project.startDate ?? '',
      endDate: project.endDate ?? '',
      completionPercent: project.completionPercentOverride ?? project.completionPercent ?? 0,
      revenue: {
        contractValue,
        invoicedRevenue,
        recognisedRevenue: null,
        revenueVariance,
        revenueVariancePercent,
      },
      costs: {
        totals: {
          budget: totalBudgetCost,
          actual: totalActualCost,
          variance: costVariance,
          variancePercent: costVariancePercent,
        },
        categories,
        simplifiedFallback: false,
      },
      grossProfit: {
        budgetGP: budgetGPResult.grossProfit,
        actualGP: actualGPResult.grossProfit,
        gpVariance: actualGPResult.grossProfit - budgetGPResult.grossProfit,
        gpVariancePercent: budgetGPResult.grossProfit !== 0 ? ((actualGPResult.grossProfit - budgetGPResult.grossProfit) / budgetGPResult.grossProfit) * 100 : 0,
        budgetMarginPct: budgetGPResult.grossMarginPct,
        actualMarginPct: actualGPResult.grossMarginPct,
        marginVariancePp: actualGPResult.grossMarginPct - budgetGPResult.grossMarginPct,
        gpMethodUsed: 'simple',
      },
      hours: {
        budget: budgetHours,
        actual: actualHours,
        variance: hoursVariance,
        variancePercent: hoursVariancePercent,
      },
      actualLabourCosts: project.actualLabourCosts,
      actualSubcontractorCosts: project.actualSubcontractorCosts,
      actualOverheadAbsorption: project.actualOverheadAbsorption,
      costBreakdownAvailable: true,
    };

    return result;
  }

  // Live mode — call the real endpoint
  const baseUrl = getApiBaseUrl();
  const response = await apiFetch<import('@/types/reports.types').ProjectPLReport>(
    `${baseUrl}/reports/project-pl/${projectId}`
  );
  return response;
}

// ── Project Forecast Report ─────────────────────────────────────────────────

/**
 * Fetches per-project forecast data for the Project Forecast Report.
 *
 * Demo mode: builds from `mockProjects` + `getMockBudgetData()`. Returns the
 * same shape as the live endpoint so the report page works identically in both
 * modes.
 *
 * Live mode: calls `GET /api/reports/project-forecast` and returns the
 * `projects` array from the response.
 */
export async function fetchProjectForecastData(): Promise<{
  projects: import('@/types/reports.types').ProjectForecastData[];
  summary: import('@/types/reports.types').ForecastSummary;
}> {
  const mode = getDataMode();

  if (mode === 'demo' || mode === 'tour') {
    await new Promise(resolve => setTimeout(resolve, 300));

    const projects = mode === 'demo' ? mockData.mockProjects : (tourMockData as any).tourMockProjects ?? mockData.mockProjects;
    const thresholds = getBudgetHealthThresholds();

    let totalBudgetCost = 0;
    let atRiskCount = 0;

    const forecastProjects: import('@/types/reports.types').ProjectForecastData[] = projects
      .filter((p: any) => {
        const statusId = p.statusId ?? 1;
        return statusId === 1 || statusId === 2; // Planning, In Progress
      })
      .map((project: any) => {
        const budgetData = getMockBudgetData(project.id);
        const costBudgetTotal = budgetData?.costBudget?.totals?.budget ?? 0;
        const actualCosts = (project.actualLabour ?? 0) + (project.actualExpense ?? 0);
        const completionPercent = computeEffectiveCompletionPercent(project);

        totalBudgetCost += costBudgetTotal;

        // Derive health
        const health = deriveProjectHealth(
          {
            actualCost: actualCosts,
            budgetCost: costBudgetTotal,
            startDate: project.startDate,
            endDate: project.endDate,
            completionPercent,
            actualHours: project.actualHours ?? 0,
            budgetHours: project.budgetHours ?? 0,
            wipAmount: project.wipAmount ?? 0,
            actualRevenue: project.actualRevenue ?? 0,
            budgetRevenue: project.budgetRevenue ?? 0,
            forecastEndDate: project.forecastEndDate,
          },
          thresholds
        );

        if (health === 'amber' || health === 'red') {
          atRiskCount++;
        }

        // Resolve PM name
        const pmFromRoles = project.roleAssignments?.find(
          (r: any) => r.role === 'project-manager'
        )?.employeeName;
        const pmName = pmFromRoles ?? (
          mockData.mockEmployees?.find((e: any) => e.id === project.projectManagerId)?.name ?? '—'
        );

        // Revenue data
        const projectedFinalRevenue = budgetData?.allBudgets?.forecast?.projectedFinalRevenue ?? null;

        // Hours — linear projection fallback
        const budgetHours = project.budgetHours ?? 0;
        const actualHours = project.actualHours ?? 0;
        const forecastAtCompletionHours = completionPercent > 0
          ? actualHours / (completionPercent / 100)
          : null;

        const entry: import('@/types/reports.types').ProjectForecastData = {
          projectId: project.id,
          projectName: project.name,
          projectCode: project.code ?? '',
          clientName: project.clientNames ?? '—',
          projectManager: pmName,
          statusId: project.statusId ?? 0,
          status: project.status ?? String(project.statusId ?? ''),
          health,
          billable: project.billable ?? true,
          completionPercent,
          startDate: project.startDate ?? '',
          endDate: project.endDate ?? '',
          forecastEndDate: project.forecastEndDate ?? null,
          forecastInputs: {
            actualCosts,
            totalBudget: costBudgetTotal,
            completionPercent,
          },
          revenueData: {
            budgetRevenue: project.budgetRevenue ?? 0,
            actualSales: project.actualSales ?? project.actualRevenue ?? 0,
            projectedFinalRevenue,
          },
          hoursData: {
            budgetHours,
            actualHours,
            forecastAtCompletionHours,
          },
        };

        return entry;
      });

    return {
      projects: forecastProjects,
      summary: {
        totalProjects: forecastProjects.length,
        totalBudgetCost,
        atRiskCount,
      },
    };
  }

  // Live mode — call the real endpoint
  const baseUrl = getApiBaseUrl();
  const raw = await apiFetch<{
    projects: Array<{
      projectId: number;
      projectName: string;
      projectCode: string;
      clientName: string | null;
      projectManager: string;
      statusId: number;
      status: string;
      health: string;
      completionPercent: number;
      startDate: string | null;
      endDate: string | null;
      forecastEndDate: string | null;
      actualCosts: number;
      totalBudget: number;
      budgetRevenue: number;
      actualSales: number;
      projectedFinalRevenue: number | null;
      budgetHours: number;
      actualHours: number;
      forecastAtCompletionHours: number | null;
    }>;
    summary: import('@/types/reports.types').ForecastSummary;
  }>(`${baseUrl}/reports/project-forecast`);

  const projects: import('@/types/reports.types').ProjectForecastData[] = raw.projects.map(r => ({
    projectId: r.projectId,
    projectName: r.projectName,
    projectCode: r.projectCode ?? '',
    clientName: r.clientName ?? '—',
    projectManager: r.projectManager ?? '—',
    statusId: r.statusId ?? 0,
    status: r.status ?? '',
    health: (['green', 'amber', 'red'].includes((r.health ?? '').toLowerCase())
      ? r.health.toLowerCase() as 'green' | 'amber' | 'red'
      : 'green'),
    completionPercent: r.completionPercent ?? 0,
    startDate: r.startDate ?? '',
    endDate: r.endDate ?? '',
    forecastEndDate: r.forecastEndDate ?? null,
    forecastInputs: {
      actualCosts: r.actualCosts ?? 0,
      totalBudget: r.totalBudget ?? 0,
      completionPercent: r.completionPercent ?? 0,
    },
    revenueData: {
      budgetRevenue: r.budgetRevenue ?? 0,
      actualSales: r.actualSales ?? 0,
      projectedFinalRevenue: r.projectedFinalRevenue ?? null,
    },
    hoursData: {
      budgetHours: r.budgetHours ?? 0,
      actualHours: r.actualHours ?? 0,
      forecastAtCompletionHours: r.forecastAtCompletionHours ?? null,
    },
  }));

  return { projects, summary: raw.summary };
}

/** Parameters for the paginated project forecast report endpoint. */
export interface ForecastReportParams {
  page?: number;
  pageSize?: number;
  status?: string;
  pm?: string;
  search?: string;
  sortBy?: string;
  sortOrder?: 'asc' | 'desc';
  /** 'billable' | 'non-billable' — omit for all */
  billable?: string;
}

/**
 * Fetches a single page of project forecast data from the server.
 *
 * Demo mode: falls back to the full client-side dataset wrapped in a paginated
 * response shape so the page component can use the same code path.
 *
 * Live mode: calls `GET /api/reports/project-forecast?page=&pageSize=&…` which
 * returns only the requested page plus summary and filter options.
 */
export async function fetchProjectForecastDataPaged(
  params: ForecastReportParams = {},
): Promise<import('@/types/reports.types').ForecastPaginatedResponse> {
  const mode = getDataMode();

  if (mode === 'demo' || mode === 'tour') {
    const full = await fetchProjectForecastData();
    const page = params.page ?? 1;
    const pageSize = params.pageSize ?? 25;

    let filtered = full.projects;

    if (params.status && params.status !== 'all') {
      if (params.status === 'active') {
        filtered = filtered.filter(p => p.statusId === 1 || p.statusId === 2);
      } else {
        const statusId = parseInt(params.status, 10);
        if (!isNaN(statusId)) {
          filtered = filtered.filter(p => p.statusId === statusId);
        } else {
          filtered = filtered.filter(p =>
            p.status.toLowerCase() === params.status!.toLowerCase());
        }
      }
    }

    if (params.pm && params.pm !== 'all')
      filtered = filtered.filter(p => p.projectManager === params.pm);

    if (params.search) {
      const q = params.search.toLowerCase();
      filtered = filtered.filter(p =>
        p.projectName.toLowerCase().includes(q) ||
        p.projectCode.toLowerCase().includes(q) ||
        p.clientName.toLowerCase().includes(q) ||
        (p.projectManager ?? '').toLowerCase().includes(q));
    }
    if (params.billable === 'billable') {
      filtered = filtered.filter(p => p.billable !== false);
    } else if (params.billable === 'non-billable') {
      filtered = filtered.filter(p => p.billable === false);
    }

    // Sort (limited keys — server paginates on raw rows, no EAC)
    const sortKey = params.sortBy ?? 'project';
    const asc = params.sortOrder === 'asc';
    filtered = [...filtered].sort((a, b) => {
      let diff = 0;
      switch (sortKey) {
        case 'project':
        case 'projectName':
          diff = a.projectName.localeCompare(b.projectName);
          break;
        case 'client':
        case 'clientName':
          diff = a.clientName.localeCompare(b.clientName);
          break;
        case 'budget':
        case 'totalBudget':
          diff = a.forecastInputs.totalBudget - b.forecastInputs.totalBudget;
          break;
        case 'actual':
        case 'actualCosts':
          diff = a.forecastInputs.actualCosts - b.forecastInputs.actualCosts;
          break;
        default:
          diff = a.projectName.localeCompare(b.projectName);
      }
      return asc ? diff : -diff;
    });

    // Summary
    const totalBudgetCost = filtered.reduce((s, p) => s + p.forecastInputs.totalBudget, 0);
    const atRiskCount = filtered.filter(p => p.health === 'amber' || p.health === 'red').length;

    const { paginateArray } = await import('@/types/pagination');
    const { data, pagination } = paginateArray(filtered, page, pageSize);

    return {
      projects: data,
      summary: {
        totalProjects: filtered.length,
        totalBudgetCost,
        atRiskCount,
      },
      pagination,
      filterOptions: {
        projectManagers: [...new Set(full.projects.map(p => p.projectManager).filter(Boolean))].sort(),
        statuses: [...new Set(full.projects.map(p => p.status).filter(Boolean))].sort(),
      },
    };
  }

  // Live mode — server-side pagination
  const baseUrl = getApiBaseUrl();
  const searchParams = new URLSearchParams();
  if (params.page) searchParams.set('page', String(params.page));
  if (params.pageSize) searchParams.set('pageSize', String(params.pageSize));
  if (params.status && params.status !== 'all') searchParams.set('status', params.status);
  if (params.pm && params.pm !== 'all') searchParams.set('pm', params.pm);
  if (params.search) searchParams.set('search', params.search);
  if (params.sortBy) searchParams.set('sortBy', params.sortBy);
  if (params.sortOrder) searchParams.set('sortOrder', params.sortOrder);

  const qs = searchParams.toString();
  const url = `${baseUrl}/reports/project-forecast${qs ? `?${qs}` : ''}`;

  const raw = await apiFetch<{
    projects: Array<{
      projectId: number;
      projectName: string;
      projectCode: string;
      clientName: string | null;
      projectManager: string;
      statusId: number;
      status: string;
      health: string;
      completionPercent: number;
      startDate: string | null;
      endDate: string | null;
      forecastEndDate: string | null;
      actualCosts: number;
      totalBudget: number;
      budgetRevenue: number;
      actualSales: number;
      projectedFinalRevenue: number | null;
      budgetHours: number;
      actualHours: number;
      forecastAtCompletionHours: number | null;
    }>;
    summary: import('@/types/reports.types').ForecastSummary;
    pagination: import('@/types/pagination').PaginationMetadata;
    filterOptions: import('@/types/reports.types').ForecastFilterOptions;
  }>(url, { credentials: 'include' });

  const projects: import('@/types/reports.types').ProjectForecastData[] = raw.projects.map(r => ({
    projectId: r.projectId,
    projectName: r.projectName,
    projectCode: r.projectCode ?? '',
    clientName: r.clientName ?? '—',
    projectManager: r.projectManager ?? '—',
    statusId: r.statusId ?? 0,
    status: r.status ?? '',
    health: (['green', 'amber', 'red'].includes((r.health ?? '').toLowerCase())
      ? r.health.toLowerCase() as 'green' | 'amber' | 'red'
      : 'green'),
    completionPercent: r.completionPercent ?? 0,
    startDate: r.startDate ?? '',
    endDate: r.endDate ?? '',
    forecastEndDate: r.forecastEndDate ?? null,
    forecastInputs: {
      actualCosts: r.actualCosts ?? 0,
      totalBudget: r.totalBudget ?? 0,
      completionPercent: r.completionPercent ?? 0,
    },
    revenueData: {
      budgetRevenue: r.budgetRevenue ?? 0,
      actualSales: r.actualSales ?? 0,
      projectedFinalRevenue: r.projectedFinalRevenue ?? null,
    },
    hoursData: {
      budgetHours: r.budgetHours ?? 0,
      actualHours: r.actualHours ?? 0,
      forecastAtCompletionHours: r.forecastAtCompletionHours ?? null,
    },
  }));

  return {
    projects,
    summary: raw.summary,
    pagination: raw.pagination,
    filterOptions: raw.filterOptions,
  };
}

// ── Budget Variance Report ──────────────────────────────────────────────────

/**
 * Fetches the Budget Variance Report data for a single project.
 *
 * Demo mode: reads from `getBudgetDataByProjectId()` and shapes the response
 * into a `BudgetVarianceReport` so the page works identically in both modes.
 *
 * Live mode: calls `GET /api/reports/budget-variance/{projectId}?revisionId={revisionId}`.
 */
export async function fetchBudgetVarianceData(
  projectId: number,
  revisionId?: number
): Promise<import('@/types/reports.types').BudgetVarianceReport | null> {
  const mode = getDataMode();

  if (mode === 'demo' || mode === 'tour') {
    await new Promise(resolve => setTimeout(resolve, 250));

    const projects = mode === 'demo' ? mockData.mockProjects : (tourMockData as any).tourMockProjects ?? mockData.mockProjects;
    const project = projects.find((p: any) => p.id === projectId);
    if (!project) return null;

    const budgetData = getMockBudgetData(projectId);
    if (!budgetData) return null;

    const costBudget = budgetData.costBudget;
    const revisions: import('@/types/budget.types').BudgetRevision[] = budgetData.revisions ?? [];
    const nonDraftRevisions = revisions.filter(r => r.status !== 'draft');

    // Resolve the selected revision for historical baseline
    const selectedRevision = revisionId
      ? nonDraftRevisions.find(r => r.id === revisionId) ?? null
      : null;

    // Build category budget overrides from historical revision snapshot
    const categoryBudgetOverrides = new Map<number, number>();
    if (selectedRevision?.costSnapshot) {
      for (const snap of selectedRevision.costSnapshot) {
        categoryBudgetOverrides.set(snap.categoryId, snap.budget);
      }
    }

    const totalBudget = selectedRevision?.costBudget ?? costBudget.totals.budget;
    const totalActual = costBudget.totals.actual;
    const totalCommitted = costBudget.totals.committed;
    const totalAvailable = totalBudget - totalActual - totalCommitted;
    const totalVariance = totalActual - totalBudget;
    const totalVariancePct = totalBudget !== 0 ? (totalVariance / totalBudget) * 100 : 0;

    // Map cost categories to BudgetVarianceCategory[]
    const categories: import('@/types/budget.types').BudgetVarianceCategory[] = costBudget.categories.map(cat => {
      const budget = categoryBudgetOverrides.get(cat.id) ?? cat.budget;
      const actual = cat.actual;
      const committed = cat.committed;
      const available = budget - actual - committed;
      const variance = actual - budget;
      const variancePct = budget !== 0 ? (variance / budget) * 100 : 0;
      const progressPct = budget > 0 ? (actual / budget) * 100 : 0;
      const thresholds = getBudgetHealthThresholds();
      const status: import('@/types/budget.types').BudgetStatus =
        progressPct >= thresholds.costRedThreshold ? 'red'
        : progressPct >= thresholds.costAmberThreshold ? 'amber'
        : 'green';

      const subcategories: import('@/types/budget.types').BudgetVarianceSubcategory[] | undefined =
        cat.subcategories?.map(sub => {
          const subVariance = sub.actual - sub.budget;
          const subVarPct = sub.budget !== 0 ? (subVariance / sub.budget) * 100 : 0;
          const subProgress = sub.budget > 0 ? (sub.actual / sub.budget) * 100 : 0;
          const subStatus: import('@/types/budget.types').BudgetStatus =
            subProgress >= thresholds.costRedThreshold ? 'red'
            : subProgress >= thresholds.costAmberThreshold ? 'amber'
            : 'green';
          return {
            id: sub.id,
            name: sub.name,
            budget: sub.budget,
            actual: sub.actual,
            committed: sub.committed,
            available: sub.budget - sub.actual - sub.committed,
            variance: subVariance,
            variancePercent: subVarPct,
            progressPercent: subProgress,
            status: subStatus,
          };
        });

      return {
        id: cat.id,
        name: cat.name,
        type: cat.type,
        budget,
        actual,
        committed,
        available,
        variance,
        variancePercent: variancePct,
        progressPercent: progressPct,
        status,
        subcategories,
      };
    });

    // Revenue
    const budgetRevenue = project.budgetRevenue ?? 0;
    const actualRevenue = project.actualSales ?? project.actualRevenue ?? 0;
    const revenueVariance = actualRevenue - budgetRevenue;
    const revenueVarPct = budgetRevenue !== 0 ? (revenueVariance / budgetRevenue) * 100 : 0;

    // Hours
    const budgetHours = project.budgetHours ?? 0;
    const actualHours = project.actualHours ?? 0;
    const hoursVariance = actualHours - budgetHours;
    const hoursVarPct = budgetHours !== 0 ? (hoursVariance / budgetHours) * 100 : 0;

    // Baseline
    const activeRevision = nonDraftRevisions.find(r => r.status === 'active' || r.status === 'current') ?? nonDraftRevisions[0];
    const baseline: import('@/types/budget.types').BudgetVarianceBaseline = selectedRevision
      ? {
          revisionId: selectedRevision.id,
          revisionLabel: `CB-v${selectedRevision.revisionNumber} — ${selectedRevision.reason}`,
          isActiveRevision: false,
          isOriginalBaseline: selectedRevision.isBaseline,
          date: selectedRevision.date,
        }
      : {
          revisionId: activeRevision?.id,
          revisionLabel: activeRevision
            ? `CB-v${activeRevision.revisionNumber} — ${activeRevision.reason}`
            : 'Active Budget',
          isActiveRevision: true,
          date: activeRevision?.date,
        };

    // Revision options for the dropdown
    const revisionOptions: import('@/types/budget.types').BudgetVarianceRevisionOption[] = nonDraftRevisions.map(r => ({
      id: r.id,
      label: `CB-v${r.revisionNumber} — ${r.reason}`,
      status: r.status,
      isActive: r.status === 'active' || r.status === 'current',
      isBaseline: r.isBaseline ?? false,
      date: r.date,
    }));

    const report: import('@/types/reports.types').BudgetVarianceReport = {
      projectId: project.id,
      projectName: project.name,
      projectCode: project.code ?? '',
      baseline,
      revenue: {
        budget: budgetRevenue,
        actual: actualRevenue,
        variance: revenueVariance,
        variancePercent: revenueVarPct,
      },
      costs: {
        totals: {
          budget: totalBudget,
          actual: totalActual,
          committed: totalCommitted,
          available: totalAvailable,
          variance: totalVariance,
          variancePercent: totalVariancePct,
        },
        categories,
      },
      hours: {
        budget: budgetHours,
        actual: actualHours,
        variance: hoursVariance,
        variancePercent: hoursVarPct,
      },
      revisions: revisionOptions,
    };

    return report;
  }

  // Live mode — call the real endpoint
  const baseUrl = getApiBaseUrl();
  const queryParams = revisionId ? `?revisionId=${revisionId}` : '';
  const response = await apiFetch<import('@/types/reports.types').BudgetVarianceReport>(
    `${baseUrl}/reports/budget-variance/${projectId}${queryParams}`
  );
  return response;
}

// ── Budget Configuration API ─────────────────────────────────────────────

export interface BudgetConfigurationPayload {
  budgetMode: string;
  costBudgetEnabled: boolean;
  salesBudgetEnabled: boolean;
  resourceBudgetEnabled: boolean;
  hasBudgetData?: boolean;
}

export async function fetchBudgetConfiguration(): Promise<BudgetConfigurationPayload> {
  const baseUrl = getApiBaseUrl();
  return apiFetch<BudgetConfigurationPayload>(`${baseUrl}/settings/budget/configuration`);
}

export async function updateBudgetConfiguration(config: BudgetConfigurationPayload): Promise<void> {
  const baseUrl = getApiBaseUrl();
  await apiFetch<void>(`${baseUrl}/settings/budget/configuration`, {
    method: 'PUT',
    body: JSON.stringify(config),
  });
}

// ── Cost Centre Budgets API ──────────────────────────────────────────────

export interface CostCentreBudgetEntry {
  costCentreId: number;
  costCentreCode?: string;
  budgetType: 'cost' | 'sales' | 'resource';
  monthlyBudgets: Record<string, number>;
}

// Short-lived in-flight de-duplication for cost-centre budget fetches. In Cost
// Centre mode the same project/budgetType is requested concurrently by both the
// budget tab and its edit form, producing duplicate network requests. Concurrent
// callers share a single in-flight request keyed by projectId + budgetType; the
// entry is cleared once it settles so later (post-mutation) fetches still hit the
// network for fresh data.
const costCentreBudgetsInflight = new Map<string, Promise<CostCentreBudgetEntry[]>>();

export async function fetchCostCentreBudgetsByProject(
  projectId: number,
  budgetType?: 'cost' | 'sales' | 'resource'
): Promise<CostCentreBudgetEntry[]> {
  const key = `${projectId}:${budgetType ?? 'all'}`;
  const inflight = costCentreBudgetsInflight.get(key);
  if (inflight) {
    return inflight;
  }

  const baseUrl = getApiBaseUrl();
  const queryParams = budgetType ? `?budgetType=${budgetType}` : '';
  const request = apiFetch<CostCentreBudgetEntry[]>(
    `${baseUrl}/CostCentreBudgets/by-project/${projectId}${queryParams}`
  ).finally(() => {
    costCentreBudgetsInflight.delete(key);
  });
  costCentreBudgetsInflight.set(key, request);
  return request;
}

export async function upsertCostCentreBudget(
  costCentreId: number,
  budgetType: 'cost' | 'sales' | 'resource',
  monthlyBudgets: Record<string, number>
): Promise<void> {
  const baseUrl = getApiBaseUrl();
  await apiFetch<void>(`${baseUrl}/CostCentreBudgets/${costCentreId}`, {
    method: 'PUT',
    body: JSON.stringify({ budgetType, monthlyBudgets }),
  });
}

export async function fetchCostCentresWithBudgets(projectId: number): Promise<any[]> {
  const baseUrl = getApiBaseUrl();
  return apiFetch<any[]>(`${baseUrl}/CostCentres/by-project/${projectId}/with-budgets`);
}

// ── Monthly Financial Summary (BE-1) ──────────────────────────────────

export interface MonthlyFinancialSummary {
  periodNumber: number;
  yearLabel: string;
  monthLabel: string;
  cumulativeSales: number;
  cumulativeCost: number;
  periodSales: number;
  periodCost: number;
  grossProfit: number;
  grossMarginPercent: number;
  actualHours: number;
  cumulativeHours: number;
}

export async function fetchProjectMonthlySummary(
  projectCode: string,
  dateFrom?: string,
  dateTo?: string,
): Promise<MonthlyFinancialSummary[]> {
  const mode = getDataMode();
  if (mode === 'demo') {
    return [];
  }
  const params = new URLSearchParams();
  if (dateFrom) params.set('dateFrom', dateFrom);
  if (dateTo) params.set('dateTo', dateTo);
  const qs = params.toString();
  return apiFetch<MonthlyFinancialSummary[]>(
    `${getApiBaseUrl()}/projects/${encodeURIComponent(projectCode)}/financials/monthly-summary${qs ? `?${qs}` : ''}`
  );
}

export async function fetchPortfolioMonthlySummary(
  dateFrom?: string,
  dateTo?: string,
): Promise<MonthlyFinancialSummary[]> {
  const mode = getDataMode();
  if (mode === 'demo') {
    return [];
  }
  const params = new URLSearchParams();
  if (dateFrom) params.set('dateFrom', dateFrom);
  if (dateTo) params.set('dateTo', dateTo);
  const qs = params.toString();
  return apiFetch<MonthlyFinancialSummary[]>(
    `${getApiBaseUrl()}/portfolio/financials/monthly-summary${qs ? `?${qs}` : ''}`
  );
}

// ── Cost Rollup by Category (BE-4) ────────────────────────────────────

export interface CategoryCostRollup {
  categoryId: number;
  categoryName: string;
  categoryType: string;
  budgetAmount: number;
  actualAmount: number;
  nominalCodes: string[];
}

export async function fetchCostRollupByCategory(
  projectId: number,
): Promise<CategoryCostRollup[]> {
  const mode = getDataMode();
  if (mode === 'demo') {
    return [];
  }
  return apiFetch<CategoryCostRollup[]>(
    `${getApiBaseUrl()}/projects/${projectId}/cost-rollup-by-category`
  );
}

// ============================================================================
// PLATFORM ROLES — User Permission Roles (company-level)
// ============================================================================

export async function fetchPlatformRoles(): Promise<Role[]> {
  const mode = getDataMode();
  if (mode === 'demo' || mode === 'tour') {
    return [];
  }
  try {
    const response = await apiFetch<Role[]>(
      `${getApiBaseUrl()}/settings/user-permissions/roles`
    );
    return Array.isArray(response) ? response : [];
  } catch (error: unknown) {
    const appError = error as { metadata?: { apiError?: { status?: number } } };
    const statusCode = appError?.metadata?.apiError?.status;
    if (statusCode === 404) {
      return [];
    }
    console.error('Failed to fetch platform roles:', error);
    return [];
  }
}

export async function fetchPlatformRole(roleId: string): Promise<Role | null> {
  const mode = getDataMode();
  if (mode === 'demo' || mode === 'tour') {
    return null;
  }
  try {
    return await apiFetch<Role>(
      `${getApiBaseUrl()}/settings/user-permissions/roles/${roleId}`
    );
  } catch (error: unknown) {
    const appError = error as { metadata?: { apiError?: { status?: number } } };
    const statusCode = appError?.metadata?.apiError?.status;
    if (statusCode === 404) {
      return null;
    }
    throw error;
  }
}

export async function savePlatformRole(roleId: string, role: Role): Promise<Role> {
  const mode = getDataMode();
  if (mode === 'demo' || mode === 'tour') {
    return role;
  }
  return apiFetch<Role>(
    `${getApiBaseUrl()}/settings/user-permissions/roles/${roleId}`,
    {
      method: 'PUT',
      body: JSON.stringify({
        name: role.name,
        description: role.description,
        isActive: role.status === 'Active',
        permissions: role.permissions,
      }),
    }
  );
}

export async function createPlatformRole(role: {
  name: string;
  description: string;
  isActive: boolean;
  permissions: RolePermissions;
}): Promise<Role> {
  return apiFetch<Role>(
    `${getApiBaseUrl()}/settings/user-permissions/roles`,
    {
      method: 'POST',
      body: JSON.stringify(role),
    }
  );
}

export async function deletePlatformRole(roleId: string): Promise<void> {
  await apiFetch<void>(
    `${getApiBaseUrl()}/settings/user-permissions/roles/${roleId}`,
    { method: 'DELETE' }
  );
}

export async function fetchEmployeeCurrentRole(employeeId: number): Promise<{ hasRole: boolean; roleId?: number; roleName?: string }> {
  return apiFetch<{ hasRole: boolean; roleId?: number; roleName?: string }>(
    `${getApiBaseUrl()}/settings/user-permissions/roles/employees/${employeeId}/current-role`
  );
}

export async function assignEmployeeToRole(roleId: string, employeeId: number): Promise<{ assigned: boolean; previousRoleName?: string | null }> {
  return apiFetch<{ assigned: boolean; previousRoleName?: string | null }>(
    `${getApiBaseUrl()}/settings/user-permissions/roles/${roleId}/employees/${employeeId}`,
    { method: 'POST' }
  );
}

export async function removeEmployeeFromRole(roleId: string, employeeId: number): Promise<void> {
  await apiFetch<void>(
    `${getApiBaseUrl()}/settings/user-permissions/roles/${roleId}/employees/${employeeId}`,
    { method: 'DELETE' }
  );
}

export async function fetchRoleEmployees(roleId: string): Promise<Array<{
  id: number;
  name: string;
  email: string;
  roleName?: string;
  status: string;
  assignedAt: string;
}>> {
  return apiFetch(
    `${getApiBaseUrl()}/settings/user-permissions/roles/${roleId}/employees`
  );
}

export async function fetchCurrentUserRole(): Promise<{ role: string; isActive: boolean; permissions: RolePermissions }> {
  const result = await apiFetch<{ role: string; isActive?: boolean; permissions: RolePermissions }>(
    `${getApiBaseUrl()}/users/me/role`
  );
  // Default to active when the field is absent (older API responses) so that
  // access is never blocked on account of a missing flag.
  return { ...result, isActive: result.isActive ?? true };
}

// ─── Dashboard KPI Snapshot types & API ─────────────────────────────────────

export interface DashboardKpiSnapshot {
  id: number;
  organisationId: string;
  companyId: number;
  persona: string;
  userId: number | null;
  snapshotDate: string;
  periodType: string;
  activeProjectCount: number | null;
  atRiskProjectCount: number | null;
  totalWip: number | null;
  revenueRecognisedMtd: number | null;
  deferredRevenue: number | null;
  overdueInvoiceCount: number | null;
  blendedUtilisationPct: number | null;
  portfolioMarginPct: number | null;
  pmActiveProjectCount: number | null;
  pmOnTrackCount: number | null;
  pmOverdueMilestoneCount: number | null;
  createdAt: string;
}

export async function fetchDashboardKpiSnapshot(
  persona: string = 'executive',
  periodType: string = 'monthly',
  beforeDate?: string,
  userId?: number,
): Promise<DashboardKpiSnapshot | null> {
  const mode = getDataMode();
  if (mode === 'demo') return null;

  const params = new URLSearchParams({ persona, periodType });
  if (beforeDate) params.set('beforeDate', beforeDate);
  if (userId != null) params.set('userId', String(userId));

  try {
    const result = await apiFetch<DashboardKpiSnapshot | Record<string, never>>(
      `${getApiBaseUrl()}/dashboard/kpi-snapshots/latest?${params.toString()}`,
    );
    if (!result || !('id' in result)) return null;
    return result as DashboardKpiSnapshot;
  } catch {
    return null;
  }
}

export async function fetchDashboardKpiSnapshots(
  persona: string = 'executive',
  periodType: string = 'monthly',
  dateFrom?: string,
  dateTo?: string,
  userId?: number,
): Promise<DashboardKpiSnapshot[]> {
  const mode = getDataMode();
  if (mode === 'demo') return [];

  const params = new URLSearchParams({ persona, periodType });
  if (dateFrom) params.set('dateFrom', dateFrom);
  if (dateTo) params.set('dateTo', dateTo);
  if (userId != null) params.set('userId', String(userId));

  try {
    return await apiFetch<DashboardKpiSnapshot[]>(
      `${getApiBaseUrl()}/dashboard/kpi-snapshots/range?${params.toString()}`,
    );
  } catch {
    return [];
  }
}

// ─── Time Entries: date-range + multi-project query ─────────────────────────

export interface TimeEntryWithEmployee {
  id: number;
  projectId: number;
  employeeId: number;
  employeeName: string | null;
  taskId: number | null;
  date: string;
  hours: number;
  description: string | null;
  billable: boolean;
  approved: boolean;
  status: string | null;
  normalizedStatus: string | null;
  costRate: number;
  billRate: number;
  posted: boolean;
}

export async function fetchTimeEntriesByProjects(
  projectIds: number[],
  dateFrom?: string,
  dateTo?: string,
): Promise<TimeEntryWithEmployee[]> {
  const mode = getDataMode();
  if (mode === 'demo') return [];
  if (projectIds.length === 0) return [];

  const params = new URLSearchParams({ projectIds: projectIds.join(',') });
  if (dateFrom) params.set('dateFrom', dateFrom);
  if (dateTo) params.set('dateTo', dateTo);

  try {
    const response = await apiFetch<any>(
      `${getApiBaseUrl()}/timeentries/by-projects?${params.toString()}`,
    );

    let raw: any[] = [];
    if (Array.isArray(response)) {
      raw = response;
    } else if (response && typeof response === 'object') {
      if (Array.isArray(response.$values)) raw = response.$values;
      else if (Array.isArray(response.items)) raw = response.items;
      else if (Array.isArray(response.data)) raw = response.data;
      else if (Array.isArray(response.value)) raw = response.value;
    }

    return raw.map((e: any) => ({
      id: e.id ?? e.Id ?? 0,
      projectId: e.projectId ?? e.ProjectId ?? 0,
      employeeId: e.employeeId ?? e.EmployeeId ?? 0,
      employeeName: e.employeeName ?? e.EmployeeName ?? null,
      taskId: e.taskId ?? e.TaskId ?? null,
      date: (e.date ?? e.Date) ? String(e.date ?? e.Date).split('T')[0] : '',
      hours: e.hours ?? e.Hours ?? 0,
      description: e.description ?? e.Description ?? null,
      billable: !!(e.billable ?? e.Billable),
      approved: !!(e.approved ?? e.Approved),
      status: e.status ?? e.Status ?? null,
      normalizedStatus: e.normalizedStatus ?? e.NormalizedStatus ?? null,
      costRate: e.costRate ?? e.CostRate ?? 0,
      billRate: e.billRate ?? e.BillRate ?? 0,
      posted: e.posted ?? e.Posted ?? false,
    }));
  } catch {
    return [];
  }
}

// ─── Utilisation by date range ──────────────────────────────────────────────

export interface UtilisationByRange {
  employeeId: number;
  employeeName: string;
  role: string | null;
  department: string | null;
  billableHours: number;
  totalHours: number;
  availableHours: number;
  utilisationPercent: number;
}

export async function fetchUtilisationByDateRange(
  dateFrom: string,
  dateTo: string,
): Promise<UtilisationByRange[]> {
  const mode = getDataMode();
  if (mode === 'demo') return [];

  try {
    return await apiFetch<UtilisationByRange[]>(
      `${getApiBaseUrl()}/utilisation?dateFrom=${dateFrom}&dateTo=${dateTo}`,
    );
  } catch {
    return [];
  }
}

// ─── Dashboard Dedicated Endpoint Types & Fetchers ──────────────────────────
// PRD §7.2 — Pre-aggregated endpoints to replace the bulk fetchProjects call
// on the homepage dashboard.

export interface ExecutiveKpis {
  activeProjects: number;
  revenueRecognisedMtd: number;
  blendedMarginPercent: number;
  projectsAtRisk: number;
  totalCostBudget: number;
}

export interface FinanceKpis {
  totalWip: number;
  revenueRecognisedMtd: number;
  overdueInvoices: { count: number; value: number };
  deferredRevenue: number;
}

export interface AtRiskProject {
  id: number;
  name: string;
  code: string | null;
  clientNames: string | null;
  health: string;
  rootCauseTags: string[];
  pmName: string | null;
  costVariance: number;
  costVariancePercent: number;
  daysOverdue: number;
  budgetRevenue: number;
  completionPercent: number;
}

export interface DashboardRevenueRecognition {
  projectId: number;
  projectName: string;
  clientName: string | null;
  method: string | null;
  budgetRevenue: number;
  actualRevenue: number;
  completionPercent: number;
  recognisedRevenue: number;
  deferredRevenue: number;
}

export interface ClientConcentrationData {
  clients: {
    clientId: string;
    clientName: string;
    remainingContractValue: number;
    portfolioPercent: number;
    projectCount: number;
  }[];
  other: { count: number; remainingContractValue: number } | null;
  totalRemainingValue: number;
  concentrationStatus: string;
  topThreePercent: number;
}

export interface PortfolioProfitabilityProject {
  id: number;
  name: string;
  clientNames: string | null;
  marginPercent: number;
  marginGbp: number;
  statusId: number;
}

export interface PortfolioProfitabilityData {
  blendedMarginPercent: number;
  totalProfit: number;
  totalRevenue: number;
  marginBands: {
    lossMaking: number;
    low: number;
    healthy: number;
    strong: number;
  };
  topProjects: PortfolioProfitabilityProject[];
  bottomProjects: PortfolioProfitabilityProject[];
  lossProjects: { id: number; name: string }[];
}

export interface KeyDateEvent {
  type: 'milestone_due' | 'project_ending' | 'forecast_overrun';
  projectId: number | null;
  projectName: string | null;
  clientNames: string | null;
  date: string;
  daysUntil: number;
  amount: number | null;
  severity: 'urgent' | 'warning' | 'info';
}

export interface RevenueCostBurnProject {
  id: number;
  name: string;
  plannedCost: number;
  actualCost: number;
  actualRevenue: number;
  variancePct: number;
}

export interface ProjectFinancialSummaryRow {
  id: number;
  name: string;
  code: string | null;
  clientNames: string | null;
  pmName: string | null;
  projectType: string | null;
  revenueRecognitionMethod: string | null;
  health: string | null;
  completionPercent: number;
  budgetRevenue: number;
  actualSales: number;
  actualCosts: number;
  wipAmount: number;
  costVariancePercent: number;
  grossMarginPercent: number | null;
}

export interface WipItem {
  id: number;
  date: string;
  amount: number;
  projectId: number;
  projectName: string;
  clientName: string | null;
  agingDays: number;
}

export interface UtilisationSummaryData {
  avgUtilisationPercent: number;
  totalHeadcount: number;
  overAllocatedCount: number;
  underUtilisedCount: number;
  benchHeadcount: number;
  dateFrom: string;
  dateTo: string;
}

export async function fetchExecutiveKpis(): Promise<ExecutiveKpis> {
  const mode = getDataMode();
  if (mode !== 'live') {
    return { activeProjects: 0, revenueRecognisedMtd: 0, blendedMarginPercent: 0, projectsAtRisk: 0, totalCostBudget: 0 };
  }
  try {
    return await apiFetch<ExecutiveKpis>(`${getApiBaseUrl()}/dashboard/executive/kpis`);
  } catch {
    return { activeProjects: 0, revenueRecognisedMtd: 0, blendedMarginPercent: 0, projectsAtRisk: 0, totalCostBudget: 0 };
  }
}

export async function fetchFinanceKpis(): Promise<FinanceKpis> {
  const mode = getDataMode();
  if (mode !== 'live') {
    return { totalWip: 0, revenueRecognisedMtd: 0, overdueInvoices: { count: 0, value: 0 }, deferredRevenue: 0 };
  }
  try {
    return await apiFetch<FinanceKpis>(`${getApiBaseUrl()}/dashboard/finance/kpis`);
  } catch {
    return { totalWip: 0, revenueRecognisedMtd: 0, overdueInvoices: { count: 0, value: 0 }, deferredRevenue: 0 };
  }
}

export async function fetchAtRiskProjects(): Promise<AtRiskProject[]> {
  const mode = getDataMode();
  if (mode !== 'live') return [];
  try {
    return await apiFetch<AtRiskProject[]>(`${getApiBaseUrl()}/dashboard/projects/at-risk`);
  } catch {
    return [];
  }
}

export async function fetchDashboardRevenueRecognition(managerId?: number): Promise<DashboardRevenueRecognition[]> {
  const mode = getDataMode();
  if (mode !== 'live') return [];
  try {
    const params = managerId != null ? `?managerId=${managerId}` : '';
    return await apiFetch<DashboardRevenueRecognition[]>(`${getApiBaseUrl()}/dashboard/revenue-recognition${params}`);
  } catch {
    return [];
  }
}

export async function fetchClientConcentration(): Promise<ClientConcentrationData | null> {
  const mode = getDataMode();
  if (mode !== 'live') return null;
  try {
    return await apiFetch<ClientConcentrationData>(`${getApiBaseUrl()}/dashboard/client-concentration`);
  } catch {
    return null;
  }
}

export async function fetchPortfolioProfitability(): Promise<PortfolioProfitabilityData | null> {
  const mode = getDataMode();
  if (mode !== 'live') return null;
  try {
    return await apiFetch<PortfolioProfitabilityData>(`${getApiBaseUrl()}/dashboard/portfolio-profitability`);
  } catch {
    return null;
  }
}

export async function fetchUpcomingKeyDates(): Promise<KeyDateEvent[]> {
  const mode = getDataMode();
  if (mode !== 'live') return [];
  try {
    return await apiFetch<KeyDateEvent[]>(`${getApiBaseUrl()}/dashboard/upcoming-key-dates`);
  } catch {
    return [];
  }
}

export async function fetchRevenueCostBurnByProject(): Promise<RevenueCostBurnProject[]> {
  const mode = getDataMode();
  if (mode !== 'live') return [];
  try {
    return await apiFetch<RevenueCostBurnProject[]>(`${getApiBaseUrl()}/dashboard/revenue-cost-burn/by-project`);
  } catch {
    return [];
  }
}

export async function fetchProjectFinancialSummary(): Promise<ProjectFinancialSummaryRow[]> {
  const mode = getDataMode();
  if (mode !== 'live') return [];
  try {
    return await apiFetch<ProjectFinancialSummaryRow[]>(`${getApiBaseUrl()}/dashboard/project-financial-summary`);
  } catch {
    return [];
  }
}

export interface ProjectFinancialSummaryPagedParams {
  page: number;
  pageSize: number;
  sortColumn?: string;
  sortDirection?: 'ASC' | 'DESC';
  health?: string | null;
  projectType?: string | null;
}

export interface ProjectFinancialSummaryPagedResponse {
  rows: ProjectFinancialSummaryRow[];
  totalCount: number;
}

export async function fetchProjectFinancialSummaryPaged(
  params: ProjectFinancialSummaryPagedParams,
): Promise<ProjectFinancialSummaryPagedResponse> {
  const mode = getDataMode();
  if (mode !== 'live') return { rows: [], totalCount: 0 };
  try {
    const qs = new URLSearchParams();
    qs.set('page', String(params.page));
    qs.set('pageSize', String(params.pageSize));
    if (params.sortColumn) qs.set('sortColumn', params.sortColumn);
    if (params.sortDirection) qs.set('sortDirection', params.sortDirection);
    if (params.health) qs.set('health', params.health);
    if (params.projectType) qs.set('projectType', params.projectType);
    return await apiFetch<ProjectFinancialSummaryPagedResponse>(
      `${getApiBaseUrl()}/dashboard/project-financial-summary?${qs.toString()}`,
    );
  } catch {
    return { rows: [], totalCount: 0 };
  }
}

export async function fetchDashboardWipItems(): Promise<WipItem[]> {
  const mode = getDataMode();
  if (mode !== 'live') return [];
  try {
    return await apiFetch<WipItem[]>(`${getApiBaseUrl()}/dashboard/wip/items`);
  } catch {
    return [];
  }
}

export async function fetchUtilisationSummary(): Promise<UtilisationSummaryData | null> {
  const mode = getDataMode();
  if (mode !== 'live') return null;
  try {
    return await apiFetch<UtilisationSummaryData>(`${getApiBaseUrl()}/dashboard/utilisation/summary`);
  } catch {
    return null;
  }
}

// ── Schedule Health (aggregated — replaces per-project scheduling calls) ──

export interface ScheduleHealthAttentionProject {
  projectId: number;
  projectName: string;
  clientNames: string | null;
  budgetHours: number;
  scheduledHours: number;
  coveragePercent: number;
  unmetRoles: number;
  healthStatus: 'critical' | 'warning' | 'healthy';
}

export interface ScheduleHealthResponse {
  totalProjects: number;
  healthyCount: number;
  warningCount: number;
  criticalCount: number;
  unmetRolesTotal: number;
  attentionProjects: ScheduleHealthAttentionProject[];
}

export async function fetchDashboardScheduleHealth(managerId?: number): Promise<ScheduleHealthResponse> {
  const mode = getDataMode();
  const empty: ScheduleHealthResponse = {
    totalProjects: 0, healthyCount: 0, warningCount: 0, criticalCount: 0,
    unmetRolesTotal: 0, attentionProjects: [],
  };
  if (mode !== 'live') return empty;
  try {
    const params = managerId != null ? `?managerId=${managerId}` : '';
    return await apiFetch<ScheduleHealthResponse>(`${getApiBaseUrl()}/dashboard/schedule-health${params}`);
  } catch {
    return empty;
  }
}

// ── Billing Events (aggregated — replaces per-project milestone calls) ──

export interface DashboardBillingEvent {
  id: number;
  projectId: number;
  projectName: string;
  clientName: string | null;
  milestoneName: string;
  amount: number;
  dueDate: string | null;
  status: string | null;
  eventType: string;
  daysOverdue: number;
}

export async function fetchDashboardBillingEvents(managerId?: number): Promise<DashboardBillingEvent[]> {
  const mode = getDataMode();
  if (mode !== 'live') return [];
  try {
    const params = managerId != null ? `?managerId=${managerId}` : '';
    return await apiFetch<DashboardBillingEvent[]>(`${getApiBaseUrl()}/dashboard/billing-events${params}`);
  } catch {
    return [];
  }
}

// ── Billing Pipeline (Due this month / next month / Overdue) ────────────

export interface BillingPipelineBucket {
  amount: number;
  count: number;
}

export interface BillingPipeline {
  fixedPrice: {
    thisMonth: BillingPipelineBucket;
    nextMonth: BillingPipelineBucket;
    overdue: BillingPipelineBucket;
  };
  timeAndMaterials: {
    thisMonth: BillingPipelineBucket;
    /** forecastAmount is the share of amount derived from a 3-month actuals forecast rather than an agreed amount */
    nextMonth: BillingPipelineBucket & { forecastAmount: number };
    overdue: BillingPipelineBucket;
  };
  /** Fixed-price projects with no milestone schedule, therefore absent from the pipeline */
  fixedPriceProjectsWithoutSchedule: number;
}

export async function fetchBillingPipeline(): Promise<BillingPipeline | null> {
  const mode = getDataMode();
  if (mode !== 'live') return null;
  try {
    return await apiFetch<BillingPipeline>(`${getApiBaseUrl()}/dashboard/billing-pipeline`);
  } catch {
    return null;
  }
}

// ── PM Dashboard Projects Summary (aggregated — replaces full Project[] fetch) ──

export interface PMDashboardKpis {
  activeProjectCount: number;
  hoursRemaining: number;
  onTrackCount: number;
  totalBudgetHours: number;
}

export interface PMProjectSummaryRow {
  id: number;
  name: string;
  code: string | null;
  clientNames: string | null;
  health: string | null;
  statusId: number;
  completionPercent: number;
  completionPercentOverride: number | null;
  budgetRevenue: number;
  actualRevenue: number;
  actualLabour: number;
  actualExpense: number;
  budgetLabour: number;
  budgetExpense: number;
  budgetCost: number;
  actualCosts: number;
  budgetHours: number;
  actualHours: number;
  billableHours: number | null;
  hoursVariance: number;
  costVariancePercent: number;
  wipAmount: number;
  endDate: string | null;
  forecastEndDate: string | null;
  startDate: string | null;
  projectManagerId: number | null;
  type: string | null;
}

export interface PMDashboardProjectsSummary {
  kpis: PMDashboardKpis;
  projects: PMProjectSummaryRow[];
}

export async function fetchPMDashboardProjectsSummary(managerId?: number): Promise<PMDashboardProjectsSummary> {
  const mode = getDataMode();
  if (mode !== 'live') return { kpis: { activeProjectCount: 0, hoursRemaining: 0, onTrackCount: 0, totalBudgetHours: 0 }, projects: [] };
  const qs = managerId ? `?managerId=${managerId}` : '';
  return apiFetch<PMDashboardProjectsSummary>(`${getApiBaseUrl()}/dashboard/pm/projects-summary${qs}`);
}
